const VENPOS_API_BASE = 'https://online1.venpos.net/VenposJourneyAPI';
const IS_DEVELOPMENT = process.env.NODE_ENV === 'development' || window.location.hostname === 'localhost';
const FLOWER_GARDEN_PAGE_ID = '1ced1e1a-b442-4dee-b1e1-ae8b5b7bee09';
const GROTTO_RETAIL_VILLAGE_PAGE_ID = '9afa8eb9-dd32-4529-b9af-316e13a7397e';
const NO_FAMILY_SAVER_PAGE_IDS = [FLOWER_GARDEN_PAGE_ID, GROTTO_RETAIL_VILLAGE_PAGE_ID];

/*
 * VENPOS API INTEGRATION
 * 
 * Customer information is handled through the basket and payment flow:
 * - Customer details are passed to Basket/Load via customerId and customer object
 * - Customer validation happens during Payment/Initialize 
 * - No separate CustomerDetails endpoints exist
 * 
 * Working endpoints:
 * - Basket/Create (POST)
 * - Basket/Load (POST) 
 * - Payment/Initialize (POST)
 * - Payment/Complete (POST)
 */

// Helper function to generate GUIDs for customerId and orderId
const generateGuid = () => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
};

// Helper function to create headers matching working Venpos requests
const getCorsHeaders = () => {
  const headers = {
    'Accept': 'application/json, text/javascript, */*; q=0.01',
    'Content-Type': 'application/json',
    'X-Requested-With': 'XMLHttpRequest'
  };

  // In production, add origin and referer headers
  if (!IS_DEVELOPMENT) {
    headers['Origin'] = 'https://online1.venpos.net';
    if (typeof window !== 'undefined') {
      headers['Referer'] = 'https://online1.venpos.net/VenPosJourneyUI/Basket';
    }
  } else {
    // In development, use localhost origin
    if (typeof window !== 'undefined') {
      headers['Origin'] = window.location.origin;
    }

  }

  return headers;
};

// Helper function to make API requests with proper error handling
const makeApiRequest = async (url, options = {}) => {
  try {

    const requestOptions = {
      ...options,
      headers: {
        ...getCorsHeaders(),
        ...options.headers
      }
    };

    // Handle credentials and CORS for both development and production
    // Venpos returns Access-Control-Allow-Origin: * which conflicts with credentials: 'include'
    requestOptions.mode = 'cors';
    requestOptions.credentials = 'omit';
    
    if (IS_DEVELOPMENT) {

    } else {

    }

    const response = await fetch(url, requestOptions);


    // Check if we got redirected to an error page
    if (response.url.includes('/error-pages/') || response.url.includes('/Error/')) {

      throw new Error(`Request redirected to error page: ${response.url} - likely authentication required`);
    }
    
    if (!response.ok) {
      // Get response body for error details
      let errorBody = '';
      try {
        errorBody = await response.text();

      } catch (e) {

      }
      
      if (response.status === 401) {
        throw new Error(`Authentication required - please log in to Venpos first (Status: ${response.status}). Body: ${errorBody}`);
      }
      if (response.status === 403) {
        throw new Error(`Access forbidden - insufficient permissions (Status: ${response.status}). Body: ${errorBody}`);
      }
      throw new Error(`HTTP error! status: ${response.status} ${response.statusText}. Body: ${errorBody}`);
    }
    
    const data = await response.json();

    // For Venpos API, return the data directly to preserve structure
    if (data && typeof data === 'object' && 'data' in data) {
      return data; // Venpos format: { isError: false, data: {...} }
    }
    
    return { isError: false, data };
  } catch (error) {

    // More specific error handling for authentication issues
    if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {

      return { 
        isError: true, 
        data: null, 
        error: 'Authentication or network error - please ensure you are logged in to Venpos' 
      };
    }
    
    return { isError: true, data: null, error: error.message };
  }
};

export const venposService = {
  async registerGuestUser(locationId, customerDetails) {
    try {

      const url = new URL(`${VENPOS_API_BASE}/Customer/RegisterGuestUser`);
      url.searchParams.append('locationId', locationId);

      // Ensure we have complete customer details before registration
      const address = customerDetails.address || {};

      // Convert UK to GB for Venpos API requirement
      let countryCode = address.country || 'GB';
      if (countryCode === 'UK') {
        countryCode = 'GB'; // Venpos requires GB not UK
      }

      // Build payload with all required fields
      const payload = {
        webCustomerId: null, // Creating new guest user
        firstName: customerDetails.firstName,
        lastName: customerDetails.lastName,
        selectedTitle: customerDetails.selectedTitle || 1, // Use selectedTitle from form, default to 1
        mobile: customerDetails.phone || customerDetails.mobile || '', // Required field - provide empty string if not available
        selectedCountry: countryCode, // Always use GB for UK
        postCode: address.postcode || '',
        cityTown: address.city || '',
        addressFirstLine: address.line1 || '',
        addressSecondLine: address.line2 || '',
        houseNameNumber: address.houseNumber || '', // Required field - provide empty string if not available
        email: customerDetails.email,
        password: '', // Guest users don't need password
        baseUrl: 'https://online1.venpos.net/VenPosJourneyUI', // Must use Venpos domain, not your domain
        name: `${customerDetails.firstName} ${customerDetails.lastName}`,
        contactable: true,
        contactableByPost: true,
        contactableByPhone: true,
        contactableByEmail: true,
        contactableBySMS: true,
        contactableBySocialMedia: true,
        contactableByThirdParty: true,
        isGuest: true
      };

      const response = await makeApiRequest(url.toString(), {
        method: 'POST',
        body: JSON.stringify(payload)
      });

      if (response.isError) {
        throw new Error(response.error || 'Failed to register guest user');
      }


      // Extract the webCustomerId from response
      // Venpos returns the customer ID directly as a string in data, not as an object
      let webCustomerId = null;

      if (typeof response.data === 'string') {
        // Direct string response - this is the customer ID
        webCustomerId = response.data;

      } else if (typeof response.data === 'object') {
        // Object response - check multiple possible property names
        webCustomerId = response.data?.webCustomerId ||
                       response.data?.customerId ||
                       response.data?.id ||
                       response.data?.webCustomerID;

      }

      if (!webCustomerId) {


        if (typeof response.data === 'object') {

        }
      } else {

      }

      return {
        isError: false,
        data: {
          webCustomerId,
          customerDetails: response.data,
          success: true
        }
      };
    } catch (error) {

      return {
        isError: true,
        error: error.message || 'Guest user registration failed'
      };
    }
  },

  // Postcode address lookup - get list of addresses for a postcode
  async getPostcodeAddresses(locationId, postcode) {
    try {

      // Remove spaces from postcode for API call
      const cleanPostcode = postcode.replace(/\s+/g, '');
      
      const url = new URL(`${VENPOS_API_BASE}/Customer/PostcodeAddresses`);
      url.searchParams.append('locationId', locationId);
      url.searchParams.append('postcode', cleanPostcode);

      const response = await makeApiRequest(url.toString(), {
        method: 'GET'
      });

      if (response.isError) {
        throw new Error(response.error || 'Failed to fetch postcode addresses');
      }

      return {
        isError: false,
        data: response.data || []
      };
    } catch (error) {

      return {
        isError: true,
        error: error.message || 'Postcode lookup failed',
        data: []
      };
    }
  },

  // Get full address details by address ID
  async getAddressDetails(locationId, addressId) {
    try {

      const url = new URL(`${VENPOS_API_BASE}/Customer/AddressDetails`);
      url.searchParams.append('locationId', locationId);
      url.searchParams.append('id', addressId);

      const response = await makeApiRequest(url.toString(), {
        method: 'GET'
      });

      if (response.isError) {
        throw new Error(response.error || 'Failed to fetch address details');
      }

      return {
        isError: false,
        data: response.data
      };
    } catch (error) {

      return {
        isError: true,
        error: error.message || 'Address lookup failed'
      };
    }
  },

  // Get list of titles from Venpos
  async getTitles(locationId) {
    try {

      const url = new URL(`${VENPOS_API_BASE}/Customer/GetTitles`);
      url.searchParams.append('locationId', locationId);

      const response = await makeApiRequest(url.toString(), {
        method: 'GET'
      });

      if (response.isError) {
        throw new Error(response.error || 'Failed to fetch titles');
      }

      return {
        isError: false,
        data: response.data || []
      };
    } catch (error) {

      return {
        isError: true,
        error: error.message || 'Failed to fetch titles',
        data: []
      };
    }
  },

  // Get list of countries from Venpos
  async getCountries(locationId) {
    try {

      const url = new URL(`${VENPOS_API_BASE}/Customer/GetCountries`);
      url.searchParams.append('locationId', locationId);

      const response = await makeApiRequest(url.toString(), {
        method: 'GET'
      });

      if (response.isError) {
        throw new Error(response.error || 'Failed to fetch countries');
      }

      return {
        isError: false,
        data: response.data || []
      };
    } catch (error) {

      return {
        isError: true,
        error: error.message || 'Failed to fetch countries',
        data: []
      };
    }
  },

  async addEventCapacityUsages(locationId, pageId, itemsPayload, orderId = null, discounts = []) {
    try {
      const url = new URL(`${VENPOS_API_BASE}/Event/AddEventCapacityUsages`);
      url.searchParams.append('locationId', locationId);

      // Generate a real orderId if not provided (working example always has one)
      const realOrderId = orderId || generateGuid();

      // Ensure items have all required fields from working example
      const enrichedItems = itemsPayload.map(item => ({
        pageType: item.pageType || 'WebPage',
        pageId: item.pageId,
        webSaleItemId: item.webSaleItemId,
        webSaleItemTypeID: item.webSaleItemTypeID,
        quantity: item.quantity,
        selectedDate: item.selectedDate,
        fixedDonationAmount: item.fixedDonationAmount || 0,
        name: item.name,
        giftAidEligible: item.giftAidEligible || false,
        isSysGift: item.isSysGift || false,
        isSysMemberCard: item.isSysMemberCard || false,
        reservationExpiryDateTime: item.reservationExpiryDateTime,
        // Include any existing event metadata
        ...(item.eventId && { eventId: item.eventId }),
        ...(item.eventCapacityUsageId && { eventCapacityUsageId: item.eventCapacityUsageId })
      }));

      // Build basket model exactly matching working requests
      const basket = {
        pageId,
        orderId: realOrderId,
        giftAidSelected: false,
        discounts: discounts || [],
        discountsFromMembership: [],
        modifiers: [],
        items: enrichedItems
      };

      const payload = {
        pageIds: [pageId],
        basket
      };

      const response = await makeApiRequest(url.toString(), {
        method: 'POST',
        body: JSON.stringify(payload)
      });

      return response;
    } catch (err) {

      return { isError: true, error: err?.message || 'AddEventCapacityUsages failed' };
    }
  },

  async validateBasketTimes(locationId, items) {
    try {
      const url = new URL(`${VENPOS_API_BASE}/Basket/ValidateTimes`);
      url.searchParams.append('locationId', locationId);

      const response = await makeApiRequest(url.toString(), {
        method: 'POST',
        body: JSON.stringify(items)
      });

      return response;
    } catch (err) {

      return { isError: true, error: err?.message || 'ValidateTimes failed' };
    }
  },
  async getWebPageData(locationId, pageId) {
    try {

      const response = await fetch(
        `${VENPOS_API_BASE}/WebPage/Get?` +
        `locationId=${locationId}&` +
        `id=${pageId}`,
        {
          method: 'GET',
          headers: {
            'Accept': 'application/json, text/javascript, */*; q=0.01',
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
            'Origin': 'https://online1.venpos.net',
            'Referer': 'https://online1.venpos.net/VenPosJourneyUI/DateSelector',
            'sec-fetch-dest': 'empty',
            'sec-fetch-mode': 'cors',
            'sec-fetch-site': 'same-origin'
          },
          credentials: 'include'
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch WebPage data');
      }

      const data = await response.json();

      return data;
    } catch (error) {

      throw error;
    }
  },

  // Confirm payment (per Vennersys Journey doc) to obtain WebTransHeaderId
  async confirmPaymentJourney(locationId, orderId, paymentToken, paymentType = 0) {
    try {

      const candidateEndpoints = [
        `${VENPOS_API_BASE}/Payment/Confirmation?locationId=${locationId}&orderId=${orderId}&paymentToken=${encodeURIComponent(paymentToken || '')}&paymentType=${paymentType}`,
        `${VENPOS_API_BASE}/Checkout/Confirmation?locationId=${locationId}&orderId=${orderId}&paymentToken=${encodeURIComponent(paymentToken || '')}&paymentType=${paymentType}`,
        // Fallback seen in codebase previously (uses bookingReference but we pass orderId)
        `${VENPOS_API_BASE}/Booking/Confirmation?bookingReference=${orderId}&locationId=${locationId}`
      ];

      let result = null;
      for (const endpoint of candidateEndpoints) {
        try {

          result = await makeApiRequest(endpoint, { method: 'GET' });

          if (!result.isError) {

            break;
          }
        } catch (e) {

          continue;
        }
      }

      if (!result || result.isError) {
        return {
          isError: true,
          data: null,
          error: result?.error || 'Failed to confirm payment with any endpoint'
        };
      }

      const payload = result.data || result;

      // Attempt to normalize potential response shapes
      const webTransHeaderId = payload?.basketPaidViewModel?.webTransHeaderId ||
                               payload?.BasketPaidViewModel?.WebTransHeaderId ||
                               payload?.webTransHeaderID ||
                               payload?.WebTransHeaderId;

      return {
        isError: false,
        data: {
          raw: payload,
          webTransHeaderId
        }
      };
    } catch (error) {

      return { isError: true, data: null, error: 'Payment confirmation failed' };
    }
  },

  async getWebSaleItems(locationId, webPageId, selectedDate = null) {
    const response = await fetch(
      `${VENPOS_API_BASE}/WebSaleItem/Get?` +
      `locationId=${locationId}&` +
      `id=${webPageId}&` +
      `pageType=WebPage&` +
      `dateSelectorType=OnScreenCalendar&` +
      (selectedDate ? `selectedDate=${selectedDate}&` : '') +
      `mainWebPageId=${webPageId}`
    );
    
    if (!response.ok) {
      throw new Error('Failed to fetch web sale items');
    }
    
    return response.json();
  },

  async validateBasket(locationId, basket) {
    const response = await fetch(
      `${VENPOS_API_BASE}/Basket/ValidateTimes?locationId=${locationId}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(basket),
      }
    );
    
    if (!response.ok) {
      throw new Error('Failed to validate basket');
    }
    
    return response.json();
  },

  async validateBasketWithQuestions(locationId, pageId, pageType, payload) {
    try {
      const url = `${VENPOS_API_BASE}/Basket/Validate?locationId=${locationId}&id=${pageId}&pageType=${pageType}&dateSelectorType=OnScreenCalendar`;


      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Accept': 'application/json, text/javascript, */*; q=0.01',
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        mode: 'cors',
        credentials: 'omit',
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorText = await response.text();

        throw new Error(`Failed to validate basket: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();

      return data;
    } catch (error) {

      throw error;
    }
  },

  async validateBasketWithModifiers(locationId, payload) {
    try {
      const url = `${VENPOS_API_BASE}/Basket/ValidateModifiers?locationId=${locationId}`;


      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Accept': 'application/json, text/javascript, */*; q=0.01',
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        mode: 'cors',
        credentials: 'omit',
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorText = await response.text();

        throw new Error(`Failed to validate modifiers: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();

      return data;
    } catch (error) {

      throw error;
    }
  },

  async getAvailableDates(locationId, pageId, options = {}, selectedItems = []) {
    const today = new Date();
    const endDate = new Date();
    endDate.setDate(1); // Set to 1st to avoid month overflow issues
    endDate.setMonth(endDate.getMonth() + 12);

    const {
      pageType = 'WebPage',
      selectedDate = '2024-03-19',
      dateSelectorType = 'OnScreenCalendar',
      lng = 'en',
      webSaleItemId
    } = options;

    try {

      const allDates = [];
      let currentDate = new Date(selectedDate);
      let monthCount = 1;
      
      while (currentDate <= endDate) {
        // Format date in local timezone to avoid UTC conversion issues
        const year = currentDate.getFullYear();
        const month = String(currentDate.getMonth() + 1).padStart(2, '0');
        const day = String(currentDate.getDate()).padStart(2, '0');
        const formattedCurrentDate = `${year}-${month}-${day}`;
        
        const requestUrl = new URL(`${VENPOS_API_BASE}/WebSaleItem/GetCalendar`);
        requestUrl.searchParams.append('locationId', locationId);
        requestUrl.searchParams.append('id', pageId);
        requestUrl.searchParams.append('pageType', pageType);
        requestUrl.searchParams.append('selectedDate', formattedCurrentDate);
        requestUrl.searchParams.append('dateSelectorType', dateSelectorType);

        const response = await fetch(requestUrl.toString(), {
          method: 'POST',
          headers: {
            'Accept': 'application/json, text/javascript, */*; q=0.01',
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({
            items: selectedItems
          })
        });

        if (!response.ok) {

          throw new Error(`Failed to fetch dates for ${currentDate.toLocaleString('default', { month: 'long' })}`);
        }

        const calendarData = await response.json();
        
        if (!calendarData.isError && calendarData.data?.datePrices) {
          const formattedDates = calendarData.data.datePrices.map(datePrice => {
            // Parse date in local timezone to avoid UTC conversion issues
            let normalizedDate = datePrice.date;
            if (datePrice.date) {
              try {
                const d = new Date(datePrice.date);
                const y = d.getFullYear();
                const m = String(d.getMonth() + 1).padStart(2, '0');
                const day = String(d.getDate()).padStart(2, '0');
                normalizedDate = `${y}-${m}-${day}`;
              } catch (e) {
                // If parsing fails, use original date
                normalizedDate = String(datePrice.date).split('T')[0];
              }
            }
            return {
              ...datePrice,
              date: normalizedDate
            };
          });
          allDates.push(...formattedDates);
        }

        // Set to 1st of month before incrementing to avoid skipping months
        // (e.g., Oct 31 + 1 month = Dec 1, skipping November)
        currentDate.setDate(1);
        currentDate.setMonth(currentDate.getMonth() + 1);
        monthCount++;
      }


      const datesByMonth = allDates.reduce((acc, date) => {
        const monthYear = new Date(date.date).toLocaleString('default', { month: 'long', year: 'numeric' });
        if (!acc[monthYear]) {
          acc[monthYear] = [];
        }
        acc[monthYear].push({
          date: date.date,
          disabled: date.disabled,
          price: date.price,
          capacityPotExists: date.capacityPotExists,
          disabledReason: date.disabledReason
        });
        return acc;
      }, {});

      Object.entries(datesByMonth).forEach(([monthYear, dates]) => {


      });


      const processedData = {
        isError: false,
        data: {
          calendar: allDates.map(datePrice => ({
            // Already normalized to YYYY-MM-DD
            date: datePrice.date,
            // Availability mapping for UI:
            // 1 = Available (clickable), 4 = Not Available (greyed)
            availability: !datePrice.disabled ? 1 : 4,
            debug: {
              price: datePrice.price,
              capacityPotExists: datePrice.capacityPotExists,
              disabledReason: datePrice.disabledReason
            }
          }))
        }
      };

      return processedData;

    } catch (error) {

      throw error;
    }
  },

  // New booking journey methods
  async getTimeSlots(locationId, pageId, selectedDate, selectedItems = []) {
    try {
      // Format date in local timezone to avoid UTC conversion issues
      const dateObj = new Date(selectedDate);
      const year = dateObj.getFullYear();
      const month = String(dateObj.getMonth() + 1).padStart(2, '0');
      const day = String(dateObj.getDate()).padStart(2, '0');
      const selectedDateKey = `${year}-${month}-${day}`;

      // 1) Preferred per documentation: Event/GetTimes (POST with query params)
      // URL: /Event/GetTimes?locationId=...&mainWebPageId=...&id=...&pageType=WebPage&date=YYYY-MM-DD
      try {
        const getTimesUrl = new URL(`${VENPOS_API_BASE}/Event/GetTimes`);
        getTimesUrl.searchParams.append('locationId', locationId);
        getTimesUrl.searchParams.append('mainWebPageId', pageId);
        // Per captured journey request, id should match mainWebPageId (pageId)
        getTimesUrl.searchParams.append('id', pageId);
        getTimesUrl.searchParams.append('pageType', 'WebPage');
        getTimesUrl.searchParams.append('date', selectedDateKey);

        const r1 = await fetch(getTimesUrl.toString(), {
          method: 'POST',
          headers: {
            'Accept': 'application/json, text/javascript, */*; q=0.01',
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          // Body can include items; many implementations ignore it for this endpoint
          body: JSON.stringify({ items: selectedItems || [] })
        });
        if (r1.ok) {
          const raw1 = await r1.json();

          // Log the raw response for Flower & Garden Show to debug
          if (pageId === FLOWER_GARDEN_PAGE_ID) {
            console.log('🌸 [Flower & Garden Show] Event/GetTimes raw response:', JSON.stringify(raw1, null, 2));
          }

          const d1 = raw1?.data ?? raw1;
          // Prefer documented shape: data.events[{ startDateTime, eventId, eventCapacityUsageId }]
          if (Array.isArray(d1?.events) && d1.events.length) {
            const normalizedEvents = d1.events
              .filter(ev => ev?.startDateTime)
              .map(ev => {
                const dt = new Date(ev.startDateTime);
                const hh = String(dt.getHours()).padStart(2, '0');
                const mm = String(dt.getMinutes()).padStart(2, '0');
                return {
                  time: `${hh}:${mm}`,
                  eventId: ev.eventId || ev.id,
                  eventCapacityUsageId: ev.eventCapacityUsageId || ev.capacityUsageId,
                  startDateTime: ev.startDateTime,
                  // Preserve other useful fields
                  description: ev.description,
                  quantityAvailable: ev.quantityAvailable
                };
              });
            if (pageId === FLOWER_GARDEN_PAGE_ID) {
              console.log('🌸 [Flower & Garden Show] Normalized events with IDs:', normalizedEvents);
            }
            if (normalizedEvents.length) return { isError: false, data: { timeSlots: normalizedEvents } };
          }

          // FALLBACK PATH - WARNING: This loses eventId and eventCapacityUsageId!
          if (pageId === FLOWER_GARDEN_PAGE_ID) {
            console.warn('⚠️ [Flower & Garden Show] Falling back to simple time string parsing - this will lose event IDs!', {
              d1Structure: Object.keys(d1 || {}),
              hasEvents: !!d1?.events,
              eventsLength: d1?.events?.length
            });
          }
          const candidates1 = [d1?.times, d1?.timeSlots, d1?.availableTimes, d1];
          let slots1 = [];
          for (const c of candidates1) { if (Array.isArray(c)) { slots1 = c; break; } }
          const normalized1 = slots1.map(s => typeof s === 'string' ? s : (s?.time || s?.label || s?.startTime || s?.from || '')).filter(Boolean);
          if (normalized1.length) return { isError: false, data: { timeSlots: normalized1 } };
        }
      } catch (e) {

      }

      // 2) Fallback: WebSaleItem/GetTimeSlots (POST)
      try {
        const url2 = new URL(`${VENPOS_API_BASE}/WebSaleItem/GetTimeSlots`);
        url2.searchParams.append('locationId', locationId);
        url2.searchParams.append('id', pageId);
        url2.searchParams.append('pageType', 'WebPage');
        url2.searchParams.append('selectedDate', selectedDateKey);
        url2.searchParams.append('dateSelectorType', 'OnScreenCalendar');

        const r2 = await fetch(url2.toString(), {
          method: 'POST',
          headers: {
            'Accept': 'application/json, text/javascript, */*; q=0.01',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ items: selectedItems })
        });
        if (r2.ok) {
          const raw2 = await r2.json();

          const d2 = raw2?.data ?? raw2;
          if (Array.isArray(d2?.events) && d2.events.length) {
            const normalizedE2 = d2.events
              .map(ev => ev?.startDateTime)
              .filter(Boolean)
              .map(dt => {
                const t = new Date(dt);
                const hh = String(t.getHours()).padStart(2, '0');
                const mm = String(t.getMinutes()).padStart(2, '0');
                return `${hh}:${mm}`;
              });
            if (normalizedE2.length) return { isError: false, data: { timeSlots: normalizedE2 } };
          }
          const candidates2 = [d2?.timeSlots, d2?.times, d2?.availableTimes, d2];
          let slots2 = [];
          for (const c of candidates2) { if (Array.isArray(c)) { slots2 = c; break; } }
          const normalized2 = slots2.map(s => typeof s === 'string' ? s : (s?.time || s?.label || s?.startTime || s?.from || '')).filter(Boolean);
          if (normalized2.length) return { isError: false, data: { timeSlots: normalized2 } };
        }
      } catch (e2) {

      }

      // 3) Final fallback: empty times
      return { isError: false, data: { timeSlots: [] } };
    } catch (error) {

      return { isError: true, data: { timeSlots: [] }, error: String(error) };
    }
  },

  async createBasket(locationId, basketItems) {
    try {

      const result = await makeApiRequest(
        `${VENPOS_API_BASE}/Basket/Create?locationId=${locationId}`,
        {
          method: 'POST',
          body: JSON.stringify({ items: basketItems })
        }
      );

      if (result.isError) {

        return {
          isError: true,
          data: null,
          error: result.error || 'Failed to create basket'
        };
      }

      return result;
    } catch (error) {

      return {
        isError: true,
        data: null,
        error: String(error)
      };
    }
  },

  async addToBasket(basketId, locationId, basketItems) {
    try {

      // Separate items and modifiers according to VenPos API structure
      const items = basketItems.map(item => {
        const { modifierSelection, questionAnswers, ...itemWithoutModifiers } = item;
        return itemWithoutModifiers;
      });

      // Extract modifiers from items that have them
      const modifiers = [];
      basketItems.forEach(item => {
        if (item.modifierSelection && Object.keys(item.modifierSelection).length > 0) {
          Object.entries(item.modifierSelection).forEach(([modifierId, selection]) => {
            if (selection && (selection.productModifierTextId || selection.modifierTextId)) {
              modifiers.push({
                webSaleItemId: item.webSaleItemId,
                productModifierId: parseInt(modifierId),
                productModifierTextId: selection.productModifierTextId || selection.modifierTextId,
                quantity: selection.quantity || 1
              });
            }
          });
        }
      });

      const payload = { items };
      if (modifiers.length > 0) {
        payload.modifiers = modifiers;

      }

      const response = await fetch(
        `${VENPOS_API_BASE}/Basket/AddItems?basketId=${basketId}&locationId=${locationId}`,
        {
          method: 'POST',
          headers: {
            'Accept': 'application/json, text/javascript, */*; q=0.01',
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify(payload)
        }
      );

      if (!response.ok) {
        throw new Error('Failed to add items to basket');
      }

      const data = await response.json();

      return data;
    } catch (error) {

      throw error;
    }
  },

  async getBasket(basketId, locationId) {
    try {

      const response = await fetch(
        `${VENPOS_API_BASE}/Basket/Get?basketId=${basketId}&locationId=${locationId}`,
        {
          method: 'GET',
          headers: {
            'Accept': 'application/json, text/javascript, */*; q=0.01',
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch basket');
      }

      const data = await response.json();

      return data;
    } catch (error) {

      throw error;
    }
  },

  async processPayment(basketId, locationId, paymentDetails, customerDetails) {
    try {

      const paymentPayload = {
        basketId,
        locationId,
        paymentMethod: paymentDetails.method,
        customerDetails: {
          firstName: customerDetails.firstName,
          lastName: customerDetails.lastName,
          email: customerDetails.email,
          phone: customerDetails.phone,
          address: customerDetails.address
        },
        paymentData: paymentDetails.method === 'card' ? {
          cardNumber: paymentDetails.cardDetails.number,
          expiryMonth: paymentDetails.cardDetails.expiryMonth,
          expiryYear: paymentDetails.cardDetails.expiryYear,
          cvv: paymentDetails.cardDetails.cvv,
          cardholderName: paymentDetails.cardDetails.name
        } : {}
      };

      const response = await fetch(
        `${VENPOS_API_BASE}/Payment/Process`,
        {
          method: 'POST',
          headers: {
            'Accept': 'application/json, text/javascript, */*; q=0.01',
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify(paymentPayload)
        }
      );

      if (!response.ok) {
        throw new Error('Payment processing failed');
      }

      const data = await response.json();

      return data;
    } catch (error) {

      throw error;
    }
  },

  async confirmBooking(basketId, locationId, bookingDetails) {
    try {

      // For free bookings, we need to finalize the basket into a transaction first
      const isFreeBooking = bookingDetails.totalAmount === 0 || 
                           bookingDetails.paymentToken === 'FREE_BOOKING' ||
                           (!bookingDetails.isPaidBooking && bookingDetails.totalAmount === 0);
      
      console.log('🔍 [confirmBooking] Booking details:', {
        totalAmount: bookingDetails.totalAmount,
        paymentToken: bookingDetails.paymentToken,
        isPaidBooking: bookingDetails.isPaidBooking,
        isMembershipJourney: bookingDetails.isMembershipJourney,
        isFreeBooking: isFreeBooking,
        membershipsCount: bookingDetails.memberships?.length || 0
      });

      // Helper to normalize selected date into YYYY-MM-DD
      const normalizeDate = (value) => {
        if (!value) {
          const today = new Date();
          return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
        }
        if (typeof value === 'string') {
          // Accept already formatted dates; strip time if present
          return value.split('T')[0];
        }
        try {
          // Format in local timezone to avoid UTC shift
          const d = new Date(value);
          return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        } catch (_) {
          const today = new Date();
          return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
        }
      };

      const deriveDateAndTime = () => {
        const pad = (n) => String(n).padStart(2, '0');
        const slotStart = bookingDetails.selectedTimeSlot?.startDateTime;
        if (slotStart) {
          const d = new Date(slotStart);
          return {
            date: normalizeDate(d),
            time: `${pad(d.getHours())}:${pad(d.getMinutes())}`
          };
        }

        if (bookingDetails?.venposBasket?.data?.groups?.length) {
          const firstItem = bookingDetails.venposBasket.data.groups.flatMap(g => g.items || [])[0];
          if (firstItem?.startDateTime) {
            const d = new Date(firstItem.startDateTime);
            return {
              date: normalizeDate(d),
              time: `${pad(d.getHours())}:${pad(d.getMinutes())}`
            };
          }
          if (firstItem?.time) {
            return {
              date: normalizeDate(bookingDetails.selectedDate),
              time: firstItem.time
            };
          }
        }

        return {
          date: normalizeDate(bookingDetails.selectedDate),
          time: bookingDetails.selectedTimeSlot?.time || '10:00'
        };
      };

      const { date: selectedDateStr, time: selectedTimeStr } = deriveDateAndTime();
      const selectedDateTime = `${selectedDateStr}T${selectedTimeStr}:00`;

      const originalBasketId = basketId; // keep a stable copy of the incoming order/basket id
      const isNonZeroGuid = (id) => !!id && id !== '00000000-0000-0000-0000-000000000000';
      let freeOrderCreated = false;
      let headerIdFromCreate = null;
      let transactionDetails = null; // Declare transactionDetails at the top level
      let transactionId = null; // Declare transactionId at the top level
      let confirmedHeaderId = null; // Declare confirmedHeaderId at the top level
      
      // For membership journeys that are free, we still need to create the order via CreateFreeOrder
      // The memberships array will be included in the CreateFreeOrder payload
      // For paid membership orders, they're created via Payment/Initialize (which includes memberships)
      if (bookingDetails.isMembershipJourney && isFreeBooking && !bookingDetails.isPaidBooking) {
        // Let it fall through to CreateFreeOrder below - don't skip it!
        // The CreateFreeOrder payload already includes memberships array
      }
      
      if (isFreeBooking && !bookingDetails.isPaidBooking) {
      // ✅ CRITICAL: Only use CreateFreeOrder for actual free bookings
      // For membership journeys, skip Payment/Initialize and go straight to CreateFreeOrder
      
      if (!bookingDetails.isMembershipJourney) {
        // Try official flow first: Get payment token then use Checkout/Confirm
        try {

          // Build Payment/Initialize payload following the official manual structure
          const paymentInitPayload = {
            // Core payment fields from manual
            amount: 0, // Free booking
          currency: 'GBP',
            paymentProcessor: 0, // From manual: 0 = free booking
            orderId: basketId,
            
            // Customer details following CustomerDetailsViewModel from manual
            customerDetails: {
              webCustomerId: bookingDetails.customerDetails?.webCustomerId || null, // Don't generate - use registered customer ID
              firstName: bookingDetails.customerDetails?.firstName || '',
              lastName: bookingDetails.customerDetails?.lastName || '',
              email: bookingDetails.customerDetails?.email || '',
              mobile: bookingDetails.customerDetails?.phone || bookingDetails.customerDetails?.mobile || '',
              selectedCountry: bookingDetails.customerDetails?.address?.country || 'UK',
              postCode: bookingDetails.customerDetails?.address?.postcode || '',
              cityTown: bookingDetails.customerDetails?.address?.city || '',
              addressFirstLine: bookingDetails.customerDetails?.address?.line1 || '',
              addressSecondLine: bookingDetails.customerDetails?.address?.line2 || '',
              houseNameNumber: bookingDetails.customerDetails?.address?.houseNameNumber || '',
              contactable: bookingDetails.customerDetails?.contactable || false, // Marketing consent
              isGuest: true, // From manual example - always true for public bookings
              baseUrl: window.location.origin
            },
            
            // Basket structure following BasketRequestViewModel from manual
            basket: {
              pageId: bookingDetails.pageId || bookingDetails?.selectedTickets?.[0]?.pageId,
              orderId: basketId,
              customerId: bookingDetails.customerDetails?.webCustomerId || null, // Don't generate - use registered customer ID
              giftAidSelected: false,
              discounts: bookingDetails.discounts || [],
              discountsFromMembership: [],
              items: (bookingDetails.selectedTickets || []).map(ticket => ({
                pageType: 'WebPage',
                pageId: bookingDetails.pageId || bookingDetails?.selectedTickets?.[0]?.pageId,
                webSaleItemId: ticket.webSaleItemId,
                webSaleItemTypeID: ticket.webSaleItemTypeID || 2,
                quantity: ticket.quantity,
                selectedDate: bookingDetails.isMembershipJourney ? null : (bookingDetails.selectedDate ? 
                  normalizeDate(bookingDetails.selectedDate) : null),
                fixedDonationAmount: 0,
                giftAidEligible: false,
                isSysGift: false,
                isSysMemberCard: false,
                name: ticket.name,
                // CRITICAL: Include individual event metadata for paid orders too
                eventId: bookingDetails.isMembershipJourney ? null : (ticket.eventId || bookingDetails.selectedTimeSlot?.eventId || null),
                eventCapacityUsageId: bookingDetails.isMembershipJourney ? null : (ticket.eventCapacityUsageId || bookingDetails.selectedTimeSlot?.eventCapacityUsageId || null),
                reservationExpiryDateTime: bookingDetails.isMembershipJourney ? null : (ticket.reservationExpiryDateTime || new Date(Date.now() + 30 * 60 * 1000).toISOString())
              })),
              memberships: bookingDetails.memberships || [],
              modifiers: [],
              newAccountCreated: false,
              questions: [],
              cateringCustomTextResponse1: '',
              cateringCustomTextResponse2: '',
              cateringFacilityId: null,
              cateringMode: 'NotSetup'
            }
          };
          
          // Use server-side proxy to avoid CORS/anti-forgery on Payment/Initialize
          const finalizeBase = (typeof window !== 'undefined' && window.location && window.location.hostname === 'localhost')
            ? 'http://localhost:8888/Website/front-end/public'
            : 'https://belvoircastle.com';
          const finalizeProxy = `${finalizeBase}/api/venpos-finalize.php`;

          const paymentInitResult = await fetch(finalizeProxy, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({
              action: 'paymentInitialize',
              locationId: locationId,
              mainWebPageId: paymentInitPayload.pageId,
              startId: paymentInitPayload.pageId,
              payload: paymentInitPayload
            })
          }).then(async (r) => ({ ok: r.ok, status: r.status, json: await r.json() }))
            .then(res => {
              if (!res.ok) {
                return { isError: true, error: res.json?.error || `Proxy HTTP ${res.status}`, data: null };
              }
              // Pass through upstream JSON (should be Venpos format with isError/data)
              return res.json;
            })
            .catch(err => ({ isError: true, error: err?.message || 'Proxy request failed', data: null }));
          
          if (!paymentInitResult.isError && paymentInitResult.data?.paymentToken) {

            // Use official Checkout/Confirm flow
            const confirmUrl = `${VENPOS_API_BASE}/Checkout/Confirm?token=${paymentInitResult.data.paymentToken}&type=0&locationId=${locationId}&startId=${bookingDetails.pageId}`;

            // Fetch transaction header to expose reference for our confirmation screen
            let headerData = null;
            try {
              const headerUrl = `${VENPOS_API_BASE}/WebTransHeader/Get?locationId=${locationId}&id=${basketId}`;
              const headerResp = await makeApiRequest(headerUrl, { method: 'GET' });
              if (!headerResp.isError) {
                headerData = headerResp.data || headerResp;
              }
            } catch (_) {}

            // Do NOT redirect to vendor UI; return control to caller immediately
            return { 
              isError: false, 
              data: { 
                redirected: false, 
                url: confirmUrl,
                bookingComplete: true,
                redirectDelay: 0,
                bookingReference: headerData?.transRef || headerData?.transactionSummaryModel?.transactionReference || null,
                webTransHeaderID: headerData?.webTransHeaderID || basketId
              } 
            };
          } else {

          }
        } catch (initError) {

        }
      } // End of non-membership journey Payment/Initialize block

      // For both membership and non-membership free bookings, create the order via CreateFreeOrder
      console.log('🚀 [CreateFreeOrder] Starting CreateFreeOrder flow', {
        isFreeBooking,
        isMembershipJourney: bookingDetails.isMembershipJourney,
        totalAmount: bookingDetails.totalAmount,
        basketId
      });
      
      try {
          // Build CreateFreeOrder payload closely matching real network example
          const orderId = basketId;
          const pageId = bookingDetails.pageId || bookingDetails?.selectedTickets?.[0]?.pageId;
          console.log('📦 [CreateFreeOrder] Building payload', { orderId, pageId, membershipsCount: bookingDetails.memberships?.length || 0 });
          // For CreateFreeOrder, use the same customerId that's already in the basket
          // We'll get this from the current basket state or use the provided one
          const customerId = bookingDetails.customerDetails?.webCustomerId || null; // Don't generate - use registered customer ID

          // Build items from provided booking context to avoid CORS on Basket/Get in dev
          let items = [];
          // Flatten venpos basket items (if available) - USE THESE FIRST as they have all event metadata (same as paid orders)
          const venposBasketItems = (bookingDetails?.venposBasket?.data?.groups || [])
            .flatMap(g => g.items || [])
            .map(it => ({
              ...it,
              webSaleItemId: it.webSaleItemId || it.id || it.webSaleItemID || it.webSaleItemCode,
              selectedDate: it.selectedDate || it.startDateTime || null
            }));

          // ✅ CRITICAL: Use basket items FIRST (same as paid orders) - they already have event metadata
          if (venposBasketItems.length > 0) {
            console.log('✅ [CreateFreeOrder] Using items from venposBasket (same approach as paid orders)', {
              basketItemsCount: venposBasketItems.length,
              itemsWithEventData: venposBasketItems.filter(it => it.eventId && it.eventCapacityUsageId).length
            });
            
            // Build a map of selectedTickets by webSaleItemId to merge additional data (questions, modifiers, etc.)
            const selectedTicketsMap = new Map();
            if (Array.isArray(bookingDetails.selectedTickets)) {
              bookingDetails.selectedTickets.forEach(t => {
                if ((t.quantity || 0) > 0) {
                  selectedTicketsMap.set(String(t.webSaleItemId), t);
                }
              });
            }
            
            // Use basket items directly - they already have all the correct event metadata
            items = venposBasketItems.map(it => {
              // Find matching selectedTicket to merge question answers, modifiers, participant details
              const matchingTicket = selectedTicketsMap.get(String(it.webSaleItemId));
              
              return {
                pageType: it.pageType || 'WebPage',
                pageId: it.pageId || pageId,
                webSaleItemId: it.webSaleItemId,
                webSaleItemTypeID: it.webSaleItemTypeID || it.webSaleItemTypeId || 2,
                name: it.name || it.description || matchingTicket?.name || '',
                quantity: it.quantity || 1,
                fixedDonationAmount: 0,
                giftAidEligible: it.giftAidEligible || false,
                isSysGift: false,
                isSysMemberCard: false,
                selectedDate: it.selectedDate || it.startDateTime || selectedDateTime,
                reservationExpiryDateTime: it.reservationExpiryDateTime || new Date(Date.now() + 30 * 60 * 1000).toISOString(),
                // ✅ CRITICAL: Basket items already have event metadata from when basket was created/loaded (same as paid orders)
                eventId: it.eventId || null,
                eventCapacityUsageId: it.eventCapacityUsageId || null,
                // Merge question answers, modifiers, and participant details from selectedTickets (preserves user input)
                questionAnswers: matchingTicket?.questionAnswers || it.questionAnswers || {},
                eventQuestions: matchingTicket?.eventQuestions || it.eventQuestions || null,
                modifierSelection: matchingTicket?.modifierSelection || it.modifierSelection || null,
                participantDetails: matchingTicket?.participantDetails || it.participantDetails || null,
                participantAnswers: matchingTicket?.participantAnswers || it.participantAnswers || {},
                parentWebSaleItemId: it.parentWebSaleItemId || matchingTicket?.parentWebSaleItemId || null
              };
            });
          }
          
          // Fallback to selectedTickets only if basket items aren't available
          if (items.length === 0 && Array.isArray(bookingDetails.selectedTickets)) {
            console.log('⚠️ [CreateFreeOrder] No basket items found, falling back to selectedTickets');

            items = bookingDetails.selectedTickets
              .filter(t => (t.quantity || 0) > 0)
              .map((t, idx) => {
                // Check if this is an upsell
                const isUpsell = t.isUpsellItem || t.pageType === 'EventTemplate';

                return {
                  // Preserve EventTemplate for upsells
                  pageType: isUpsell ? 'EventTemplate' : 'WebPage',
                  // Use upsell's own pageId, or main event pageId
                  pageId: isUpsell ? (t.pageId || t.parentUpsellId) : pageId,
                webSaleItemId: t.webSaleItemId,
                webSaleItemTypeID: t.webSaleItemTypeID || 2,
                name: t.name,
                quantity: t.quantity,
                fixedDonationAmount: 0,
                giftAidEligible: false,
                isSysGift: false,
                isSysMemberCard: false,
                  // CRITICAL: Preserve question answers and event questions for question text lookup
                  questionAnswers: t.questionAnswers || {},
                  eventQuestions: t.eventQuestions || null,
                  // For membership journeys, don't include date/time/event fields
                  ...(bookingDetails.isMembershipJourney ? {} : {
                    // Upsells have their own selectedDate
                    selectedDate: isUpsell ? (t.selectedDate || t.selectedEventTime?.startDateTime) : selectedDateTime,
                    reservationExpiryDateTime: t.reservationExpiryDateTime || new Date(Date.now() + 30 * 60 * 1000).toISOString(),
                    // Upsells have their own event metadata
                    // CRITICAL: Use ticket's own capacity ID first, then fallback appropriately
                    eventCapacityUsageId: t.eventCapacityUsageId || (isUpsell ? t.selectedEventTime?.eventCapacityUsageId : bookingDetails.selectedTimeSlot?.eventCapacityUsageId) || null,
                    eventId: isUpsell ? (t.eventId || t.selectedEventTime?.eventId) : (bookingDetails.selectedTimeSlot?.eventId || null),
                  }),
                  // CRITICAL: Preserve modifier selection, question answers, participant details and answers
                  modifierSelection: t.modifierSelection || null,
                  questionAnswers: t.questionAnswers || {},
                  participantDetails: t.participantDetails || null, // Full API response (array)
                  participantAnswers: t.participantAnswers || {}, // User input (flat object)
                  // Include parentWebSaleItemId for upsells
                  ...(isUpsell && t.parentWebSaleItemId && { parentWebSaleItemId: t.parentWebSaleItemId })
                };
              });
          }

          // As a final guard, ensure we have at least one item structure
          // For membership journeys, items might be empty if we only have memberships
          if (items.length === 0 && !bookingDetails.isMembershipJourney) {
            items = [{
              pageType: 'WebPage',
              pageId: pageId || orderId,
              fixedDonationAmount: 0,
              giftAidEligible: false,
              isSysGift: false,
              isSysMemberCard: false,
              quantity: 1,
              selectedDate: selectedDateTime,
              reservationExpiryDateTime: new Date(Date.now() + 30 * 60 * 1000).toISOString()
            }];
          }

          // CRITICAL: Cache ALL modifiers from original items BEFORE any preload/processing
          // This is essential because later operations may consolidate items (e.g., 2 items with quantity:1 each become 1 item with quantity:2)
          // but we need to preserve ALL modifier selections as separate entries in the modifiers array
          const originalModifiersArray = [];
          try {

            (Array.isArray(items) ? items : []).forEach((it, idx) => {
              if (it && it.modifierSelection) {

                originalModifiersArray.push({
                  webSaleItemId: it.webSaleItemId,
                  productModifierId: it.modifierSelection.modifierId,
                  productModifierTextId: it.modifierSelection.modifierTextId,
                  value: "1",
                  // Index will be assigned later when building final array
                  modifierSelection: it.modifierSelection // Keep full object for reference
                });
              }
            });


          } catch (cacheErr) {

          }

          // Ensure event metadata is present; fetch events and attach eventId/capacity where possible
          // SKIP for membership journeys - they don't need event metadata
          if (!bookingDetails.isMembershipJourney) {
            try {
              const getTimesUrl = new URL(`${VENPOS_API_BASE}/Event/GetTimes`);
              getTimesUrl.searchParams.append('locationId', locationId);
              getTimesUrl.searchParams.append('mainWebPageId', pageId);
              getTimesUrl.searchParams.append('id', pageId);
              getTimesUrl.searchParams.append('pageType', 'WebPage');
            getTimesUrl.searchParams.append('date', selectedDateStr);

            const evtResp = await fetch(getTimesUrl.toString(), {
              method: 'POST',
              headers: {
                'Accept': 'application/json, text/javascript, */*; q=0.01',
                'Content-Type': 'application/json',
                'X-Requested-With': 'XMLHttpRequest'
              },
              body: JSON.stringify({ items: [] })
            });

            if (evtResp.ok) {
              const evtJson = await evtResp.json();
              const events = (evtJson?.data?.events) || evtJson?.events || [];
              const match = events.find(ev => {
                const dt = new Date(ev?.startDateTime);
                const hh = String(dt.getHours()).padStart(2, '0');
                const mm = String(dt.getMinutes()).padStart(2, '0');
                return `${hh}:${mm}` === selectedTimeStr;
              });
              if (match) {
                items = items.map(it => ({
                  ...it,
                  eventId: it.eventId || match.eventId || match.id || null,
                  eventCapacityUsageId: it.eventCapacityUsageId || match.eventCapacityUsageId || null
                }));
              }
            }
          } catch (eMeta) {

          }
          } // End of non-membership event metadata fetching

          // Validate times prior to creating free order (using enriched items)
          // For memberships, send minimal payload (no date/time fields)
          try {
            // For membership journeys, use minimal items array without date/time fields
            const itemsForValidation = bookingDetails.isMembershipJourney 
              ? items.map(item => ({
                  pageType: item.pageType,
                  pageId: item.pageId,
                  webSaleItemId: item.webSaleItemId,
                  webSaleItemTypeID: item.webSaleItemTypeID,
                  quantity: item.quantity
                }))
              : items;

            let validateResp = { isError: false };

            // Skip ValidateTimes for Flower & Garden Show free bookings to avoid event-validity errors
            if (!(pageId === FLOWER_GARDEN_PAGE_ID && isFreeBooking && !bookingDetails.isPaidBooking)) {
              // Try direct call first with proper headers matching browser requests
              const validateTimesUrl = `${VENPOS_API_BASE}/Basket/ValidateTimes?locationId=${locationId}`;

              validateResp = await makeApiRequest(validateTimesUrl, {
                method: 'POST',
                headers: {
                  'Accept': 'application/json, text/javascript, */*; q=0.01',
                  'Accept-Language': 'en-GB,en-US;q=0.9,en;q=0.8',
                  'Content-Type': 'application/json',
                  'Origin': 'https://online1.venpos.net',
                  'Referer': `https://online1.venpos.net/VenPosJourneyUI/${bookingDetails.isMembershipJourney ? 'Membership' : 'Checkout'}?locationId=${locationId}&mainWebPageId=${pageId}&startId=${pageId}`,
                  'User-Agent': 'Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/140.0.0.0 Safari/537.36',
                  'X-Requested-With': 'XMLHttpRequest',
                  'Cache-Control': 'no-cache',
                  'Pragma': 'no-cache',
                  'sec-ch-ua': '"Chromium";v="140", "Not=A?Brand";v="24", "Google Chrome";v="140"',
                  'sec-ch-ua-mobile': '?0',
                  'sec-ch-ua-platform': '"macOS"',
                  'sec-fetch-dest': 'empty',
                  'sec-fetch-mode': 'cors',
                  'sec-fetch-site': 'same-origin',
                  'DNT': '1',
                  'Connection': 'keep-alive',
                  'Upgrade-Insecure-Requests': '1'
                },
                body: JSON.stringify(itemsForValidation)
              });

              // If direct call fails, try through proxy
              if (validateResp.isError) {

                const proxyUrl = `${window.location.origin}/api/venpos-finalize.php`;
                validateResp = await fetch(proxyUrl, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  credentials: 'include', // Forward browser cookies to proxy
                    body: JSON.stringify({
                    action: 'validateTimes',
                    locationId: locationId,
                    payload: itemsForValidation,
                    mainWebPageId: pageId
                  })
                }).then(async (r) => {
                  if (!r.ok) {
                    const errorText = await r.text();
                    return { isError: true, error: `Proxy HTTP ${r.status}: ${errorText}`, data: null };
                  }
                  return r.json();
                });
              }
            }

          } catch (ve) {

          }
          console.log('✅ [CreateFreeOrder] Basket times validated');

          // Build discounts and de-duplicate to avoid multiple submissions (e.g., FS2 repeated)


          const discounts = [];
          if (Array.isArray(bookingDetails.discounts) && bookingDetails.discounts.length > 0) {
            discounts.push(...bookingDetails.discounts);
          }

          // Include Family Saver if provided by caller context
          if (bookingDetails.familySaverCode) {
            discounts.push(bookingDetails.familySaverCode);
          }

          const uniqueDiscounts = Array.from(new Set(discounts.filter(Boolean)));

          // Enrich items and discounts using a fresh Basket/Load so we get server-resolved
          // eventId and eventCapacityUsageId and the final applied discount codes (e.g. ADMIN100)
          let preloadBasketItems = null; // Store the exact items from preload for CreateFreeOrder
          try {
            const preloadPayload = {
              pageId,
              orderId,
              items: items.map(it => {
                // Check if this is an upsell (EventTemplate)
                const isUpsell = it.pageType === 'EventTemplate';
                
                return {
                  // Preserve EventTemplate for upsells
                  pageType: isUpsell ? 'EventTemplate' : 'WebPage',
                  // Use upsell's own pageId, or main event pageId
                  pageId: isUpsell ? it.pageId : pageId,
                webSaleItemId: it.webSaleItemId,
                webSaleItemTypeID: it.webSaleItemTypeID,
                quantity: it.quantity,
                  // Upsells have their own selectedDate, main tickets use selectedDateTime
                  selectedDate: isUpsell ? it.selectedDate : selectedDateTime,
                fixedDonationAmount: 0,
                // CRITICAL: Preserve name field to prevent undefined names in CreateFreeOrder
                name: it.name || `${isUpsell ? 'Upsell' : 'Ticket'} ${items.indexOf(it) + 1}`,
                giftAidEligible: it.giftAidEligible || false,
                isSysGift: it.isSysGift || false,
                isSysMemberCard: it.isSysMemberCard || false,
                // CRITICAL: Preserve event metadata to maintain locked timeslot
                eventId: it.eventId,
                eventCapacityUsageId: it.eventCapacityUsageId,
                  reservationExpiryDateTime: it.reservationExpiryDateTime,
                  // Include parentWebSaleItemId for upsells
                  ...(isUpsell && it.parentWebSaleItemId && { parentWebSaleItemId: it.parentWebSaleItemId }),
                  // CRITICAL: Preserve question answers and modifiers
                  questionAnswers: it.questionAnswers || {},
                  modifierSelection: it.modifierSelection || null
                };
              }),
              giftAidSelected: false,
              discounts: uniqueDiscounts,
              discountsFromMembership: [],
              customerId
            };
            const preload = await this.loadBasket(locationId, preloadPayload, null);
            if (!preload.isError && preload.data) {

              // Map event metadata from basket response
              const groupItems = Array.isArray(preload.data.groups) ? preload.data.groups.flatMap(g => g.items || []) : [];

              // CRITICAL: Validate that the basket still has valid event metadata after discount reload
              const itemsWithValidEventData = groupItems.filter(item => item.eventId && item.eventCapacityUsageId);
              if (itemsWithValidEventData.length === 0) {


                // Try to re-reserve capacity for items that lost their event metadata

                try {
                  const retryItems = items.map(item => ({
                    ...item,
                    // Ensure we have the correct reservationExpiryDateTime for re-reservation
                    reservationExpiryDateTime: new Date(Date.now() + 30 * 60 * 1000).toISOString()
                  }));

                  const capacityRetry = await venposService.addEventCapacityUsages(locationId, pageId, retryItems, orderId, uniqueDiscounts);
                  if (!capacityRetry.isError && capacityRetry.data) {
                    const retryResponseItems = capacityRetry.data.items || capacityRetry.data || [];

                    // Merge the new capacity IDs back into the original items
                    const capById = new Map(retryResponseItems.map(ri => [ri.webSaleItemId, ri]));
                    groupItems = groupItems.map(it => ({
                      ...it,
                      eventCapacityUsageId: capById.get(it.webSaleItemId)?.eventCapacityUsageId || it.eventCapacityUsageId,
                      eventId: capById.get(it.webSaleItemId)?.eventId || it.eventId
                    }));

                  }
                } catch (capErr) {

                  throw new Error('Basket reload lost event metadata and re-reservation failed. Please try selecting a different time slot or contact support.');
                }
              }

              // Store the exact items from preload for CreateFreeOrder (these have the correct reservationExpiryDateTime)
              preloadBasketItems = groupItems;

              // Build a composite map keyed by webSaleItemId + selectedDate (to the minute) to avoid mismatches
              const normalizeToMinuteKey = (dtStr) => {
                try {
                  const d = new Date(dtStr);
                  const y = d.getFullYear();
                  const m = String(d.getMonth() + 1).padStart(2, '0');
                  const d2 = String(d.getDate()).padStart(2, '0');
                  const hh = String(d.getHours()).padStart(2, '0');
                  const mm = String(d.getMinutes()).padStart(2, '0');
                  return `${y}-${m}-${d2}T${hh}:${mm}`;
                } catch (_) {
                  // If it's already a partial string like YYYY-MM-DDTHH:MM:SS, slice to minutes
                  return String(dtStr || '').slice(0, 16);
                }
              };

              const compositeMap = new Map(
                groupItems.map(gi => {
                  const key = `${String(gi.webSaleItemId)}|${normalizeToMinuteKey(gi.selectedDate)}`;
                  return [key, gi];
                })
              );

              // Build a map of original items by webSaleItemId + selectedDate for merging modifier/question/participant data
              const originalItemsMap = new Map();
              items.forEach(it => {
                const key = `${String(it.webSaleItemId)}|${normalizeToMinuteKey(it.selectedDate)}`;
                originalItemsMap.set(key, it);
              });

              // Enrich preloadBasketItems with modifier/question/participant data from original items
              preloadBasketItems = preloadBasketItems.map(gi => {
                const key = `${String(gi.webSaleItemId)}|${normalizeToMinuteKey(gi.selectedDate)}`;
                const originalItem = originalItemsMap.get(key) || items.find(x => String(x.webSaleItemId) === String(gi.webSaleItemId));
                
                // Check if this is an upsell
                const isUpsell = gi.pageType === 'EventTemplate' || (originalItem && originalItem.pageType === 'EventTemplate');
                
                return {
                  ...gi,
                  // CRITICAL: Preserve modifier selection, question answers, participant details, and event questions from original items
                  modifierSelection: originalItem?.modifierSelection || null,
                  questionAnswers: originalItem?.questionAnswers || {},
                  eventQuestions: originalItem?.eventQuestions || null, // Preserve event questions for question text lookup
                  participantDetails: originalItem?.participantDetails || null,
                  participantAnswers: originalItem?.participantAnswers || {},
                  // CRITICAL: Upsells keep their own selectedDate, main items use selectedDateTime
                  selectedDate: isUpsell && originalItem?.selectedDate ? originalItem.selectedDate : (gi.selectedDate || selectedDateTime),
                  // Preserve personIndex if it exists
                  personIndex: originalItem?.personIndex,
                  // Preserve parentWebSaleItemId for upsells
                  ...(originalItem?.parentWebSaleItemId && { parentWebSaleItemId: originalItem.parentWebSaleItemId })
                };
              });

              items = items.map(it => {
                const key = `${String(it.webSaleItemId)}|${normalizeToMinuteKey(it.selectedDate)}`;
                const gi = compositeMap.get(key) || groupItems.find(x => String(x.webSaleItemId) === String(it.webSaleItemId));
                
                // Check if this is an upsell
                const isUpsell = it.pageType === 'EventTemplate';
                
                const enriched = {
                  ...it,
                  // Only override if the basket response has valid values, otherwise keep the original values
                  eventId: (gi && gi.eventId) ? gi.eventId : (it.eventId || null),
                  eventCapacityUsageId: (gi && gi.eventCapacityUsageId) ? gi.eventCapacityUsageId : (it.eventCapacityUsageId || null),
                  // CRITICAL: Upsells keep their own selectedDate, main items use selectedDateTime
                  selectedDate: isUpsell ? it.selectedDate : selectedDateTime,
                  // CRITICAL: Preserve modifier selection, question answers, and event questions
                  modifierSelection: it.modifierSelection || null,
                  questionAnswers: it.questionAnswers || {},
                  eventQuestions: it.eventQuestions || null // Preserve event questions for question text lookup
                };

                return enriched;
              });

              // Ensure discounts include any server-side applied codes
              const preloadDiscountCodes = (preload.data.discounts || []).map(d => d?.code).filter(Boolean);
              const merged = Array.from(new Set([...
                uniqueDiscounts,
                ...preloadDiscountCodes
              ]));
              if (merged.length !== uniqueDiscounts.length) {

              }
              // Overwrite with merged list for CreateFreeOrder
              while (uniqueDiscounts.length) uniqueDiscounts.pop();
              merged.forEach(c => uniqueDiscounts.push(c));
            }
          } catch (preErr) {
            
          }

          // Final fallback: if eventCapacityUsageId is still null, try to get it from Event/GetTimes
          const itemsNeedingEventId = items.filter(it => !it.eventCapacityUsageId);
          if (itemsNeedingEventId.length > 0) {

            try {
              const getTimesUrl = new URL(`${VENPOS_API_BASE}/Event/GetTimes`);
              getTimesUrl.searchParams.append('locationId', locationId);
              getTimesUrl.searchParams.append('mainWebPageId', pageId);
              getTimesUrl.searchParams.append('id', pageId);
              getTimesUrl.searchParams.append('pageType', 'WebPage');
              getTimesUrl.searchParams.append('date', selectedDateStr);

              const evtResp = await fetch(getTimesUrl.toString(), {
                method: 'POST',
                headers: {
                  'Accept': 'application/json, text/javascript, */*; q=0.01',
                  'Content-Type': 'application/json',
                  'X-Requested-With': 'XMLHttpRequest'
                },
                body: JSON.stringify({ items: [] })
              });

              if (evtResp.ok) {
                const evtJson = await evtResp.json();

                // Try multiple possible response structures
                let events = [];
                if (evtJson?.data?.events) {
                  events = evtJson.data.events;
                } else if (evtJson?.events) {
                  events = evtJson.events;
                } else if (evtJson?.data?.timeSlots) {
                  events = evtJson.data.timeSlots;
                } else if (evtJson?.timeSlots) {
                  events = evtJson.timeSlots;
                }

                const match = events.find(ev => {
                  const dt = new Date(ev?.startDateTime || ev?.time || ev?.dateTime);
                  const hh = String(dt.getHours()).padStart(2, '0');
                  const mm = String(dt.getMinutes()).padStart(2, '0');
                  return `${hh}:${mm}` === selectedTimeStr;
                });
                
                if (match) {


                  // Try to find eventCapacityUsageId in various possible field names
                  const possibleCapacityIds = [
                    match.eventCapacityUsageId,
                    match.capacityUsageId,
                    match.eventCapacityId,
                    match.capacityId,
                    match.usageId
                  ].filter(Boolean);

                  items = items.map(it => ({
                    ...it,
                    eventId: it.eventId || match.eventId || match.id || null,
                    eventCapacityUsageId: it.eventCapacityUsageId || possibleCapacityIds[0] || null
                  }));

          } else {

                }
              }
            } catch (fallbackErr) {

            }
          }

          // Final fallback: if we still don't have eventCapacityUsageId, try to get it from the time slots we loaded earlier
          const itemsStillNeedingCapacityId = items.filter(it => !it.eventCapacityUsageId && it.eventId);
          if (itemsStillNeedingCapacityId.length > 0) {

            try {
              // Use the time slots we already loaded in the component
              const timeSlotsResponse = await this.getTimeSlots(locationId, pageId, selectedDateStr, []);
              const timeSlots = timeSlotsResponse?.data?.timeSlots || timeSlotsResponse?.timeSlots || [];

              const matchingTimeSlot = timeSlots.find(ts => {
                const tsTime = ts.time || ts.startTime;
                return tsTime === selectedTimeStr;
              });
              
              if (matchingTimeSlot) {

                items = items.map(it => ({
                  ...it,
                  eventCapacityUsageId: it.eventCapacityUsageId || matchingTimeSlot.eventCapacityUsageId || null
                }));

              }
            } catch (timeSlotErr) {

            }
          }

          // Enrich missing event metadata from venpos basket items and selected time slot (no network)
          if (!bookingDetails.isMembershipJourney && items.length > 0) {
            // ✅ ENHANCED: More aggressive fallback to selectedTimeSlot for all items
            const timeSlotEventId = bookingDetails.selectedTimeSlot?.eventId;
            const timeSlotCapacityId = bookingDetails.selectedTimeSlot?.eventCapacityUsageId;
            
            console.log('🔧 [CreateFreeOrder] Enriching items with event metadata', {
              itemsCount: items.length,
              timeSlotHasEventId: !!timeSlotEventId,
              timeSlotHasCapacityId: !!timeSlotCapacityId,
              basketItemsCount: venposBasketItems.length
            });

            items = items.map(it => {
              // If already has both, skip
              if (it.eventId && it.eventCapacityUsageId) {
                console.log('✅ [CreateFreeOrder] Item already has event metadata:', it.webSaleItemId);
                return it;
              }

              // Try to match by webSaleItemId first from venpos basket
              const match = venposBasketItems.find(bi => String(bi.webSaleItemId) === String(it.webSaleItemId));
              if (match && (match.eventId || match.eventCapacityUsageId)) {
                console.log('✅ [CreateFreeOrder] Enriched from basket match:', { 
                  webSaleItemId: it.webSaleItemId,
                  hasEventId: !!match.eventId,
                  hasCapacityId: !!match.eventCapacityUsageId
                });
                return {
                  ...it,
                  eventId: it.eventId || match.eventId || match.id || timeSlotEventId || null,
                  eventCapacityUsageId: it.eventCapacityUsageId || match.eventCapacityUsageId || timeSlotCapacityId || null,
                  selectedDate: it.selectedDate || match.selectedDate || match.startDateTime || it.selectedDate
                };
              }

              // Fallback: try to get from original selectedTickets (they might have event metadata from time slot selection)
              if (Array.isArray(bookingDetails.selectedTickets)) {
                const originalTicket = bookingDetails.selectedTickets.find(t => 
                  String(t.webSaleItemId) === String(it.webSaleItemId) && (t.quantity || 0) > 0
                );
                if (originalTicket && (originalTicket.eventId || originalTicket.eventCapacityUsageId)) {
                  console.log('✅ [CreateFreeOrder] Enriched from selectedTickets:', {
                    webSaleItemId: it.webSaleItemId,
                    hasEventId: !!originalTicket.eventId,
                    hasCapacityId: !!originalTicket.eventCapacityUsageId
                  });
                  return {
                    ...it,
                    eventId: it.eventId || originalTicket.eventId || timeSlotEventId || null,
                    eventCapacityUsageId: it.eventCapacityUsageId || originalTicket.eventCapacityUsageId || timeSlotCapacityId || null
                  };
                }
              }

              // ✅ ENHANCED: Always use selected time slot as fallback (same event for all tickets in most cases)
              if (timeSlotEventId && timeSlotCapacityId) {
                console.log('✅ [CreateFreeOrder] Using selectedTimeSlot as fallback for:', it.webSaleItemId);
                return {
                  ...it,
                  eventId: it.eventId || timeSlotEventId,
                  eventCapacityUsageId: it.eventCapacityUsageId || timeSlotCapacityId
                };
              }

              console.warn('⚠️ [CreateFreeOrder] No event metadata found for item:', it.webSaleItemId);
              return it;
            });
          }

          // Validate that we have the required event metadata for CreateFreeOrder
          // SKIP this validation for membership journeys (memberships don't need event metadata)
          if (!bookingDetails.isMembershipJourney) {
            const itemsMissingEventData = items.filter(it => !it.eventId || !it.eventCapacityUsageId);
            if (itemsMissingEventData.length > 0) {

              // Final attempt: try to get event metadata from Event/GetTimes if items are missing it

              try {
                for (const missingItem of itemsMissingEventData) {
                  if (missingItem.selectedDate) {
                    // Check if this is an upsell (use its own pageId)
                    const isUpsell = missingItem.pageType === 'EventTemplate';
                    const itemPageId = isUpsell ? missingItem.pageId : pageId;

                    const timeSlotResponse = await this.getTimeSlots(locationId, itemPageId, missingItem.selectedDate.split('T')[0], [missingItem]);
                    const availableSlots = timeSlotResponse?.data?.timeSlots || [];

                    // Find the matching time slot and extract event metadata
                    const timeToMatch = missingItem.selectedDate.split('T')[1]?.substring(0, 5);
                    const matchingSlot = availableSlots.find(slot =>
                      slot.time === timeToMatch || slot.startDateTime?.includes(timeToMatch)
                    );

                    if (matchingSlot) {
                      missingItem.eventId = matchingSlot.eventId;
                      missingItem.eventCapacityUsageId = matchingSlot.eventCapacityUsageId;

                    } else {

                    }
                  }
                }
              } catch (timeSlotErr) {

              }

              // Re-check after fallback attempt
              const stillMissingEventData = items.filter(it => !it.eventId || !it.eventCapacityUsageId);
              if (stillMissingEventData.length > 0) {
                // For all pages, if event metadata is missing after all fallback attempts, create a comprehensive error
                const pageName = pageId === FLOWER_GARDEN_PAGE_ID ? 'Flower & Garden Show' : 
                                 pageId === GROTTO_RETAIL_VILLAGE_PAGE_ID ? 'Grotto Retail Village' : 
                                 'this event';
                
                // Log detailed diagnostic information
                const diagnosticInfo = {
                  pageId,
                  pageName,
                  missingItemsCount: stillMissingEventData.length,
                  missingItems: stillMissingEventData.map(it => ({
                    webSaleItemId: it.webSaleItemId,
                    name: it.name,
                    selectedDate: it.selectedDate,
                    hasEventId: !!it.eventId,
                    hasEventCapacityUsageId: !!it.eventCapacityUsageId
                  })),
                  selectedTimeSlot: bookingDetails.selectedTimeSlot ? {
                    hasEventId: !!bookingDetails.selectedTimeSlot.eventId,
                    hasEventCapacityUsageId: !!bookingDetails.selectedTimeSlot.eventCapacityUsageId,
                    time: bookingDetails.selectedTimeSlot.time
                  } : 'not provided',
                  selectedTicketsCount: bookingDetails.selectedTickets?.length || 0,
                  venposBasketItemsCount: venposBasketItems.length,
                  basketId,
                  orderId,
                  customerEmail: bookingDetails.customerDetails?.email
                };
                
                console.error(`❌ [CreateFreeOrder] Missing event metadata for ${pageName} after all fallback attempts`, diagnosticInfo);
                
                const errorMessage = `Unable to complete your booking for ${pageName}. Required booking information could not be retrieved. Please try selecting a different time slot or contact support at info@belvoircastle.com if the problem persists.`;
                
                // Create a special error that includes diagnostic info for the error handler
                const error = new Error(errorMessage);
                error.name = 'BookingMetadataMissingError';
                error.diagnosticInfo = diagnosticInfo;
                error.requiresSystemStatusUpdate = true;
                error.requiresEmailNotification = true;
                
                throw error;
              }
            }
          } else {
            // For membership journeys, log that we're skipping event validation
            console.log('✅ [CreateFreeOrder] Skipping event metadata validation for membership journey');
          }

          // Use the actual basketId as orderId, not the webTransHeaderId
          const actualOrderId = basketId;
          
          // Redirect back to our booking journey (avoid vendor confirmation screen)
          const redirectUrl = `${window.location.origin}/booking-journey`;
          const venposConfirmationUrl = bookingDetails.isMembershipJourney
            ? `https://online1.venpos.net/VenPosJourneyUI/Confirmation?WebTransHeaderId=${actualOrderId}&locationId=${locationId}&mainWebPageId=${pageId}&startId=${pageId}`
            : redirectUrl;
          
          // Ensure reservationExpiryDateTime format matches source example exactly
          if (items && items.length > 0) {
            items.forEach(item => {
              if (typeof item.reservationExpiryDateTime === 'string' && item.reservationExpiryDateTime.trim().length > 0) {
                // Ensure proper format with exactly 7 digits of milliseconds and local timezone
                if (item.reservationExpiryDateTime.endsWith('Z')) {
                  // Convert from UTC to local timezone format
                  item.reservationExpiryDateTime = item.reservationExpiryDateTime.replace('Z', '+01:00');
                }
                // Ensure milliseconds format matches source example exactly
                const parts = item.reservationExpiryDateTime.split('+');
                if (parts.length === 2) {
                  const dateTime = parts[0];
                  const timezone = '+' + parts[1];
                  const dotIndex = dateTime.lastIndexOf('.');
                  if (dotIndex !== -1) {
                    const milliseconds = dateTime.substring(dotIndex + 1);
                    // Ensure exactly 7 digits by padding with zeros if needed
                    const paddedMilliseconds = (milliseconds + '0000000').substring(0, 7);
                    item.reservationExpiryDateTime = dateTime.substring(0, dotIndex + 1) + paddedMilliseconds + timezone;
                  }
                }

              } else {
                // Omit if not present; server may still accept payload without it
                if ('reservationExpiryDateTime' in item && (item.reservationExpiryDateTime == null || item.reservationExpiryDateTime === '')) {
                  delete item.reservationExpiryDateTime;
                }
                
              }
            });
          }

          // Use formatted questions from bookingDetails if available (from Basket/Validate)
          // Otherwise extract from items
          let allQuestions = [];
          if (bookingDetails.formattedEventQuestions && bookingDetails.formattedEventQuestions.length > 0) {

            allQuestions = bookingDetails.formattedEventQuestions;
          } else {

            let questionIndex = 0;
            items.forEach(item => {
              if (item.questionAnswers && typeof item.questionAnswers === 'object') {
                // questionAnswers is a map of questionId__occurrenceIndex -> answer value
                Object.entries(item.questionAnswers).forEach(([rawKey, answerValue]) => {
                  if (answerValue) {
                    // Parse the question key (may include occurrence index like "questionId__0")
                    let baseId = rawKey;
                    let occIndex = 0;
                    if (typeof rawKey === 'string' && rawKey.includes('__')) {
                      const parts = rawKey.split('__');
                      baseId = parts[0];
                      occIndex = Number(parts[1]) || 0;
                    }

                    // Find the full question details from the item's eventQuestions if available
                    let questionData = null;
                    if (item.eventQuestions && Array.isArray(item.eventQuestions)) {
                      questionData = item.eventQuestions.find(q => 
                        q.questionId === baseId || q.id === baseId
                      );
                    }

                    if (questionData) {
                      allQuestions.push({
                        id: questionData.questionId || questionData.id || baseId,
                        question: questionData.question || '',
                        value: answerValue,
                        eventId: questionData.eventId || item.eventId || null,
                        configurationId: questionData.configurationId || null,
                        index: occIndex,
                        productId: questionData.productId || null
                      });
                    } else {
                      // Fallback if question data not found
                      allQuestions.push({
                        id: baseId,
                        question: '', // Question text not available
                        value: answerValue,
                        eventId: item.eventId || null,
                        configurationId: null,
                        index: occIndex,
                        productId: null
                      });
                    }
                  }
                });
              }
            });
          }

          // CRITICAL: Use the originalModifiersArray that was cached BEFORE the preload replaced items
          // This ensures we have ALL modifiers even if Venpos consolidated items
          // FIXED: Assign index per-item, not globally, for proper Venpos email display
          const modifierIndexByItem = new Map(); // Track index per webSaleItemId
          const allModifiers = originalModifiersArray.map((mod) => {
            const currentIndex = modifierIndexByItem.get(mod.webSaleItemId) || 0;
            modifierIndexByItem.set(mod.webSaleItemId, currentIndex + 1);
            
            return {
              webSaleItemId: mod.webSaleItemId,
              productModifierId: mod.productModifierId,
              productModifierTextId: mod.productModifierTextId,
              value: mod.value || "1",
              index: currentIndex // Use per-item index instead of global index
            };
          });

          // Build CreateFreeOrder payload matching the EXACT example structure provided
          // NOTE: VennerSys working example (locationId=978) does NOT include discountsFromMembership or customerDetails
          
          // CRITICAL: Use preloadBasketItems if available (has correct quantities from server)
          // Otherwise fall back to items array
          // This fixes the "zero quantity available" error for free bookings
          let itemsForConsolidation = (preloadBasketItems && Array.isArray(preloadBasketItems) && preloadBasketItems.length > 0) 
            ? preloadBasketItems 
            : items;
          
          // ✅ CRITICAL FIX: Merge event IDs from selectedTickets (same as paid orders)
          // This ensures event metadata is preserved even if basket items lost it
          if (bookingDetails.selectedTickets && bookingDetails.selectedTickets.length > 0 && !bookingDetails.isMembershipJourney) {
            const isFlowerGardenShow = pageId === FLOWER_GARDEN_PAGE_ID;
            if (isFlowerGardenShow) {
              console.log('🌸 [CreateFreeOrder] Merging event IDs from selectedTickets before consolidation');
            }
            
            itemsForConsolidation = itemsForConsolidation.map(item => {
              const matchingTicket = bookingDetails.selectedTickets.find(t => 
                String(t.webSaleItemId) === String(item.webSaleItemId) && (t.quantity || 0) > 0
              );
              
              if (matchingTicket) {
                // Merge event IDs from selectedTickets (which have capacity reservation IDs)
                const mergedItem = {
                  ...item,
                  eventId: item.eventId || matchingTicket.eventId || bookingDetails.selectedTimeSlot?.eventId || null,
                  eventCapacityUsageId: item.eventCapacityUsageId || matchingTicket.eventCapacityUsageId || bookingDetails.selectedTimeSlot?.eventCapacityUsageId || null,
                  // Use proper datetime from selectedTimeSlot if available
                  selectedDate: bookingDetails.selectedTimeSlot?.startDateTime || item.selectedDate
                };
                
                if (isFlowerGardenShow && (!item.eventId || !item.eventCapacityUsageId)) {
                  console.log('🌸 [CreateFreeOrder] Merged event IDs for item:', {
                    webSaleItemId: item.webSaleItemId,
                    before: { eventId: item.eventId, eventCapacityUsageId: item.eventCapacityUsageId },
                    after: { eventId: mergedItem.eventId, eventCapacityUsageId: mergedItem.eventCapacityUsageId }
                  });
                }
                
                return mergedItem;
              }
              
              return item;
            });
          }
          
          console.log('📦 [CreateFreeOrder] Items for consolidation', {
            usingPreloadBasketItems: preloadBasketItems && Array.isArray(preloadBasketItems) && preloadBasketItems.length > 0,
            preloadBasketItemsCount: preloadBasketItems?.length || 0,
            originalItemsCount: items.length,
            itemsForConsolidationCount: itemsForConsolidation.length,
            itemsForConsolidation: itemsForConsolidation.map(item => ({
              webSaleItemId: item.webSaleItemId,
              quantity: item.quantity,
              name: item.name,
              hasEventId: !!item.eventId,
              hasEventCapacityUsageId: !!item.eventCapacityUsageId,
              selectedDate: item.selectedDate,
              pageType: item.pageType,
              pageId: item.pageId
            }))
          });
          
          // Validate that items have valid quantities before consolidation
          const itemsWithQuantity = itemsForConsolidation.filter(item => item && (item.quantity || 0) > 0);
          if (itemsWithQuantity.length === 0 && !bookingDetails.isMembershipJourney) {
            console.error('❌ [CreateFreeOrder] No items with valid quantity found for consolidation', {
              itemsForConsolidationCount: itemsForConsolidation.length,
              itemsForConsolidation: itemsForConsolidation.map(item => ({
                webSaleItemId: item.webSaleItemId,
                quantity: item.quantity,
                name: item.name
              })),
              usingPreloadBasketItems: preloadBasketItems && Array.isArray(preloadBasketItems) && preloadBasketItems.length > 0,
              originalItemsCount: items.length
            });
            throw new Error('Unable to create free order: no items with valid quantity found. Please try again or contact support.');
          }
          
          // CRITICAL: For EVENTS, keep individual participants (no consolidation)
          // For non-events, we could consolidate, but events need separate participants in Venpos
          const consolidatedItems = [];
          const itemMap = new Map(); // Key: webSaleItemId|eventId|personIndex -> individual participant

          itemsWithQuantity.forEach((item, idx) => {
            // Use personIndex (from ticket expansion) or index to ensure each person is separate
            const personIndex = item.personIndex !== undefined ? item.personIndex : idx;
            const key = `${item.webSaleItemId}|${item.eventId}|${item.selectedDate}|${personIndex}`;
            
            // DEBUG: Log each item's event assignment for troubleshooting

            // For events, keep each participant separate (no consolidation)
            // For non-events, we could consolidate but keeping simple for now
            // Store participant details separately for eventParticipants building (don't include in items)
            const itemForMap = {
              pageType: item.pageType,
              pageId: item.pageId,
              webSaleItemId: item.webSaleItemId,
              webSaleItemTypeID: item.webSaleItemTypeID,
              quantity: item.quantity, // Should always be 1 for individual participants
              selectedDate: item.selectedDate,
              fixedDonationAmount: item.fixedDonationAmount || 0,
              name: item.name || `Item ${idx + 1}`, // CRITICAL: Ensure name is never undefined
              giftAidEligible: item.giftAidEligible || false,
              isSysGift: item.isSysGift || false,
              isSysMemberCard: item.isSysMemberCard || false,
              reservationExpiryDateTime: item.reservationExpiryDateTime,
              eventCapacityUsageId: item.eventCapacityUsageId || null,
              eventId: item.eventId || null,
              // Include parentWebSaleItemId for upsells (from working example)
              ...(item.parentWebSaleItemId && { parentWebSaleItemId: item.parentWebSaleItemId }),
              // Preserve person tracking for debugging
              personIndex: personIndex
            };
            
            // Store participant details separately for eventParticipants building
            itemForMap._participantDetails = item.participantDetails || null;
            itemForMap._participantAnswers = item.participantAnswers || {};
            
            itemMap.set(key, itemForMap);
            const storedName = item.name || `Item ${idx + 1}`;

          });
          
          // Convert map to array (no consolidation - each person is separate)
          let consolidatedItemsArray = Array.from(itemMap.values());

          // DEBUG: Log individual participants by event for troubleshooting
          const participantsByEvent = {};
          consolidatedItemsArray.forEach(item => {
            const eventKey = item.eventId || 'NO_EVENT';
            if (!participantsByEvent[eventKey]) participantsByEvent[eventKey] = [];
            participantsByEvent[eventKey].push({
              name: item.name,
              quantity: item.quantity,
              webSaleItemId: item.webSaleItemId,
              eventId: item.eventId,
              personIndex: item.personIndex
            });
          });

          Object.entries(participantsByEvent).forEach(([eventId, participants]) => {

          });
          
          // Build eventParticipants array from consolidatedItemsArray (same items that will be in payload)
          // This ensures participant details match the items being sent
          const eventParticipantsMap = new Map(); // Group by eventId

          consolidatedItemsArray.forEach((item, idx) => {
            // Get participant details from stored _participantDetails (not in items payload)
            const participantDetails = item._participantDetails || item.participantDetails;
            const participantAnswers = item._participantAnswers || item.participantAnswers;
            const isArray = Array.isArray(participantDetails);
            const hasParticipantAnswers = participantAnswers && Object.keys(participantAnswers).length > 0;

            // participantDetails is the API response (array of events)
            // participantAnswers is the user input (flat object with keys like "eventId-itemIdx-pIdx")
            if (isArray && participantDetails.length > 0 && hasParticipantAnswers) {
              // Process each event in the participant details API response
              participantDetails.forEach(eventParticipant => {
                const eventId = eventParticipant.eventId;
                
                if (!eventId) {
                  return;
                }
                
                // Get or create event entry in map
                if (!eventParticipantsMap.has(eventId)) {
                  eventParticipantsMap.set(eventId, {
                    eventId: eventParticipant.eventId,
                    description: eventParticipant.description,
                    eventName: eventParticipant.eventName,
                    areParticipantsRequired: eventParticipant.areParticipantsRequired,
                    requiresAllParticipantDetails: eventParticipant.requiresAllParticipantDetails,
                    eventItemDetails: []
                  });
                }
                
                const eventEntry = eventParticipantsMap.get(eventId);
                
                // Process each item in the event
                eventParticipant.eventItemDetails?.forEach((itemDetail, itemIdx) => {
                  const participantDetailsList = [];
                  
                  // Process each participant in the item
                  itemDetail.participantDetails?.forEach((participant, pIdx) => {
                    // Build the key used in participantAnswers: "eventId-itemIdx-pIdx"
                    const answerKey = `${eventId}-${itemIdx}-${pIdx}`;
                    const userAnswers = participantAnswers[answerKey] || {};

                    participantDetailsList.push({
                      webCustomerId: participant.webCustomerId || null,
                      firstName: userAnswers.firstName || participant.firstName || '',
                      lastName: userAnswers.lastName || participant.lastName || '',
                      selectedTitle: userAnswers.selectedTitle || participant.selectedTitle || 1,
                      isLeadParticipant: participant.isLeadParticipant || false,
                      isCustomerDetail: participant.isCustomerDetail || false,
                      isRequired: participant.isRequired || false,
                      errorMessage: participant.errorMessage || [],
                      titleIsMandatory: participant.titleIsMandatory !== undefined ? participant.titleIsMandatory : true
                    });
                  });
                  
                  // Add item details
                  eventEntry.eventItemDetails.push({
                    itemName: itemDetail.itemName,
                    participantDetails: participantDetailsList,
                    participantsCount: itemDetail.participantsCount || participantDetailsList.length
                  });
                });
              });
            }
          });
          
          const eventParticipants = Array.from(eventParticipantsMap.values());
          
          // Clean up items array - remove participant details and internal fields (they're in eventParticipants)
          consolidatedItemsArray = consolidatedItemsArray.map(item => {
            const cleanedItem = {
              pageType: item.pageType,
              pageId: item.pageId,
              webSaleItemId: item.webSaleItemId,
              webSaleItemTypeID: item.webSaleItemTypeID,
              quantity: item.quantity,
              selectedDate: item.selectedDate,
              fixedDonationAmount: item.fixedDonationAmount || 0,
              name: item.name,
              giftAidEligible: item.giftAidEligible || false,
              isSysGift: item.isSysGift || false,
              isSysMemberCard: item.isSysMemberCard || false,
              reservationExpiryDateTime: item.reservationExpiryDateTime,
              eventCapacityUsageId: item.eventCapacityUsageId || null,
              eventId: item.eventId || null,
              ...(item.parentWebSaleItemId && { parentWebSaleItemId: item.parentWebSaleItemId })
            };
            // Remove participant details and internal fields from items (they're in eventParticipants)
            delete cleanedItem.participantDetails;
            delete cleanedItem.participantAnswers;
            delete cleanedItem._participantDetails;
            delete cleanedItem._participantAnswers;
            delete cleanedItem.personIndex; // Internal tracking field, not needed in payload
            return cleanedItem;
          });
          
          // For membership journeys, ensure items only have the exact fields from official example
          if (bookingDetails.isMembershipJourney && consolidatedItemsArray.length > 0) {
            consolidatedItemsArray = consolidatedItemsArray.map(item => ({
              pageType: item.pageType,
              pageId: item.pageId,
              fixedDonationAmount: item.fixedDonationAmount || 0,
              giftAidEligible: item.giftAidEligible || false,
              isSysGift: item.isSysGift || false,
              isSysMemberCard: item.isSysMemberCard || false,
              name: item.name,
              quantity: item.quantity,
              webSaleItemId: item.webSaleItemId,
              webSaleItemTypeID: item.webSaleItemTypeID
            }));
          }
          
          // Build payload matching EXACT official Venpos structure
          // Include eventParticipants if we have participant details (required for events like Santa's Grotto)
          const freeOrderPayload = {
            cateringCustomTextResponse1: '',
            cateringCustomTextResponse2: '',
            cateringFacilityId: null,
            cateringMode: 'NotSetup',
            customerId: customerId || null,
            ddOnCompletionredirectUrl: venposConfirmationUrl,
            discounts: uniqueDiscounts,
            giftAidSelected: false,
            items: consolidatedItemsArray,
            memberships: bookingDetails.memberships || [],
            newAccountCreated: false,
            orderId: actualOrderId,
            pageId: pageId,
            questions: allQuestions,
            webTransHeaderId: actualOrderId,
            // Include eventParticipants if we have participant details
            ...(eventParticipants.length > 0 && { eventParticipants: eventParticipants })
            // ⚠️ NOTE: Official example does NOT include:
            // - modifiers
            // - customerDetails
            // - discountsFromMembership
            // But eventParticipants ARE needed for events that require participant details
          };
          
          // Debug logging - show FULL payload for comparison
          console.log('📋 [CreateFreeOrder] FULL PAYLOAD:', JSON.stringify(freeOrderPayload, null, 2));
          console.log('📋 [CreateFreeOrder] Payload Summary:', {
            itemsCount: freeOrderPayload.items.length,
            items: freeOrderPayload.items.map(item => ({
              webSaleItemId: item.webSaleItemId,
              name: item.name,
              quantity: item.quantity,
              webSaleItemTypeID: item.webSaleItemTypeID,
              hasSelectedDate: !!item.selectedDate,
              selectedDate: item.selectedDate,
              hasEventId: !!item.eventId,
              eventId: item.eventId,
              hasEventCapacityUsageId: !!item.eventCapacityUsageId,
              eventCapacityUsageId: item.eventCapacityUsageId,
              pageType: item.pageType,
              pageId: item.pageId,
              reservationExpiryDateTime: item.reservationExpiryDateTime
            })),
            membershipsCount: freeOrderPayload.memberships.length,
            memberships: freeOrderPayload.memberships.map(m => ({
              membershipTypeId: m.membershipTypeId,
              basketItemId: m.basketItemId,
              memberCount: m.memberCount,
              memberDetailsCount: m.memberDetails?.length || 0,
              firstMember: m.memberDetails?.[0] ? {
                firstName: m.memberDetails[0].firstName,
                lastName: m.memberDetails[0].lastName,
                email: m.memberDetails[0].email,
                mobile: m.memberDetails[0].mobile,
                contactable: m.memberDetails[0].contactable
              } : null
            })),
            discounts: freeOrderPayload.discounts,
            customerId: freeOrderPayload.customerId,
            orderId: freeOrderPayload.orderId,
            webTransHeaderId: freeOrderPayload.webTransHeaderId,
            pageId: freeOrderPayload.pageId,
            questionsCount: freeOrderPayload.questions.length,
            eventParticipantsCount: freeOrderPayload.eventParticipants?.length || 0
          });
          
          console.log('🔍 [CreateFreeOrder] Items Details:', freeOrderPayload.items);

          // Log the exact payload structure for debugging (matching VennerSys example format)


          if (freeOrderPayload.items && freeOrderPayload.items.length > 0) {


          }

          // Call Basket/ValidateBasketItems before CreateFreeOrder (from real example flow)

          try {
            // Try direct call first with proper headers matching browser requests
            const validateItemsUrl = `${VENPOS_API_BASE}/Basket/ValidateBasketItems?locationId=${locationId}&webPageId=${pageId}`;

            const validateItemsPayload = {
              items: items
            };

            let itemValidationResult = await makeApiRequest(validateItemsUrl, {
              method: 'POST',
              headers: {
                'Accept': 'application/json, text/javascript, */*; q=0.01',
                'Accept-Language': 'en-GB,en-US;q=0.9,en;q=0.8',
                'Content-Type': 'application/json',
                'Origin': 'https://online1.venpos.net',
                'Referer': `https://online1.venpos.net/VenPosJourneyUI/Checkout?locationId=${locationId}&mainWebPageId=${pageId}&startId=${pageId}`,
                'User-Agent': 'Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/140.0.0.0 Safari/537.36',
                'X-Requested-With': 'XMLHttpRequest',
                'Cache-Control': 'no-cache',
                'Pragma': 'no-cache',
                'sec-ch-ua': '"Chromium";v="140", "Not=A?Brand";v="24", "Google Chrome";v="140"',
                'sec-ch-ua-mobile': '?0',
                'sec-ch-ua-platform': '"macOS"',
                'sec-fetch-dest': 'empty',
                'sec-fetch-mode': 'cors',
                'sec-fetch-site': 'same-origin',
                'DNT': '1',
                'Connection': 'keep-alive',
                'Upgrade-Insecure-Requests': '1'
              },
              body: JSON.stringify(validateItemsPayload)
            });

            // If direct call fails, try through proxy (like CreateFreeOrder does)
            if (itemValidationResult.isError) {

              const proxyUrl = `${window.location.origin}/api/venpos-finalize.php`;
              itemValidationResult = await fetch(proxyUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include', // Forward browser cookies to proxy
                body: JSON.stringify({
                  action: 'validateBasketItems',
                  locationId: locationId,
                  payload: validateItemsPayload,
                  mainWebPageId: pageId
                })
              }).then(async (r) => {
                if (!r.ok) {
                  const errorText = await r.text();
                  return { isError: true, error: `Proxy HTTP ${r.status}: ${errorText}`, data: null };
                }
                return r.json();
              });
            }

          } catch (vError) {

          }

          // Call basket/GetBasketFinalPrice before CreateFreeOrder (from real example flow)

          const buildFinalPricePayload = (withItems) => {
            // Extract modifiers from items for root-level Modifiers array
            const modifiersForFinalPrice = [];
            const modifierIndexByItemForPrice = new Map(); // Track index per webSaleItemId
            withItems.forEach(item => {
              if (item.modifierSelection) {
                const currentIndex = modifierIndexByItemForPrice.get(item.webSaleItemId) || 0;
                modifierIndexByItemForPrice.set(item.webSaleItemId, currentIndex + 1);
                
                modifiersForFinalPrice.push({
                  webSaleItemId: item.webSaleItemId,
                  productModifierId: item.modifierSelection.modifierId,
                  productModifierTextId: item.modifierSelection.modifierTextId,
                  value: "1",
                  index: currentIndex // Use per-item index instead of global index
                });
              }
            });
            
            // For memberships, exclude date/time/event fields from Items
            const itemsForPrice = bookingDetails.isMembershipJourney
              ? withItems.map(item => ({
                  pageType: item.pageType,
                  pageId: item.pageId,
                  webSaleItemId: item.webSaleItemId,
                  webSaleItemTypeID: item.webSaleItemTypeID,
                  quantity: item.quantity,
                  fixedDonationAmount: item.fixedDonationAmount || 0,
                  name: item.name,
                  giftAidEligible: item.giftAidEligible || false,
                  isSysGift: item.isSysGift || false,
                  isSysMemberCard: item.isSysMemberCard || false,
                  modifiers: []
                }))
              : withItems.map(item => ({
                  pageType: item.pageType,
                  pageId: item.pageId,
                  webSaleItemId: item.webSaleItemId,
                  webSaleItemTypeID: item.webSaleItemTypeID,
                  quantity: item.quantity,
                  selectedDate: item.selectedDate,
                  fixedDonationAmount: item.fixedDonationAmount || 0,
                  name: item.name,
                  giftAidEligible: item.giftAidEligible || false,
                  isSysGift: item.isSysGift || false,
                  isSysMemberCard: item.isSysMemberCard || false,
                  reservationExpiryDateTime: item.reservationExpiryDateTime,
                  eventCapacityUsageId: item.eventCapacityUsageId,
                  eventId: item.eventId,
                  modifiers: []
                }));

            return {
            customerId,
            orderId: actualOrderId, // Use basketId, not webTransHeaderId
            PageId: pageId,
            Discounts: uniqueDiscounts,
            GiftAidSelected: false,
            Items: itemsForPrice,
            Modifiers: modifiersForFinalPrice // ✅ Add root-level Modifiers array (capital M)
            };
          };

          let itemsForFinalPrice = items;
          try {
            let finalPricePayload = buildFinalPricePayload(itemsForFinalPrice);


            // Try direct call first with proper headers matching browser requests
            const finalPriceUrl = `${VENPOS_API_BASE}/basket/GetBasketFinalPrice?locationId=${locationId}`;

            let finalPriceResult = await makeApiRequest(finalPriceUrl, {
              method: 'POST',
              headers: {
                'Accept': 'application/json, text/javascript, */*; q=0.01',
                'Accept-Language': 'en-GB,en-US;q=0.9,en;q=0.8',
                'Content-Type': 'application/json',
                'Origin': 'https://online1.venpos.net',
                'Referer': `https://online1.venpos.net/VenPosJourneyUI/Checkout?locationId=${locationId}&mainWebPageId=${pageId}&startId=${pageId}`,
                'User-Agent': 'Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/140.0.0.0 Safari/537.36',
                'X-Requested-With': 'XMLHttpRequest',
                'Cache-Control': 'no-cache',
                'Pragma': 'no-cache',
                'sec-ch-ua': '"Chromium";v="140", "Not=A?Brand";v="24", "Google Chrome";v="140"',
                'sec-ch-ua-mobile': '?0',
                'sec-ch-ua-platform': '"macOS"',
                'sec-fetch-dest': 'empty',
                'sec-fetch-mode': 'cors',
                'sec-fetch-site': 'same-origin',
                'DNT': '1',
                'Connection': 'keep-alive',
                'Upgrade-Insecure-Requests': '1'
              },
              body: JSON.stringify(finalPricePayload)
            });

            // If direct call fails, try through proxy
            if (finalPriceResult.isError) {

              const proxyUrl = `${window.location.origin}/api/venpos-finalize.php`;
              finalPriceResult = await fetch(proxyUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include', // Forward browser cookies to proxy
                body: JSON.stringify({
                  action: 'getBasketFinalPrice',
                  locationId: locationId,
                  payload: finalPricePayload,
                  mainWebPageId: pageId
                })
              }).then(async (r) => {
                if (!r.ok) {
                  const errorText = await r.text();
                  return { isError: true, error: `Proxy HTTP ${r.status}: ${errorText}`, data: null };
                }
                return r.json();
              });
            }


            // If event became invalid, re-reserve capacity and retry once
            if (finalPriceResult?.data?.basketIsValid === false && /event no longer valid/i.test(finalPriceResult?.data?.basketErrorMessage || '')) {

              try {
                const capacityRetry = await venposService.addEventCapacityUsages(locationId, pageId, itemsForFinalPrice, orderId, uniqueDiscounts);
                if (!capacityRetry.isError && capacityRetry.data) {
                  const retryItems = (capacityRetry.data.items || capacityRetry.data || []).length > 0 ? (capacityRetry.data.items || capacityRetry.data) : itemsForFinalPrice;
                  // Merge new capacity ids into the original items array by webSaleItemId
                  const capById = new Map(retryItems.map(ri => [ri.webSaleItemId, ri]));
                  itemsForFinalPrice = itemsForFinalPrice.map(it => ({
                    ...it,
                    eventCapacityUsageId: capById.get(it.webSaleItemId)?.eventCapacityUsageId || it.eventCapacityUsageId,
                    eventId: capById.get(it.webSaleItemId)?.eventId || it.eventId
                  }));

                  finalPricePayload = buildFinalPricePayload(itemsForFinalPrice);

                  // Retry with updated items
                  finalPriceResult = await makeApiRequest(finalPriceUrl, {
                    method: 'POST',
                    headers: {
                      'Accept': 'application/json, text/javascript, */*; q=0.01',
                      'Accept-Language': 'en-GB,en-US;q=0.9,en;q=0.8',
                      'Content-Type': 'application/json',
                      'Origin': 'https://online1.venpos.net',
                      'Referer': `https://online1.venpos.net/VenPosJourneyUI/Checkout?locationId=${locationId}&mainWebPageId=${pageId}&startId=${pageId}`,
                      'User-Agent': 'Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/140.0.0.0 Safari/537.36',
                      'X-Requested-With': 'XMLHttpRequest',
                      'Cache-Control': 'no-cache',
                      'Pragma': 'no-cache',
                      'sec-ch-ua': '"Chromium";v="140", "Not=A?Brand";v="24", "Google Chrome";v="140"',
                      'sec-ch-ua-mobile': '?0',
                      'sec-ch-ua-platform': '"macOS"',
                      'sec-fetch-dest': 'empty',
                      'sec-fetch-mode': 'cors',
                      'sec-fetch-site': 'same-origin',
                      'DNT': '1',
                      'Connection': 'keep-alive',
                      'Upgrade-Insecure-Requests': '1'
                    },
                    body: JSON.stringify(finalPricePayload)
                  });

                }
              } catch (capErr) {

              }
            }
            
            // Build a map of original reservationExpiryDateTime, modifierSelection, and questionAnswers keyed by item + minute + modifier
            // CRITICAL: Include modifier ID in the key to distinguish items with same webSaleItemId but different modifiers
            const makeExpiryKey = (i) => {
              const baseKey = `${String(i.webSaleItemId)}|${String(i.selectedDate || '').slice(0, 16)}`;
              // If the item has a modifier, append it to the key to make it unique
              if (i.modifierSelection && i.modifierSelection.modifierTextId) {
                return `${baseKey}|${i.modifierSelection.modifierTextId}`;
              }
              return baseKey;
            };
            const originalExpiryByKey = new Map();
            const originalModifiersByKey = new Map();
            const originalQuestionsByKey = new Map();
            try {

              (Array.isArray(items) ? items : []).forEach((it, idx) => {
                if (it && it.webSaleItemId && it.selectedDate) {
                  const key = makeExpiryKey(it);

                  if (it.reservationExpiryDateTime) {
                    originalExpiryByKey.set(key, it.reservationExpiryDateTime);
                  }
                  if (it.modifierSelection) {
                    originalModifiersByKey.set(key, it.modifierSelection);
                  }
                  if (it.questionAnswers) {
                    originalQuestionsByKey.set(key, it.questionAnswers);
                  }
                }
              });
              if (originalExpiryByKey.size > 0) {

              }
              if (originalModifiersByKey.size > 0) {

              }
              if (originalQuestionsByKey.size > 0) {

              }
            } catch (_) {}

            // CRITICAL: Use the exact items from preload basket response for CreateFreeOrder
            // This ensures we use the exact reservationExpiryDateTime format and all fields exactly as the server validated
            if (preloadBasketItems && Array.isArray(preloadBasketItems) && preloadBasketItems.length > 0) {

              items = preloadBasketItems;
              // Merge back reservationExpiryDateTime, modifierSelection, and questionAnswers from original items
              if (originalExpiryByKey.size > 0 || originalModifiersByKey.size > 0 || originalQuestionsByKey.size > 0) {
                let mergedExpiryCount = 0;
                let mergedModifierCount = 0;
                let mergedQuestionCount = 0;
                items = items.map(it => {
                  const key = makeExpiryKey(it);
                  const merged = { ...it };
                  
                  // Restore reservationExpiryDateTime if missing
                  if (!merged.reservationExpiryDateTime && originalExpiryByKey.has(key)) {
                    merged.reservationExpiryDateTime = originalExpiryByKey.get(key);
                    mergedExpiryCount += 1;
                  }
                  
                  // CRITICAL: Restore modifierSelection (always merge back)
                  if (originalModifiersByKey.has(key)) {
                    merged.modifierSelection = originalModifiersByKey.get(key);
                    mergedModifierCount += 1;
                  }
                  
                  // CRITICAL: Restore questionAnswers (always merge back)
                  if (originalQuestionsByKey.has(key)) {
                    merged.questionAnswers = originalQuestionsByKey.get(key);
                    mergedQuestionCount += 1;
                  }
                  
                  return merged;
                });
                if (mergedExpiryCount > 0) {

                }
                if (mergedModifierCount > 0) {

                }
                if (mergedQuestionCount > 0) {

                }
              }

              // CRITICAL: Validate that we have the exact reservationExpiryDateTime format from server
              if (items.length > 0) {
                const firstItem = items[0];


                // Check if the format matches the working example
                if (firstItem.reservationExpiryDateTime && firstItem.reservationExpiryDateTime.includes('+')) {

                } else if (firstItem.reservationExpiryDateTime && firstItem.reservationExpiryDateTime.endsWith('Z')) {

                } else {

                }
              }
            } else if (finalPriceResult?.data?.items && Array.isArray(finalPriceResult.data.items)) {

              items = finalPriceResult.data.items;

            }
          } catch (pError) {

          }

          // Validate CreateFreeOrder payload against VennerSys working example structure (locationId=978)

          const requiredFields = [
            'items', 'discounts', 'giftAidSelected', 'pageId', 'modifiers', 'memberships',
            'customerId', 'questions', 'webTransHeaderId', 'eventParticipants', 'cateringMode', 'cateringFacilityId',
            'ddOnCompletionredirectUrl', 'cateringCustomTextResponse1', 'cateringCustomTextResponse2',
            'orderId', 'newAccountCreated'
            // NOTE: VennerSys example does NOT include 'discountsFromMembership' or 'customerDetails'
          ];
          
          const missingFields = requiredFields.filter(field => !(field in freeOrderPayload));
          const extraFields = Object.keys(freeOrderPayload).filter(field => !requiredFields.includes(field));
          
          if (missingFields.length > 0) {

          }
          if (extraFields.length > 0) {

          }
          if (missingFields.length === 0 && extraFields.length === 0) {

          }
          
          // Validate items structure (skip for membership journeys - they don't need event metadata)
          if (!bookingDetails.isMembershipJourney && freeOrderPayload.items && freeOrderPayload.items.length > 0) {
            const requiredItemFields = [
              'pageType', 'pageId', 'webSaleItemId', 'webSaleItemTypeID', 'quantity',
              'selectedDate', 'fixedDonationAmount', 'name', 'giftAidEligible',
              'isSysGift', 'isSysMemberCard', 'reservationExpiryDateTime',
              'eventCapacityUsageId', 'eventId'
            ];
            
            freeOrderPayload.items.forEach((item, index) => {
              const missingItemFields = requiredItemFields.filter(field => !(field in item));
              if (missingItemFields.length > 0) {

              }
              
              // Flag specific critical missing values
              if (!item.eventCapacityUsageId) {

              }
              if (!item.eventId) {

              }
            });
          } else if (bookingDetails.isMembershipJourney) {
            // For membership journeys, items only need basic fields (no date/time/event)
            console.log('✅ [CreateFreeOrder] Skipping item validation for membership journey - items may be empty or minimal');
          }


          if (freeOrderPayload.items) {
            freeOrderPayload.items.forEach((item, index) => {

            });
          }


          // Use the correct basket/CreateFreeOrder endpoint with proper headers


          // Ensure all required fields are present and properly formatted


          // Use makeApiRequest directly (same as regular booking journey)
          const createFreeOrderUrl = `${VENPOS_API_BASE}/basket/CreateFreeOrder?locationId=${locationId}`;
          
          console.log('🚀 [CreateFreeOrder] About to make API call', {
            url: createFreeOrderUrl,
            payloadSize: JSON.stringify(freeOrderPayload).length,
            itemsCount: freeOrderPayload.items.length,
            membershipsCount: freeOrderPayload.memberships?.length || 0
          });

          console.log('📋 [CreateFreeOrder] FULL PAYLOAD:', JSON.stringify(freeOrderPayload, null, 2));

          const freeOrderResult = await makeApiRequest(createFreeOrderUrl, {
            method: 'POST',
            headers: {
              'Accept': 'application/json, text/javascript, */*; q=0.01',
              'Accept-Language': 'en-GB,en-US;q=0.9,en;q=0.8',
              'Content-Type': 'application/json',
              'Origin': 'https://online1.venpos.net',
              'Referer': `https://online1.venpos.net/VenPosJourneyUI/${bookingDetails.isMembershipJourney ? 'Membership' : 'Checkout'}?locationId=${locationId}&mainWebPageId=${pageId}&startId=${pageId}`,
              'X-Requested-With': 'XMLHttpRequest'
            },
            body: JSON.stringify(freeOrderPayload)
          });
          
          console.log('📡 [CreateFreeOrder] API call result:', freeOrderResult.isError ? 'Failed' : 'Success');

          console.log('📡 [CreateFreeOrder] API call completed', {
            isError: freeOrderResult.isError,
            hasData: !!freeOrderResult.data,
            response: freeOrderResult
          });
          
          if (freeOrderResult.isError) {
            console.error('❌ [CreateFreeOrder] Failed:', freeOrderResult.error);
            throw new Error(`CreateFreeOrder failed: ${freeOrderResult.error}`);
          } else {
            const resp = freeOrderResult.data || freeOrderResult;
          const hasOrderId = !!(resp?.orderId || resp?.OrderId);
          const hasWebTransHeaderId = !!(resp?.webTransHeaderId || resp?.WebTransHeaderId);
          const basketIsValid = resp?.basketIsValid;
          const basketErrorMessage = resp?.basketErrorMessage;

          console.log('📦 [CreateFreeOrder] Response received:', {
            hasOrderId,
            hasWebTransHeaderId,
            basketIsValid,
            basketErrorMessage
          });
          
          // Check if CreateFreeOrder actually succeeded
          if (basketIsValid === false) {
            const msg = (basketErrorMessage || '').toLowerCase();
            const isZeroQty = msg.includes('zero quantity available');
            const isQtyAmended = msg.includes('quantity has been amended');
            const isEventInvalid = msg.includes('event no longer valid');
            const isFlowerFree = pageId === FLOWER_GARDEN_PAGE_ID && isFreeBooking && !bookingDetails.isPaidBooking;

            // If event is no longer valid, try to re-reserve capacity and retry
            if (isEventInvalid && !hasOrderId) {
              console.warn('⚠️ [CreateFreeOrder] Event capacity expired, attempting to re-reserve capacity...');
              
              try {
                // Re-reserve capacity for all items from the payload
                const itemsToReReserve = freeOrderPayload.items.map(item => ({
                  ...item,
                  reservationExpiryDateTime: new Date(Date.now() + 30 * 60 * 1000).toISOString(),
                  // Clear the expired capacity ID so it gets a new one
                  eventCapacityUsageId: null
                }));

                // Group items by pageId (main tickets vs upsells)
                const itemsByPage = {};
                itemsToReReserve.forEach(item => {
                  const itemPageId = item.pageId || pageId;
                  if (!itemsByPage[itemPageId]) {
                    itemsByPage[itemPageId] = [];
                  }
                  itemsByPage[itemPageId].push(item);
                });

                // Re-reserve capacity for each page
                const reReservedItems = [];
                for (const [itemPageId, pageItems] of Object.entries(itemsByPage)) {
                  const capacityResponse = await this.addEventCapacityUsages(
                    locationId,
                    itemPageId,
                    pageItems,
                    actualOrderId,
                    uniqueDiscounts
                  );

                  if (!capacityResponse.isError && capacityResponse.data) {
                    const responseItems = capacityResponse.data.items || capacityResponse.data || [];
                    reReservedItems.push(...responseItems);
                    console.log('✅ [CreateFreeOrder] Re-reserved capacity for page:', itemPageId, {
                      itemsCount: responseItems.length
                    });
                  } else {
                    console.error('❌ [CreateFreeOrder] Failed to re-reserve capacity:', capacityResponse.error);
                    // Still add original items as fallback
                    reReservedItems.push(...pageItems);
                  }
                }

                // Update freeOrderPayload.items with new capacity IDs
                const capacityMap = new Map(reReservedItems.map(ri => [ri.webSaleItemId, ri]));
                freeOrderPayload.items = freeOrderPayload.items.map(item => {
                  const reReserved = capacityMap.get(item.webSaleItemId);
                  if (reReserved && reReserved.eventCapacityUsageId) {
                    return {
                      ...item,
                      eventId: reReserved.eventId || item.eventId,
                      eventCapacityUsageId: reReserved.eventCapacityUsageId,
                      reservationExpiryDateTime: new Date(Date.now() + 30 * 60 * 1000).toISOString()
                    };
                  }
                  return item;
                });
                
                console.log('🔄 [CreateFreeOrder] Retrying CreateFreeOrder with refreshed capacity...');
                
                // Retry CreateFreeOrder with refreshed capacity
                const retryResponse = await makeApiRequest(createFreeOrderUrl, {
                  method: 'POST',
                  headers: {
                    'Accept': 'application/json, text/javascript, */*; q=0.01',
                    'Accept-Language': 'en-GB,en-US;q=0.9,en;q=0.8',
                    'Content-Type': 'application/json',
                    'Origin': 'https://online1.venpos.net',
                    'Referer': `https://online1.venpos.net/VenPosJourneyUI/${bookingDetails.isMembershipJourney ? 'Membership' : 'Checkout'}?locationId=${locationId}&mainWebPageId=${pageId}&startId=${pageId}`,
                    'X-Requested-With': 'XMLHttpRequest'
                  },
                  body: JSON.stringify(freeOrderPayload)
                });

                if (retryResponse.isError) {
                  throw new Error(`CreateFreeOrder retry failed: ${retryResponse.error || 'Unknown error'}`);
                }

                const retryResp = retryResponse.data || retryResponse;
                const retryBasketIsValid = retryResp.basketIsValid !== false;
                const retryBasketErrorMessage = retryResp.basketErrorMessage || retryResp.basketError || '';

                if (retryBasketIsValid === false && !retryResp.orderId && !retryResp.OrderId) {
                  console.error('❌ [CreateFreeOrder] Retry validation failed:', retryBasketErrorMessage);
                  throw new Error(`CreateFreeOrder validation failed after retry: ${retryBasketErrorMessage || 'Unknown error'}`);
                }

                // Use retry response
                resp = retryResp;
                basketIsValid = retryBasketIsValid;
                basketErrorMessage = retryBasketErrorMessage;
                hasOrderId = !!(retryResp.orderId || retryResp.OrderId);
                
                console.log('✅ [CreateFreeOrder] Retry successful with refreshed capacity');
                
              } catch (retryError) {
                console.error('❌ [CreateFreeOrder] Failed to re-reserve capacity and retry:', retryError);
                // Fall through to original error handling
              }
            }

            // Treat certain errors as soft-warnings when we already have an orderId (common with free bookings)
            if (hasOrderId && (isZeroQty || (isFlowerFree && (isQtyAmended || isEventInvalid)))) {
              console.warn('⚠️ [CreateFreeOrder] Basket reported invalid state but orderId is present. Continuing as success.', {
                orderId: resp?.orderId || actualOrderId,
                basketErrorMessage
              });
              // fall through to success handling below
            } else if (basketIsValid === false && !hasOrderId) {
              console.error('❌ [CreateFreeOrder] Validation failed:', basketErrorMessage);
              throw new Error(`CreateFreeOrder validation failed: ${basketErrorMessage || 'Unknown error'}`);
            }
          }

          {
              console.log('✅ [CreateFreeOrder] Success!', {
                orderId: resp?.orderId || actualOrderId,
                webTransHeaderId: resp?.webTransHeaderId || resp?.WebTransHeaderId,
                isMembershipJourney: bookingDetails.isMembershipJourney,
                membershipsCount: bookingDetails.memberships?.length || 0
              });
              
              freeOrderCreated = true; // ✅ Mark free order as created
              
              // For free orders, we can proceed to confirmation
              const orderId = resp?.orderId || actualOrderId;

              // Do not redirect to vendor confirmation page; fetch header and return control to caller
              let headerData = null;
              try {
                const headerUrl = `${VENPOS_API_BASE}/WebTransHeader/Get?locationId=${locationId}&id=${orderId}`;
                const headerResp = await makeApiRequest(headerUrl, { method: 'GET' });
                if (!headerResp.isError) {
                  headerData = headerResp.data || headerResp;
                  // Store transaction details for membership journeys
                  if (bookingDetails.isMembershipJourney) {
                    transactionDetails = {
                      webTransHeaderID: orderId,
                      transRef: headerData?.transRef || headerData?.transactionReference || headerData?.transactionSummaryModel?.transactionReference || null,
                      orderId: orderId,
                      netValue: 0,
                      discount: 0
                    };
                    console.log('✅ [CreateFreeOrder] Stored transaction details for membership:', transactionDetails.transRef);
                  }
                }
              } catch (_) {}

              // Ensure transaction details are populated for non-membership free bookings too
              if (!transactionDetails) {
                transactionDetails = {
                  webTransHeaderID: resp?.webTransHeaderId || resp?.WebTransHeaderId || orderId,
                  transRef: headerData?.transRef ||
                            headerData?.transactionReference ||
                            headerData?.transactionSummaryModel?.transactionReference ||
                            resp?.orderId || resp?.OrderId || orderId,
                  orderId: orderId,
                  netValue: 0,
                  discount: 0
                };
              }

              // Best-effort: trigger Venpos confirmation endpoints to send emails (Flower & Garden Show free flow)
              if (pageId === FLOWER_GARDEN_PAGE_ID && isFreeBooking && !bookingDetails.isPaidBooking) {
                const transRef = transactionDetails?.transRef;
                const webHeader = transactionDetails?.webTransHeaderID || resp?.webTransHeaderId || resp?.WebTransHeaderId;
                const identifiers = [transRef, orderId, webHeader].filter(Boolean);
                const uniqueIds = Array.from(new Set(identifiers));

                const triggerUrls = [];
                uniqueIds.forEach(id => {
                  triggerUrls.push(`${VENPOS_API_BASE}/Booking/Confirmation?bookingReference=${encodeURIComponent(id)}&locationId=${locationId}`);
                  triggerUrls.push(`${VENPOS_API_BASE}/Checkout/Confirmation?locationId=${locationId}&orderId=${encodeURIComponent(id)}&paymentToken=&paymentType=0`);
                });

                for (const url of triggerUrls) {
                  try {
                    await fetch(url, {
                      method: 'GET',
                      headers: {
                        'Accept': 'application/json, text/javascript, */*; q=0.01',
                        'X-Requested-With': 'XMLHttpRequest'
                      },
                      mode: 'cors',
                      credentials: 'omit'
                    });
                  } catch (_) {
                    // ignore – email trigger is best-effort
                  }
                }
              }

              // LOG FREE ORDER DETAILS (especially discount codes)
              try {
                const logPayload = {
                  basketId,
                  orderId: orderId,
                  amount: 0,
                  currency: 'GBP',
                  customerDetails: bookingDetails.customerDetails,
                  appliedDiscounts: bookingDetails.appliedDiscounts || [],
                  selectedTickets: bookingDetails.selectedTickets || [],
                  selectedDate: bookingDetails.selectedDate || null,
                  pageId: bookingDetails.pageId || null,
                  locationId: locationId,
                  paymentMethod: 'Free',
                  timestamp: new Date().toISOString()
                };

                // Send to logging API
                const logUrl = process.env.NODE_ENV === 'development' 
                  ? 'http://localhost:8888/Website/front-end/public/api/log-order.php'
                  : `${window.location.origin}/api/log-order.php`;
                
                fetch(logUrl, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify(logPayload)
                }).catch(err => console.error('Free order logging failed:', err));
              } catch (logError) {
                console.error('Failed to log free order:', logError);
              }

              // For membership journeys, don't return early - let the flow continue to set transactionDetails properly
              if (!bookingDetails.isMembershipJourney) {
                return { 
                  isError: false, 
                  data: { 
                    redirected: false, 
                    url: `${window.location.origin}/booking-journey`,
                    webTransHeaderID: orderId,
                    bookingReference: headerData?.transRef || headerData?.transactionSummaryModel?.transactionReference || null,
                    confirmationText: headerData?.confirmationText || null,
                    bookingComplete: true,
                    redirectDelay: 0
                  } 
                };
              }
              // For membership journeys, continue to set headerIdFromCreate and let the flow continue
            }

            // Try to extract WebTransHeaderId from the response for subsequent fetch
            const responseHeaderId = resp?.webTransHeaderId || resp?.WebTransHeaderId || resp?.webTransHeaderID || resp?.WebTransHeaderID || null;
            const responseOrderId = resp?.orderId || resp?.OrderId || null;

            if (isNonZeroGuid(responseHeaderId)) {

              headerIdFromCreate = responseHeaderId;
            } else if (isNonZeroGuid(responseOrderId)) {
              // Some responses return orderId; use it if it looks valid

              headerIdFromCreate = responseOrderId;
            } else {
              
            }

            // Debug: Log the full CreateFreeOrder response to see what fields are available

            // Check if CreateFreeOrder response already contains transaction details
            // Look for various possible field names that might contain the transaction reference
            const possibleTransRef = resp?.transRef || resp?.transactionReference || resp?.TransRef || resp?.TransactionReference || resp?.bookingReference || resp?.BookingReference;
            const possibleSummary = resp?.transactionSummaryModel || resp?.TransactionSummaryModel || resp?.summary || resp?.Summary;
            
            if (possibleTransRef || possibleSummary || resp?.webTransHeaderID || resp?.WebTransHeaderID) {

              transactionDetails = {
                webTransHeaderID: responseHeaderId || responseOrderId || originalBasketId,
                transRef: possibleTransRef,
                netValue: resp?.netValue || resp?.NetValue || 0,
                discount: resp?.discount || resp?.Discount || 0,
                visitDate: resp?.visitDate || resp?.VisitDate,
                confirmationText: resp?.confirmationText || resp?.ConfirmationText,
                transactionSummaryModel: possibleSummary
              };

            } else {
              console.log('⚠️ [CreateFreeOrder] No transaction reference in response, will fetch from WebTransHeader');
            }
          }
        } catch (err) {
          console.error('❌ [CreateFreeOrder] Error in CreateFreeOrder flow:', err);
          console.error('❌ [CreateFreeOrder] Error stack:', err.stack);
          // Re-throw the error so it's caught by the outer catch block
          throw err;
        }
      } else if (bookingDetails.isPaidBooking) {


        // For paid bookings, the order was already created by POST /Payment
        // Payment was already completed by Payment Sense
        // We just need to verify the transaction exists
        headerIdFromCreate = bookingDetails.webTransHeaderId || bookingDetails.confirmedHeaderId || basketId;
        transactionId = headerIdFromCreate;


      }

      // For membership journeys, after CreateFreeOrder succeeds, skip payment completion and confirmation endpoints
      // The order is already created via CreateFreeOrder with the memberships array
      if (bookingDetails.isMembershipJourney && freeOrderCreated) {
        // Order was created via CreateFreeOrder with memberships - skip confirmation endpoints
        // Use the order ID from CreateFreeOrder response
        confirmedHeaderId = headerIdFromCreate || basketId;
        transactionId = headerIdFromCreate || basketId;
        
        // If transactionDetails wasn't set from CreateFreeOrder response, try to fetch it
        if (!transactionDetails || !transactionDetails.transRef) {
          // Try to fetch the booking reference from WebTransHeader
          try {
            const headerUrl = `${VENPOS_API_BASE}/WebTransHeader/Get?locationId=${locationId}&id=${headerIdFromCreate || basketId}`;
            const headerResp = await makeApiRequest(headerUrl, { method: 'GET' });
            if (!headerResp.isError && headerResp.data) {
              const headerData = headerResp.data;
              transactionDetails = {
                webTransHeaderID: headerData?.webTransHeaderID || headerData?.WebTransHeaderID || headerIdFromCreate || basketId,
                transRef: headerData?.transRef || headerData?.transactionReference || headerData?.TransRef || null,
                orderId: headerIdFromCreate || basketId
              };
              console.log('✅ [Membership] Fetched booking reference:', transactionDetails.transRef);
            } else {
              // Fallback: use order ID as reference
              transactionDetails = { 
                webTransHeaderID: headerIdFromCreate || basketId, 
                transRef: headerIdFromCreate || basketId,
                orderId: headerIdFromCreate || basketId
              };
              console.warn('⚠️ [Membership] Could not fetch booking reference, using order ID');
            }
          } catch (fetchError) {
            console.error('❌ [Membership] Error fetching booking reference:', fetchError);
            // Fallback: use order ID as reference
            transactionDetails = { 
              webTransHeaderID: headerIdFromCreate || basketId, 
              transRef: headerIdFromCreate || basketId,
              orderId: headerIdFromCreate || basketId
            };
          }
        } else {
          console.log('✅ [Membership] Using transaction details from CreateFreeOrder:', transactionDetails.transRef);
        }
      }
      
      if (!bookingDetails.isMembershipJourney) {
        // Skip Payment/Complete step if free order already created OR if this is a paid booking
        if (!freeOrderCreated && !bookingDetails.isPaidBooking) {
        // For any booking (free or paid), try to finalize the transaction if it hasn't been done yet

        try {
          const finalizePayload = {
            basketId,
            locationId,
            paymentToken: bookingDetails.paymentToken || 'FREE_BOOKING',
            paymentSessionId: bookingDetails.paymentToken || 'FREE_BOOKING',
            transactionId: bookingDetails.transactionId || `TXN_${Date.now()}`,
            amount: bookingDetails.totalAmount || 0,
            currency: 'GBP',
            paymentMethod: bookingDetails.totalAmount === 0 ? 'FreeBooking' : 'PaymentSense',
            status: 'Success',
            customerDetails: bookingDetails.customerDetails
          };

          const finalizeResult = await makeApiRequest(
            `${VENPOS_API_BASE}/Payment/Complete`,
            {
              method: 'POST',
              body: JSON.stringify(finalizePayload)
            }
          );

          if (!finalizeResult.isError) {

          } else {

          }
        } catch (finalizeError) {

          // Continue with transaction fetch
          }
        } else if (freeOrderCreated) {

        } else if (bookingDetails.isPaidBooking) {

        }

        // Confirm payment per Journey doc to obtain WebTransHeaderId, then fetch header
        const orderId = bookingDetails.orderId || basketId;
        if (!freeOrderCreated && !bookingDetails.isPaidBooking) {

        try {
          let paymentToken = bookingDetails.paymentToken;
          if (!paymentToken) {
            const status = await this.getPaymentStatus(locationId, orderId);
            if (!status.isError) {
              paymentToken = status.data?.paymentToken;
            }
          }
          const confirmation = await this.confirmPaymentJourney(locationId, orderId, paymentToken, 0);
          if (!confirmation.isError) {
            confirmedHeaderId = confirmation.data?.webTransHeaderId || null;
            if (confirmedHeaderId) {

            }
          } else {
            
          }
        } catch (e) {

          }
        } else if (freeOrderCreated) {
          
        } else if (bookingDetails.isPaidBooking) {

          confirmedHeaderId = bookingDetails.webTransHeaderId || bookingDetails.confirmedHeaderId || basketId;

        }
      }

      // Define pollWebTransHeader function for use by both free and paid bookings
      const pollWebTransHeader = async (locId, primaryId, maxAttempts = 12, delayMs = 600, secondaryId = null) => {
        for (let attempt = 1; attempt <= maxAttempts; attempt++) {
          try {
            const idToUse = attempt <= Math.ceil(maxAttempts / 2) ? primaryId : (secondaryId || primaryId);
            const res = await makeApiRequest(
              `${VENPOS_API_BASE}/WebTransHeader/Get?locationId=${locId}&id=${idToUse}`,
              { method: 'GET' }
            );
            if (!res.isError) {
              const d = res.data;
              const headerId = d?.webTransHeaderID || d?.WebTransHeaderID;
              if (headerId && headerId !== '00000000-0000-0000-0000-000000000000' && (d?.transRef || d?.transactionReference)) {
                return d;
              }
            }
          } catch (_) {
            // ignore and retry
          }
          await new Promise(r => setTimeout(r, delayMs));
        }
        return null;
      };

      // For membership journeys, handle both free and paid orders
      if (bookingDetails.isMembershipJourney) {
        if (freeOrderCreated) {
          // Free membership order - already handled above
        } else if (bookingDetails.isPaidBooking) {
          // Paid membership order - order was created by POST /Payment
          // Fetch transaction details from WebTransHeader
          transactionId = bookingDetails.webTransHeaderId || bookingDetails.confirmedHeaderId || basketId;
          
          // Try to fetch the booking reference
          if (!transactionDetails || !transactionDetails.transRef) {
            try {
              const headerUrl = `${VENPOS_API_BASE}/WebTransHeader/Get?locationId=${locationId}&id=${transactionId}`;
              const headerResp = await makeApiRequest(headerUrl, { method: 'GET' });
              if (!headerResp.isError && headerResp.data) {
                const headerData = headerResp.data;
                transactionDetails = {
                  webTransHeaderID: headerData?.webTransHeaderID || headerData?.WebTransHeaderID || transactionId,
                  transRef: headerData?.transRef || headerData?.transactionReference || headerData?.TransRef || null,
                  orderId: transactionId
                };
                console.log('✅ [Paid Membership] Fetched booking reference:', transactionDetails.transRef);
              } else {
                // Fallback: use order ID as reference
                transactionDetails = { 
                  webTransHeaderID: transactionId, 
                  transRef: transactionId,
                  orderId: transactionId
                };
                console.warn('⚠️ [Paid Membership] Could not fetch booking reference, using order ID');
              }
            } catch (fetchError) {
              console.error('❌ [Paid Membership] Error fetching booking reference:', fetchError);
              transactionDetails = { 
                webTransHeaderID: transactionId, 
                transRef: transactionId,
                orderId: transactionId
              };
            }
          }
        } else {
          // Membership journey but no order created yet - use basket ID as fallback
          console.warn('⚠️ [Membership] No order created, using basket ID as fallback');
          transactionId = basketId;
          transactionDetails = { webTransHeaderID: basketId, transRef: basketId };
        }
      } else if (!bookingDetails.isMembershipJourney && !transactionDetails && !bookingDetails.isPaidBooking) {
        // Use confirmedHeaderId if provided, otherwise fall back to other methods
        transactionId = bookingDetails.confirmedHeaderId ||
                       bookingDetails.webTransHeaderId ||
                       confirmedHeaderId ||
                       headerIdFromCreate ||
                       originalBasketId;

      // Get transaction details using WebTransHeader/Get (function now defined above)
        try {
          const alternateId = (transactionId === headerIdFromCreate && isNonZeroGuid(originalBasketId)) ? originalBasketId : (isNonZeroGuid(headerIdFromCreate) ? headerIdFromCreate : null);
          transactionDetails = await pollWebTransHeader(locationId, transactionId, 25, 1000, alternateId);
          if (transactionDetails) {

        } else {

        }
      } catch (transError) {

        }
      } else if (transactionDetails) {

      } else if (bookingDetails.isPaidBooking) {

        // Set transaction ID for paid bookings
        transactionId = confirmedHeaderId || bookingDetails.webTransHeaderId || basketId;

        // Check if we already have the booking reference from completePayment
        // Support both existingBookingReference and bookingReference field names
        const existingRef = bookingDetails.existingBookingReference || bookingDetails.bookingReference;
        console.log('🔍 [Paid Membership] Checking for existing booking reference');
        console.log('🔍 [Paid Membership] existingBookingReference:', bookingDetails.existingBookingReference);
        console.log('🔍 [Paid Membership] bookingReference:', bookingDetails.bookingReference);
        console.log('🔍 [Paid Membership] existingRef:', existingRef);
        
        if (existingRef) {
          console.log('✅ [Paid Membership] Using existing booking reference from completePayment:', existingRef);
          transactionDetails = {
            webTransHeaderID: transactionId,
            transRef: existingRef,
            netValue: bookingDetails.totalAmount,
            discount: 0,
            visitDate: bookingDetails.selectedDate,
            confirmationText: 'Thank you for your booking. Payment successful.',
            paymentComplete: true,
            orderId: transactionId
          };
        } else {
          console.log('⚠️ [Paid Membership] No existing booking reference, will fetch from WebTransHeader');
          // No existing reference, fetch it from Venpos (but use shorter timeout for paid orders)

          try {
            const alternateId = null; // No alternate ID for paid orders
            // Poll for paid orders: payment already completed, just waiting for Venpos to commit transaction to DB
            // Use same timeout as free orders since both are just database writes
            transactionDetails = await pollWebTransHeader(locationId, transactionId, 25, 1000, alternateId);
            
            if (transactionDetails) {

            } else {

              // Don't use PAY- fallback here, just leave transactionDetails as null
              // confirmBooking will handle the polling with a longer timeout
              transactionDetails = null;
            }
          } catch (transError) {

            // Don't throw - let confirmBooking handle it with more polling
            transactionDetails = null;
          }
        }

      }

      // Ensure transactionId is set
      if (!transactionId) {
        transactionId = confirmedHeaderId || bookingDetails.webTransHeaderId || basketId;

      }

      // Generate booking reference based on VenPos response or fallback
      // For membership journeys, use basket ID if transactionDetails doesn't have a reference
      let bookingReference;
      if (bookingDetails.isMembershipJourney) {
        bookingReference = transactionDetails?.transRef || 
                          transactionDetails?.transactionReference || 
                          basketId || 
                          `MEM${Date.now()}`;
      } else {
        bookingReference = transactionDetails?.transRef || 
                          transactionDetails?.transactionReference || 
                          `VEN${Date.now()}`;
      }

      const confirmationNumber = transactionDetails?.transRef || 
                                transactionDetails?.transactionReference || 
                                bookingReference;

      return {
        isError: false,
        data: {
          bookingReference,
          confirmationNumber,
          success: true,
          bookingDate: bookingDetails.selectedDate,
          totalAmount: bookingDetails.totalAmount,
          transactionId: transactionId,
          transactionDetails: transactionDetails,
          confirmationText: transactionDetails?.confirmationText,
          paymentMethod: bookingDetails.totalAmount === 0 ? 'FreeBooking' : 'PaymentSense',
          // Include VenPos specific data
          webTransHeaderID: transactionDetails?.webTransHeaderID,
          transRef: transactionDetails?.transRef,
          netValue: transactionDetails?.netValue,
          discount: transactionDetails?.discount,
          visitDate: transactionDetails?.visitDate
        }
      };

    } catch (error) {
      console.error('❌ [confirmBooking] Error caught:', error);
      console.error('❌ [confirmBooking] Error stack:', error.stack);
      console.error('❌ [confirmBooking] Booking details:', {
        isMembershipJourney: bookingDetails.isMembershipJourney,
        totalAmount: bookingDetails.totalAmount,
        isPaidBooking: bookingDetails.isPaidBooking
      });

      // ✅ CRITICAL: Re-throw BookingMetadataMissingError so it can be handled by BookingJourney
      // This error requires special handling (system status update + email notification)
      if (error.name === 'BookingMetadataMissingError' || error.requiresSystemStatusUpdate) {
        console.error('🚨 [confirmBooking] Re-throwing BookingMetadataMissingError for special handling');
        throw error; // Re-throw to BookingJourney error handler
      }

      // ❌ REMOVED: No more fallback responses - all errors should be thrown
      // This ensures the error is properly caught and handled by BookingJourney
      // The user should see an error message, not a fake confirmation
      console.error('🚨 [confirmBooking] Booking failed - throwing error instead of fallback');
      throw error;
    }
  },

  // Attempt to apply a discount/promo/voucher code to the basket in Venpos
  // NOTE: This function is deprecated. The Basket/ApplyVoucher and Basket/ApplyPromotion endpoints
  // have been replaced. Discounts should now be applied via Basket/Load by including them in the discounts array.
  // Use loadBasket with discounts array instead: await venposService.loadBasket(locationId, { ...payload, discounts: [code] }, null);
  async applyDiscountCode(locationId, basketId, code) {
    try {
      if (!code) {
        return { isError: false, data: { applied: false, reason: 'NO_CODE' } };
      }

      console.warn('[DEPRECATED] applyDiscountCode is deprecated. Use loadBasket with discounts array instead.');

      // Try Basket/ApplyVoucher first (deprecated endpoint - may return 404)
      const tryEndpoints = [
        {
          url: `${VENPOS_API_BASE}/Basket/ApplyVoucher?locationId=${encodeURIComponent(locationId)}&basketId=${encodeURIComponent(basketId)}`,
          body: { code }
        },
        {
          url: `${VENPOS_API_BASE}/Basket/ApplyPromotion?locationId=${encodeURIComponent(locationId)}&basketId=${encodeURIComponent(basketId)}`,
          body: { promotionCode: code }
        }
      ];

      for (const ep of tryEndpoints) {
        try {
          const res = await fetch(ep.url, {
            method: 'POST',
            headers: {
              'Accept': 'application/json, text/javascript, */*; q=0.01',
              'Content-Type': 'application/json',
              'X-Requested-With': 'XMLHttpRequest'
            },
            body: JSON.stringify(ep.body)
          });
          if (res.ok) {
            const data = await res.json();

            return { isError: false, data: { applied: true, raw: data } };
          }
        } catch (e) {
          // continue to next endpoint
        }
      }

      return { isError: true, data: { applied: false }, error: 'APPLY_ENDPOINTS_FAILED - Endpoints deprecated. Use Basket/Load with discounts array instead.' };
    } catch (error) {

      return { isError: true, data: { applied: false }, error: String(error) };
    }
  },

  async getBookingConfirmation(bookingReference, locationId) {
    try {

      const response = await fetch(
        `${VENPOS_API_BASE}/Booking/Confirmation?bookingReference=${bookingReference}&locationId=${locationId}`,
        {
          method: 'GET',
          headers: {
            'Accept': 'application/json, text/javascript, */*; q=0.01',
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch booking confirmation');
      }

      const data = await response.json();

      return data;
    } catch (error) {

      throw error;
    }
  },

  async getCustomerRegistrationSettings(locationId) {
    try {
      const url = `${VENPOS_API_BASE}/Customer/CustomerRegistrationSettings?locationId=${locationId}`;
      const response = await makeApiRequest(url, { method: 'GET' });
      
      if (response.isError) {
        return response;
      }

      return {
        isError: false,
        data: response?.data || response
      };
    } catch (error) {
      return {
        isError: true,
        error: error?.message || 'Failed to load registration settings'
      };
    }
  },

  // Customer validation through basket flow
  async validateCustomerThroughBasket(locationId, customerDetails, basketItems) {
    try {
      // Filter out items with quantity 0 and ensure consistent date format
      const validItems = basketItems
        .filter(item => item.quantity > 0)
        .map(item => {
          let normalizedDate = null;
          if (item.selectedDate) {
            if (typeof item.selectedDate === 'string') {
              normalizedDate = item.selectedDate.split('T')[0];
            } else {
              const d = new Date(item.selectedDate);
              normalizedDate = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
            }
          }
          return {
            ...item,
            selectedDate: normalizedDate
          };
        });

      if (validItems.length === 0) {
        return {
          isError: true,
          data: null,
          error: 'No valid items for validation'
        };
      }

      // Create customer object for basket
      const venposCustomer = {
        firstName: customerDetails.firstName,
        lastName: customerDetails.lastName, 
        email: customerDetails.email,
        mobile: customerDetails.phone || customerDetails.mobile,
        addressFirstLine: customerDetails.address?.line1 || '',
        addressSecondLine: customerDetails.address?.line2 || '',
        cityTown: customerDetails.address?.city || '',
        postCode: customerDetails.address?.postcode || '',
        selectedCountry: customerDetails.address?.country || 'UK'
      };

      // Create basket with customer details
      const basketPayload = {
        pageId: validItems[0]?.pageId,
        items: validItems,
        giftAidSelected: false,
        discounts: [],
        customerId: null, // Will be set by Venpos
        customer: venposCustomer
      };

      // Validate through basket load (no family saver for validation)
      const result = await this.loadBasket(locationId, basketPayload, null);
      
      if (result.isError) {
        return {
          isError: true,
          data: null,
          error: result.error || 'Customer validation failed'
        };
      }

      return {
        isError: false,
        data: {
          isValid: true,
          customerValidated: true,
          basketDetails: result.data
        }
      };
    } catch (error) {

      return {
        isError: true,
        data: null,
        error: String(error)
      };
    }
  },

  // Enhanced basket management following API documentation
  async createBasketWithValidation(locationId, basketItems, customerDetails = null, existingOrderId = null, existingCustomerId = null) {
    try {

      // Use Basket/Load directly (which creates and loads in one call)
      // Prioritize: existingCustomerId (from registration) > customerDetails.webCustomerId > null
      const payloadCustomerId = existingCustomerId || customerDetails?.webCustomerId || null;

      const loadPayload = {
        pageId: basketItems[0]?.pageId,
        items: basketItems,
        giftAidSelected: false,
        discounts: [],
        discountsFromMembership: [],
        customerId: payloadCustomerId,
        orderId: existingOrderId || null  // Preserve existing order ID if provided
      };

      // Don't include customer object - only customerId is needed (from registration)

      const loadResponse = await this.loadBasket(locationId, loadPayload, null);
      
      if (loadResponse.isError) {
        throw new Error(loadResponse.error || 'Failed to load basket');
      }

      // Extract basket information from the response
      const basketId = loadResponse.data?.orderId;
      const orderId = loadResponse.data?.orderId; // Same as basketId in Venpos
      const customerId = loadResponse.data?.customerId;

      if (!basketId) {


      }

      return {
        isError: false,
        data: {
          basketId: basketId || `basket-${Date.now()}`, // Fallback ID if needed
          orderId: orderId,
          customerId: customerId,
          basketDetails: loadResponse.data || loadResponse,
          loadResponse: loadResponse.data
        }
      };
    } catch (error) {

      return { isError: true, data: null, error: String(error) };
    }
  },

  // Apply voucher/discount code with proper error handling
  async applyVoucherCode(locationId, basketId, voucherCode) {
    try {
      // NOTE: The Basket/ApplyVoucher endpoint has been deprecated.
      // Discounts are now applied via Basket/Load by including them in the discounts array.
      // This function is kept for backward compatibility but should be replaced with loadBasket calls.
      // 
      // To apply a discount, use loadBasket with discounts array instead:
      // await venposService.loadBasket(locationId, { ...payload, discounts: [voucherCode] }, null);

      console.warn('[DEPRECATED] applyVoucherCode is deprecated. Use loadBasket with discounts array instead.');
      
      // For backward compatibility, we'll need to get the current basket first
      // then reload it with the discount code included
      // However, without the full basket payload, we can't properly reload
      // So we'll return an error suggesting the correct approach
      return {
        isError: true,
        data: null,
        error: 'applyVoucherCode is deprecated. Please use loadBasket with discounts array instead. See Basket/Load endpoint documentation.'
      };
    } catch (error) {

      return {
        isError: true,
        data: null,
        error: String(error)
      };
    }
  },

  async refreshBasketWithDiscounts(locationId, basketId, currentItems, additionalDiscounts = [], registeredCustomerId = null, orderId = null, pageId = null) {
    try {

      // Prepare payload for basket refresh matching working example structure
      // CRITICAL: Use provided orderId if available, otherwise fallback to basketId
      // The orderId from basket data is more reliable than basketId
      const actualOrderId = orderId || basketId;
      
      // CRITICAL: Validate items before sending
      if (!currentItems || currentItems.length === 0) {
        throw new Error('No items provided for basket refresh');
      }

      // Validate each item has required fields
      const validatedItems = currentItems.map((item, index) => {
        if (!item.webSaleItemId) {
          throw new Error(`Item at index ${index} is missing webSaleItemId`);
        }
        if (!item.quantity || item.quantity <= 0) {
          throw new Error(`Item at index ${index} has invalid quantity: ${item.quantity}`);
        }
        return item;
      });

      // CRITICAL: Get pageId from items first, then fallback to provided pageId
      // Never use basketId as pageId - basketId is a GUID, not a pageId
      const payloadPageId = validatedItems[0]?.pageId || pageId;
      
      if (!payloadPageId) {
        console.error('❌ [venposService] No pageId found in items or provided, cannot refresh basket');
        throw new Error('Missing pageId - required for basket refresh');
      }

      // CRITICAL: Extract modifiers from items and format for root-level array (matching loadBasket structure)
      // Format: [{webSaleItemId, productModifierId, productModifierTextId, value, index}]
      const modifiersArray = [];
      const modifierIndexByItem = new Map(); // Track index per webSaleItemId
      validatedItems.forEach(item => {
        if (item.modifierSelection) {
          const currentIndex = modifierIndexByItem.get(item.webSaleItemId) || 0;
          modifierIndexByItem.set(item.webSaleItemId, currentIndex + 1);
          
          modifiersArray.push({
            webSaleItemId: item.webSaleItemId,
            productModifierId: item.modifierSelection.modifierId,
            productModifierTextId: item.modifierSelection.modifierTextId,
            value: "1",
            index: currentIndex // Use per-item index instead of global index
          });
        }
      });

      // Clean items - remove modifierSelection and questionAnswers (modifiers go to root level)
      const cleanItems = validatedItems.map(item => {
        const { questionAnswers, modifierSelection, ...cleanItem } = item;
        return {
          pageType: cleanItem.pageType || 'WebPage',
          pageId: cleanItem.pageId,
          fixedDonationAmount: cleanItem.fixedDonationAmount ?? 0,
          webSaleItemId: cleanItem.webSaleItemId,
          webSaleItemTypeID: cleanItem.webSaleItemTypeID || 2,
          quantity: cleanItem.quantity,
          name: cleanItem.name || '',
          giftAidEligible: cleanItem.giftAidEligible || false,
          isSysGift: cleanItem.isSysGift || false,
          isSysMemberCard: cleanItem.isSysMemberCard || false,
          // Include date/time fields if present
          ...(cleanItem.selectedDate && { selectedDate: cleanItem.selectedDate }),
          ...(cleanItem.reservationExpiryDateTime && { reservationExpiryDateTime: cleanItem.reservationExpiryDateTime }),
          ...(cleanItem.eventId && { eventId: cleanItem.eventId }),
          ...(cleanItem.eventCapacityUsageId && { eventCapacityUsageId: cleanItem.eventCapacityUsageId }),
          // Include parentWebSaleItemId for upsells
          ...(cleanItem.parentWebSaleItemId && { parentWebSaleItemId: cleanItem.parentWebSaleItemId })
        };
      });

      const refreshPayload = {
        items: cleanItems,
        discounts: additionalDiscounts || [], // Use only the provided discounts
        giftAidSelected: false,
        pageId: payloadPageId, // Use pageId from items, never use basketId
        modifiers: modifiersArray, // Modifiers at root level (matching loadBasket structure)
        discountsFromMembership: [],
        orderId: actualOrderId // Use actual orderId from basket if available
      };

      // Only include customerId if we have one (registered customer)
      if (registeredCustomerId) {
        refreshPayload.customerId = registeredCustomerId;
      }

      // Log the payload for debugging (without sensitive data)
      console.log('📤 [venposService] refreshBasketWithDiscounts payload:', {
        itemCount: validatedItems.length,
        items: validatedItems.map(item => ({
          webSaleItemId: item.webSaleItemId,
          name: item.name,
          quantity: item.quantity,
          pageType: item.pageType,
          hasSelectedDate: !!item.selectedDate,
          hasEventId: !!item.eventId,
          hasEventCapacityUsageId: !!item.eventCapacityUsageId
        })),
        discountCount: additionalDiscounts?.length || 0,
        discounts: additionalDiscounts,
        orderId: actualOrderId,
        pageId: refreshPayload.pageId,
        hasCustomerId: !!registeredCustomerId
      });

      // Refresh basket with provided discounts using direct Basket/Load call
      const url = new URL(`${VENPOS_API_BASE}/Basket/Load`);
      url.searchParams.append('locationId', locationId);

      const refreshResponse = await makeApiRequest(url.toString(), {
        method: 'POST',
        body: JSON.stringify(refreshPayload)
      });
      
      if (refreshResponse.isError) {
        throw new Error(refreshResponse.error || 'Failed to refresh basket with discounts');
      }

      return {
        isError: false,
        data: refreshResponse.data
      };

    } catch (error) {

      return {
        isError: true,
        error: error.message || 'Failed to refresh basket with discounts'
      };
    }
  },

  // Create payment request using working Venpos endpoints + Payment Sense
  async createPaymentRequest(locationId, basketId, customerDetails, amount, selectedTickets = null, selectedDate = null, pageId = null, modifierSelections = {}, registeredCustomerId = null, appliedDiscounts = [], selectedTimeSlot = null, memberships = null) {
    try {

      // Use payload structure from ACTUAL working Venpos system (network traffic analysis)
      const currentDate = selectedDate || (() => {
        const today = new Date();
        return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
      })();
      const realPageId = pageId || customerDetails.pageId || basketId;
      
      // Detect if this is a membership journey - use same logic as BookingJourney
      // Check pageId first (most reliable), then memberships array, then selectedTickets
      const MEMBERSHIP_PAGE_ID = '37bd858f-4f04-4e83-a469-05274db0d546';
      const isMembershipJourneyByPageId = realPageId === MEMBERSHIP_PAGE_ID;
      const isMembershipJourneyByMemberships = memberships && memberships.length > 0;
      const isMembershipJourneyByTickets = selectedTickets && selectedTickets.length > 0 && 
        selectedTickets.every(ticket => ticket.webSaleItemTypeID === 4);
      
      const isMembershipJourney = isMembershipJourneyByPageId || isMembershipJourneyByMemberships || isMembershipJourneyByTickets;
      
      // Log detection for debugging
      if (isMembershipJourney) {
        console.log('🎟️ [createPaymentRequest] Membership journey detected:', {
          byPageId: isMembershipJourneyByPageId,
          byMemberships: isMembershipJourneyByMemberships,
          byTickets: isMembershipJourneyByTickets,
          pageId: realPageId,
          membershipsCount: memberships?.length || 0,
          selectedTicketsCount: selectedTickets?.length || 0,
          ticketTypes: selectedTickets?.map(t => t.webSaleItemTypeID) || []
        });
        
        // Warn if memberships array is missing for a membership journey
        if (!memberships || memberships.length === 0) {
          console.warn('⚠️ [createPaymentRequest] Membership journey detected but memberships array is empty!', {
            pageId: realPageId,
            memberships: memberships,
            selectedTickets: selectedTickets
          });
        }
      }
      
      // Detect if direct debit is enabled for any membership
      const hasDirectDebit = memberships && memberships.some(m => m.isDirectDebit === true);


      // CRITICAL: Load basket items from Venpos to ensure we have the correct data
      // This is especially important for Flower & Garden Show where selectedTickets might not have all data
      let basketItemsFromVenpos = [];
      const isFlowerGardenShow = realPageId === FLOWER_GARDEN_PAGE_ID;
      
      if (isFlowerGardenShow) {
        console.log('🌸 [Flower & Garden Show] Loading basket items for payment request...');
      }
      
      try {
        const basketLoadUrl = new URL(`${VENPOS_API_BASE}/Basket/Load`);
        basketLoadUrl.searchParams.append('locationId', locationId);
        
        // For Flower & Garden Show, we need to include the actual items from selectedTickets in the load request
        // Otherwise Basket/Load might return an empty basket
        let itemsForBasketLoad = [];
        if (selectedTickets && selectedTickets.length > 0) {
          // Log selectedTimeSlot data for Flower & Garden Show
          if (isFlowerGardenShow) {
            console.log('🌸 [Flower & Garden Show] Building basket items with selectedTimeSlot:', {
              hasStartDateTime: !!selectedTimeSlot?.startDateTime,
              startDateTime: selectedTimeSlot?.startDateTime,
              hasEventId: !!selectedTimeSlot?.eventId,
              eventId: selectedTimeSlot?.eventId,
              hasEventCapacityUsageId: !!selectedTimeSlot?.eventCapacityUsageId,
              eventCapacityUsageId: selectedTimeSlot?.eventCapacityUsageId,
              fallbackDate: selectedDate || currentDate
            });
          }

          itemsForBasketLoad = selectedTickets
            .filter(t => (t.quantity || 0) > 0)
            .map(t => ({
              pageType: 'WebPage',
              pageId: realPageId,
              webSaleItemId: t.webSaleItemId,
              webSaleItemTypeID: t.webSaleItemTypeID || 2,
              quantity: t.quantity,
              selectedDate: selectedTimeSlot?.startDateTime || selectedDate || currentDate,
              eventId: t.eventId || selectedTimeSlot?.eventId || null,
              eventCapacityUsageId: t.eventCapacityUsageId || selectedTimeSlot?.eventCapacityUsageId || null
            }));
        }
        
        const basketLoadPayload = {
          pageId: realPageId,
          orderId: basketId,
          customerId: registeredCustomerId || null,
          items: itemsForBasketLoad,
          giftAidSelected: false,
          discounts: appliedDiscounts || []
        };
        
        if (isFlowerGardenShow) {
          console.log('🌸 [Flower & Garden Show] Basket/Load payload:', {
            orderId: basketId,
            itemsCount: itemsForBasketLoad.length,
            items: itemsForBasketLoad.map(i => ({
              webSaleItemId: i.webSaleItemId,
              quantity: i.quantity,
              eventId: i.eventId,
              eventCapacityUsageId: i.eventCapacityUsageId
            }))
          });
        }
        
        const basketLoadResponse = await makeApiRequest(basketLoadUrl.toString(), {
          method: 'POST',
          body: JSON.stringify(basketLoadPayload)
        });
        
        if (!basketLoadResponse.isError && basketLoadResponse.data?.groups) {
          basketItemsFromVenpos = basketLoadResponse.data.groups.flatMap(g => g.items || []);
          
          if (isFlowerGardenShow) {
            console.log('🌸 [Flower & Garden Show] Basket loaded from Venpos:', {
              basketItemsCount: basketItemsFromVenpos.length,
              items: basketItemsFromVenpos.map(item => ({
                webSaleItemId: item.webSaleItemId,
                name: item.name,
                quantity: item.quantity,
                eventId: item.eventId,
                eventCapacityUsageId: item.eventCapacityUsageId,
                price: item.price,
                totalPrice: item.totalPrice
              }))
            });
            
            if (basketItemsFromVenpos.length === 0) {
              console.error('❌ [Flower & Garden Show] Basket/Load returned 0 items! This will cause the payment request to fail.');
            }
          } else {
            console.log('✅ [createPaymentRequest] Loaded basket items from Venpos:', {
              basketItemsCount: basketItemsFromVenpos.length
            });
          }
        } else {
          if (isFlowerGardenShow) {
            console.error('❌ [Flower & Garden Show] Basket/Load failed:', basketLoadResponse.error || 'Unknown error');
          }
        }
      } catch (basketLoadError) {
        console.warn('⚠️ [createPaymentRequest] Failed to load basket from Venpos, will use selectedTickets:', basketLoadError);
        if (isFlowerGardenShow) {
          console.error('❌ [Flower & Garden Show] Basket load exception:', basketLoadError);
        }
      }

      // Build real Items array - prefer basket items from Venpos, fallback to selectedTickets
      let itemsArray = [];
      
      // Prefer basket items from Venpos if available (they have the correct event metadata)
      if (basketItemsFromVenpos.length > 0) {
        itemsArray = basketItemsFromVenpos.map(item => ({
          pageType: item.pageType || "WebPage",
          pageId: realPageId,
          webSaleItemId: item.webSaleItemId,
          webSaleItemTypeID: item.webSaleItemTypeID || item.webSaleItemTypeId || 2,
          quantity: item.quantity || 1,
          fixedDonationAmount: 0,
          modifiers: [],
          name: item.name || item.description || '',
          giftAidEligible: item.giftAidEligible || false,
          isSysGift: false,
          isSysMemberCard: false,
          ...(isMembershipJourney ? {} : {
            selectedDate: item.selectedDate || item.startDateTime || currentDate,
            eventId: item.eventId || null,
            eventCapacityUsageId: item.eventCapacityUsageId || null,
            reservationExpiryDateTime: item.reservationExpiryDateTime || new Date(Date.now() + 30 * 60 * 1000).toISOString()
          })
        }));
        console.log('✅ [createPaymentRequest] Using basket items from Venpos:', {
          itemsCount: itemsArray.length
        });
        
        // CRITICAL: Merge event IDs and modifiers from selectedTickets into basket items
        // Basket/Load doesn't return eventId/eventCapacityUsageId, but we have them in selectedTickets
        if (selectedTickets && selectedTickets.length > 0) {
          if (isFlowerGardenShow) {
            console.log('🌸 [Flower & Garden Show] Starting merge - selectedTickets:', selectedTickets.map(t => ({
              webSaleItemId: t.webSaleItemId,
              eventId: t.eventId,
              eventCapacityUsageId: t.eventCapacityUsageId
            })));
            console.log('🌸 [Flower & Garden Show] Starting merge - selectedTimeSlot:', {
              eventId: selectedTimeSlot?.eventId,
              eventCapacityUsageId: selectedTimeSlot?.eventCapacityUsageId,
              startDateTime: selectedTimeSlot?.startDateTime
            });
          }
          
          itemsArray = itemsArray.map(basketItem => {
            const matchingTicket = selectedTickets.find(t => 
              String(t.webSaleItemId) === String(basketItem.webSaleItemId)
            );
            if (matchingTicket) {
              if (isFlowerGardenShow) {
                console.log('🌸 [Flower & Garden Show] Matched ticket for webSaleItemId:', basketItem.webSaleItemId, {
                  ticketEventId: matchingTicket.eventId,
                  ticketEventCapacityUsageId: matchingTicket.eventCapacityUsageId,
                  basketItemBefore: {
                    eventId: basketItem.eventId,
                    eventCapacityUsageId: basketItem.eventCapacityUsageId
                  }
                });
              }
              
              // Merge event IDs from selectedTickets (which have capacity reservation IDs)
              if (matchingTicket.eventId && !basketItem.eventId) {
                basketItem.eventId = matchingTicket.eventId;
              }
              if (matchingTicket.eventCapacityUsageId && !basketItem.eventCapacityUsageId) {
                basketItem.eventCapacityUsageId = matchingTicket.eventCapacityUsageId;
              }
              // Also use selectedTimeSlot as fallback for event IDs
              if (!basketItem.eventId && selectedTimeSlot?.eventId) {
                basketItem.eventId = selectedTimeSlot.eventId;
              }
              if (!basketItem.eventCapacityUsageId && selectedTimeSlot?.eventCapacityUsageId) {
                basketItem.eventCapacityUsageId = selectedTimeSlot.eventCapacityUsageId;
              }
              // Use proper datetime from selectedTimeSlot
              if (selectedTimeSlot?.startDateTime && !isMembershipJourney) {
                basketItem.selectedDate = selectedTimeSlot.startDateTime;
              }
              
              if (isFlowerGardenShow) {
                console.log('🌸 [Flower & Garden Show] basketItem AFTER merge:', {
                  eventId: basketItem.eventId,
                  eventCapacityUsageId: basketItem.eventCapacityUsageId,
                  selectedDate: basketItem.selectedDate
                });
              }
              
              // Merge modifiers
              if (modifierSelections && modifierSelections[matchingTicket.webSaleItemId]) {
                const modifierData = modifierSelections[matchingTicket.webSaleItemId];
                basketItem.modifiers = [{
                  productModifierId: parseInt(modifierData.modifierId),
                  productModifierTextId: modifierData.modifierTextId,
                  quantity: 1
                }];
              }
            }
            return basketItem;
          });
        }
        
        if (isFlowerGardenShow) {
          console.log('🌸 [Flower & Garden Show] Final itemsArray after merging event IDs from selectedTickets:', {
            itemsCount: itemsArray.length,
            items: itemsArray.map(item => ({
              webSaleItemId: item.webSaleItemId,
              name: item.name,
              quantity: item.quantity,
              selectedDate: item.selectedDate,
              eventId: item.eventId,
              eventCapacityUsageId: item.eventCapacityUsageId,
              hasEventId: !!item.eventId,
              hasEventCapacityUsageId: !!item.eventCapacityUsageId
            }))
          });
          
          if (itemsArray.length === 0) {
            console.error('❌ [Flower & Garden Show] itemsArray is EMPTY! Will fallback to selectedTickets.');
          } else if (!itemsArray[0].eventId || !itemsArray[0].eventCapacityUsageId) {
            console.error('❌ [Flower & Garden Show] Items still missing event IDs after merge!', {
              firstItem: {
                eventId: itemsArray[0].eventId,
                eventCapacityUsageId: itemsArray[0].eventCapacityUsageId,
                selectedDate: itemsArray[0].selectedDate
              },
              selectedTimeSlot: {
                eventId: selectedTimeSlot?.eventId,
                eventCapacityUsageId: selectedTimeSlot?.eventCapacityUsageId,
                startDateTime: selectedTimeSlot?.startDateTime
              },
              selectedTickets: selectedTickets?.map(t => ({
                webSaleItemId: t.webSaleItemId,
                eventId: t.eventId,
                eventCapacityUsageId: t.eventCapacityUsageId
              }))
            });
          }
        }
      }
      
      // CRITICAL: If itemsArray is still empty (basket load failed or returned empty), use selectedTickets as fallback
      if (itemsArray.length === 0 && selectedTickets && selectedTickets.length > 0) {
        if (isFlowerGardenShow) {
          console.warn('⚠️ [Flower & Garden Show] Basket items were empty, falling back to selectedTickets');
        }
        console.log('📦 [createPaymentRequest] Building items array from selectedTickets (fallback):', {
          selectedTicketsCount: selectedTickets.length
        });

        itemsArray = selectedTickets
          .filter(ticket => (ticket.quantity || 0) > 0)
          .map(ticket => {
            // Get modifiers for this specific item
            const itemModifiers = [];
            if (modifierSelections[ticket.webSaleItemId]) {
              const modifierData = modifierSelections[ticket.webSaleItemId];
              itemModifiers.push({
                productModifierId: parseInt(modifierData.modifierId),
                productModifierTextId: modifierData.modifierTextId,
                quantity: 1
              });
            }
            
            // For membership journeys, exclude date/time/event fields
            const baseItem = {
              pageType: "WebPage",
              pageId: realPageId,  // Use real pageId, not basketId
              webSaleItemId: ticket.webSaleItemId,
              webSaleItemTypeID: ticket.webSaleItemTypeID || 2,
              quantity: ticket.quantity,
              fixedDonationAmount: 0,
              modifiers: itemModifiers,
              name: ticket.name || '',
              giftAidEligible: ticket.giftAidEligible || false,
              isSysGift: false,
              isSysMemberCard: false
            };
            
            // Only add date/time/event fields for non-membership journeys
            if (isMembershipJourney) {
              return baseItem;
            }
            
            return {
              ...baseItem,
              selectedDate: currentDate,
              // ✅ CRITICAL: Use ticket's own event metadata first, then fallback to selectedTimeSlot
              eventId: ticket.eventId || selectedTimeSlot?.eventId || null,
              eventCapacityUsageId: ticket.eventCapacityUsageId || selectedTimeSlot?.eventCapacityUsageId || null,
              reservationExpiryDateTime: ticket.reservationExpiryDateTime || new Date(Date.now() + 30 * 60 * 1000).toISOString()
            };
          });
          
        console.log('📦 [createPaymentRequest] itemsArray after mapping:', {
          itemsArrayCount: itemsArray.length,
          items: itemsArray.map(item => ({
            webSaleItemId: item.webSaleItemId,
            name: item.name,
            quantity: item.quantity,
            eventId: item.eventId,
            eventCapacityUsageId: item.eventCapacityUsageId,
            selectedDate: item.selectedDate,
            pageType: item.pageType,
            pageId: item.pageId
          }))
        });
      } else if (itemsArray.length === 0) {
        // CRITICAL FIX: Only create empty fallback item if array is truly empty
        // Do NOT overwrite existing items from basket!
        console.warn('⚠️ [createPaymentRequest] No items found, creating minimal fallback item');
        itemsArray = [{
          pageType: "WebPage",
          pageId: realPageId,  // Use real pageId, not basketId
          fixedDonationAmount: 0,
          modifiers: []
        }];
      }
      
      const paymentPayload = {
        // Core payment fields matching real Venpos structure
        customerId: registeredCustomerId || null, // Use registered customer ID from Venpos
        orderId: basketId,
        PageId: realPageId,  // Use real pageId, not basketId
        
        // Basket structure from working network calls
        Discounts: appliedDiscounts || [], // ✅ Include applied discounts!
        GiftAidSelected: false,
        Items: itemsArray,
        
        // Payment-specific fields
        amount: parseFloat(amount),
        currency: 'GBP',
        
        // Customer details
        customerDetails: {
          firstName: customerDetails.firstName,
          lastName: customerDetails.lastName,
          email: customerDetails.email,
          mobile: customerDetails.phone || customerDetails.mobile,
          addressFirstLine: customerDetails.address?.line1 || customerDetails.address?.addressFirstLine,
          addressSecondLine: customerDetails.address?.line2 || customerDetails.address?.addressSecondLine,
          cityTown: customerDetails.address?.city || customerDetails.address?.cityTown,
          postCode: customerDetails.address?.postcode || customerDetails.address?.postCode,
          selectedCountry: customerDetails.address?.country || customerDetails.selectedCountry || 'UK'
        },
        
        // URLs for DNA Payments integration (route back into our app)
        returnUrl: `${window.location.origin}/booking-journey`,
        cancelUrl: `${window.location.origin}/booking-journey`,
        webhookUrl: `${window.location.origin}/api/payment-webhook`
      };

      // **CRITICAL FIX**: Use POST /Payment endpoint (the REAL Venpos flow)
      // This creates the order AND returns the payment token in one call

      // Extract modifiers from selectedTickets (not from itemsArray which doesn't have modifiers)
      const paymentModifiers = [];
      let paymentModifierIndex = 0;

      if (selectedTickets && selectedTickets.length > 0) {
        selectedTickets.forEach((ticket, idx) => {

          if (ticket.modifierSelection) {
            paymentModifiers.push({
              webSaleItemId: ticket.webSaleItemId,
              productModifierId: ticket.modifierSelection.modifierId,
              productModifierTextId: ticket.modifierSelection.modifierTextId,
              value: "1",
              index: paymentModifierIndex++
            });

          }
        });
      }


      if (paymentModifiers.length > 0) {

      } else {

      }
      
      // Build eventParticipants array from selectedTickets with participant details
      const paymentEventParticipantsMap = new Map();
      
      if (selectedTickets && selectedTickets.length > 0) {
        selectedTickets.forEach(ticket => {
          const isArray = Array.isArray(ticket.participantDetails);
          const hasParticipantAnswers = ticket.participantAnswers && Object.keys(ticket.participantAnswers).length > 0;
          
          // participantDetails is the API response (array of events)
          // participantAnswers is the user input (flat object)
          if (isArray && ticket.participantDetails.length > 0 && hasParticipantAnswers) {


            // Process each event in the participant details API response
            ticket.participantDetails.forEach(eventParticipant => {
              const eventId = eventParticipant.eventId;
              
              if (!eventId) {

                return;
              }
              
              // Get or create event entry in map
              if (!paymentEventParticipantsMap.has(eventId)) {
                paymentEventParticipantsMap.set(eventId, {
                  eventId: eventParticipant.eventId,
                  description: eventParticipant.description,
                  eventName: eventParticipant.eventName,
                  areParticipantsRequired: eventParticipant.areParticipantsRequired,
                  requiresAllParticipantDetails: eventParticipant.requiresAllParticipantDetails,
                  eventItemDetails: []
                });
              }
              
              const eventEntry = paymentEventParticipantsMap.get(eventId);
              
              // Process each item in the event
              eventParticipant.eventItemDetails?.forEach((itemDetail, itemIdx) => {
                const participantDetailsList = [];
                
                // Process each participant in the item
                itemDetail.participantDetails?.forEach((participant, pIdx) => {
                  // Build the key used in participantAnswers: "eventId-itemIdx-pIdx"
                  const answerKey = `${eventId}-${itemIdx}-${pIdx}`;
                  const userAnswers = ticket.participantAnswers[answerKey] || {};
                  
                  participantDetailsList.push({
                    webCustomerId: participant.webCustomerId || null,
                    firstName: userAnswers.firstName || participant.firstName || '',
                    lastName: userAnswers.lastName || participant.lastName || '',
                    selectedTitle: userAnswers.selectedTitle || participant.selectedTitle || 1,
                    isLeadParticipant: participant.isLeadParticipant || false,
                    isCustomerDetail: participant.isCustomerDetail || false,
                    isRequired: participant.isRequired || false,
                    errorMessage: participant.errorMessage || [],
                    titleIsMandatory: participant.titleIsMandatory !== undefined ? participant.titleIsMandatory : true
                  });
                });
                
                // Add item details
                eventEntry.eventItemDetails.push({
                  itemName: itemDetail.itemName,
                  participantDetails: participantDetailsList,
                  participantsCount: itemDetail.participantsCount || participantDetailsList.length
                });
              });
            });
          }
        });
      }
      
      const paymentEventParticipants = Array.from(paymentEventParticipantsMap.values());

      // Build questions array from selectedTickets with questionAnswers (supports per-instance keys "id__occ")
      const paymentQuestions = [];
      if (selectedTickets && selectedTickets.length > 0) {
        selectedTickets.forEach(ticket => {
          if (ticket.questionAnswers && Object.keys(ticket.questionAnswers).length > 0) {
            Object.entries(ticket.questionAnswers).forEach(([rawKey, answer]) => {
              let baseId = rawKey;
              let occIndex = 0;
              if (typeof rawKey === 'string' && rawKey.includes('__')) {
                const parts = rawKey.split('__');
                baseId = parts[0];
                occIndex = Number(parts[1]) || 0;
              }

              // Find the full question details from the ticket's eventQuestions if available
              let questionData = null;
              if (ticket.eventQuestions && Array.isArray(ticket.eventQuestions)) {
                questionData = ticket.eventQuestions.find(q => q.questionId === baseId || q.id === baseId);
              }

              if (questionData) {
                paymentQuestions.push({
                  id: questionData.questionId || questionData.id,
                  question: questionData.question,
                  value: answer,
                  eventId: questionData.eventId || ticket.eventId || null,
                  configurationId: questionData.configurationId || null,
                  index: occIndex,
                  productId: questionData.productId || null
                });
              } else {
                paymentQuestions.push({
                  id: baseId,
                  question: '',
                  value: answer,
                  eventId: ticket.eventId || null,
                  configurationId: null,
                  index: occIndex,
                  productId: null
                });
              }
            });
          }
        });
      }

      // CRITICAL: For EVENTS, keep individual participants (no consolidation)
      // For non-events, we could consolidate, but events need separate participants in Venpos
      const consolidatedPaymentItems = [];
      const paymentItemMap = new Map(); // Key: webSaleItemId|eventId|personIndex -> individual participant

      // DEBUG: Log items just before consolidation for Flower & Garden Show
      if (isFlowerGardenShow) {
        console.log('🌸 [Flower & Garden Show] itemsArray BEFORE consolidation:', {
          itemsCount: itemsArray.length,
          items: itemsArray.map(i => ({
            webSaleItemId: i.webSaleItemId,
            name: i.name,
            selectedDate: i.selectedDate,
            eventId: i.eventId,
            eventCapacityUsageId: i.eventCapacityUsageId,
            hasEventId: !!i.eventId,
            hasEventCapacityUsageId: !!i.eventCapacityUsageId
          }))
        });
      }

      itemsArray.forEach((item, idx) => {
        if (isFlowerGardenShow) {
          console.log('🌸 [Flower & Garden Show] Consolidation loop - item #' + idx + ':', {
            webSaleItemId: item.webSaleItemId,
            name: item.name,
            selectedDate: item.selectedDate,
            eventId: item.eventId,
            eventCapacityUsageId: item.eventCapacityUsageId,
            hasEventId: !!item.eventId,
            hasEventCapacityUsageId: !!item.eventCapacityUsageId,
            currentDate: currentDate
          });
        }
        
        // Use personIndex (from ticket expansion) or index to ensure each person is separate
        const personIndex = item.personIndex !== undefined ? item.personIndex : idx;
        const key = `${item.webSaleItemId}|${item.eventId}|${item.selectedDate}|${personIndex}`;
        
        // For events, keep each participant separate (no consolidation)
        // For membership journeys, exclude date/time/event fields
        const basePaymentItem = {
          pageType: item.pageType || "WebPage",
          pageId: realPageId,
          fixedDonationAmount: item.fixedDonationAmount || 0,
          giftAidEligible: item.giftAidEligible || false,
          webSaleItemId: item.webSaleItemId,
          quantity: item.quantity || 1, // Should always be 1 for individual participants
          webSaleItemTypeID: item.webSaleItemTypeID || 2,
          name: item.name || `Payment Item ${idx + 1}`, // CRITICAL: Ensure name is never undefined
          isSysGift: false,
          isSysMemberCard: false
        };
        
        // Only add date/time/event fields for non-membership journeys
        if (isMembershipJourney) {
          paymentItemMap.set(key, {
            ...basePaymentItem,
            // Preserve person tracking for debugging
            personIndex: personIndex
          });
        } else {
          const consolidatedItem = {
            ...basePaymentItem,
            selectedDate: item.selectedDate || currentDate,
            eventId: item.eventId || null,
            eventCapacityUsageId: item.eventCapacityUsageId || null,
            reservationExpiryDateTime: item.reservationExpiryDateTime || new Date(Date.now() + 10 * 60 * 1000).toISOString(),
            // Preserve person tracking for debugging
            personIndex: personIndex
          };
          
          if (isFlowerGardenShow) {
            console.log('🌸 [Flower & Garden Show] Consolidation loop - consolidated item:', {
              webSaleItemId: consolidatedItem.webSaleItemId,
              name: consolidatedItem.name,
              selectedDate: consolidatedItem.selectedDate,
              eventId: consolidatedItem.eventId,
              eventCapacityUsageId: consolidatedItem.eventCapacityUsageId
            });
          }
          
          paymentItemMap.set(key, consolidatedItem);
        }

      });
      
      // Convert map to array (no consolidation - each person is separate)
      const consolidatedPaymentItemsArray = Array.from(paymentItemMap.values());
      
      // CRITICAL: Validate items array before building payload - especially for Flower & Garden Show
      if (consolidatedPaymentItemsArray.length === 0) {
        const errorMsg = isFlowerGardenShow 
          ? 'Flower & Garden Show: No items found for payment request. Please try selecting your tickets again.'
          : 'No items found for payment request. Please try again.';
        console.error(`❌ [createPaymentRequest] ${errorMsg}`, {
          basketItemsFromVenposCount: basketItemsFromVenpos.length,
          selectedTicketsCount: selectedTickets?.length || 0,
          itemsArrayCount: itemsArray.length
        });
        throw new Error(errorMsg);
      }
      
      if (isFlowerGardenShow) {
        console.log('🌸 [Flower & Garden Show] Final consolidatedPaymentItemsArray before payment:', {
          itemsCount: consolidatedPaymentItemsArray.length,
          items: consolidatedPaymentItemsArray.map(item => ({
            webSaleItemId: item.webSaleItemId,
            name: item.name,
            quantity: item.quantity,
            eventId: item.eventId,
            eventCapacityUsageId: item.eventCapacityUsageId,
            selectedDate: item.selectedDate
          }))
        });
      }

      // Build the full order payload matching the network trace structure
      // For membership journeys, match CreateFreeOrder structure exactly (no modifiers/eventParticipants at root)
      const orderPayload = isMembershipJourney ? {
        // Match CreateFreeOrder structure exactly for membership journeys
        cateringCustomTextResponse1: '',
        cateringCustomTextResponse2: '',
        cateringFacilityId: null,
        cateringMode: 'NotSetup',
        customerId: registeredCustomerId || null,
        ddOnCompletionredirectUrl: `https://online1.venpos.net/VenPosJourneyUI/Confirmation?WebTransHeaderId=${basketId}&locationId=${locationId}&mainWebPageId=${realPageId}&startId=${realPageId}`,
        discounts: appliedDiscounts || [],
        giftAidSelected: false,
        items: consolidatedPaymentItemsArray,
        memberships: memberships || [], // CRITICAL: Include memberships for membership journeys
        newAccountCreated: false,
        orderId: basketId,
        pageId: realPageId,
        questions: paymentQuestions || [],
        webTransHeaderId: basketId
        // NOTE: CreateFreeOrder does NOT include modifiers or eventParticipants at root level
      } : {
        // Regular booking structure with modifiers and eventParticipants
        items: consolidatedPaymentItemsArray,
        discounts: appliedDiscounts || [],
        giftAidSelected: false,
        pageId: realPageId,
        modifiers: paymentModifiers, // Include modifiers at root level
        memberships: memberships || [],
        customerId: registeredCustomerId || null,
        questions: paymentQuestions, // Include question answers for paid orders
        webTransHeaderId: basketId,
        eventParticipants: paymentEventParticipants,
        cateringMode: 'NotSetup',
        cateringFacilityId: null,
        ddOnCompletionredirectUrl: IS_DEVELOPMENT 
          ? 'https://belvoircastle.com/booking-journey'
          : `${window.location.origin}/booking-journey`,
        cateringCustomTextResponse1: '',
        cateringCustomTextResponse2: '',
        orderId: basketId,
        newAccountCreated: false
      };
      
      console.log('💳 [createPaymentRequest] Order payload:', {
        itemsCount: consolidatedPaymentItemsArray.length,
        items: consolidatedPaymentItemsArray.map(item => ({
          webSaleItemId: item.webSaleItemId,
          name: item.name,
          quantity: item.quantity,
          selectedDate: item.selectedDate,
          eventId: item.eventId,
          eventCapacityUsageId: item.eventCapacityUsageId,
          pageType: item.pageType,
          pageId: item.pageId
        })),
        discountsCount: (appliedDiscounts || []).length,
        discounts: appliedDiscounts,
        membershipsCount: (memberships || []).length,
        memberships: memberships || []
      });

      let finalPriceResult = null;
      try {
        // Use POST /Payment endpoint - this creates the order AND returns payment token
        const paymentUrl = `${VENPOS_API_BASE}/Payment?locationId=${locationId}&paymentType=0&pageId=${realPageId}`;

        console.log('💳 [createPaymentRequest] POST /Payment payload summary:', {
          amount: orderPayload.amount,
          itemsCount: orderPayload.items.length,
          items: orderPayload.items.map(item => ({
            webSaleItemId: item.webSaleItemId,
            name: item.name,
            quantity: item.quantity,
            selectedDate: item.selectedDate,
            eventId: item.eventId,
            eventCapacityUsageId: item.eventCapacityUsageId,
            pageType: item.pageType,
            pageId: item.pageId
          })),
          discountsCount: orderPayload.discounts.length,
          membershipsCount: (orderPayload.memberships || []).length,
          orderId: orderPayload.orderId,
          pageId: orderPayload.pageId,
          webTransHeaderId: orderPayload.webTransHeaderId
        });
        
        // Log FULL payload for Flower & Garden Show to debug
        if (realPageId === FLOWER_GARDEN_PAGE_ID) {
          console.log('🌸 [Flower & Garden Show] FULL POST /Payment Payload:', JSON.stringify(orderPayload, null, 2));
        }
        
        // Log full payload for membership journeys to compare with CreateFreeOrder
        if (isMembershipJourney && orderPayload.memberships && orderPayload.memberships.length > 0) {
          console.log('💳 [createPaymentRequest] POST /Payment FULL PAYLOAD (membership journey):', JSON.stringify(orderPayload, null, 2));
          console.log('💳 [createPaymentRequest] POST /Payment memberships detail:', {
            membershipsCount: orderPayload.memberships.length,
            memberships: orderPayload.memberships.map(m => ({
              membershipTypeId: m.membershipTypeId,
              basketItemId: m.basketItemId,
              memberCount: m.memberCount,
              memberDetailsCount: m.memberDetails?.length || 0,
              firstMember: m.memberDetails?.[0] ? {
                firstName: m.memberDetails[0].firstName,
                lastName: m.memberDetails[0].lastName,
                email: m.memberDetails[0].email,
                mobile: m.memberDetails[0].mobile
              } : null
            }))
          });
        }
        
        // For membership journeys, use headers matching CreateFreeOrder
        const paymentHeaders = isMembershipJourney ? {
          'Accept': 'application/json, text/javascript, */*; q=0.01',
          'Accept-Language': 'en-GB,en-US;q=0.9,en;q=0.8',
          'Content-Type': 'application/json',
          'Origin': 'https://online1.venpos.net',
          'Referer': `https://online1.venpos.net/VenPosJourneyUI/Membership?locationId=${locationId}&mainWebPageId=${realPageId}&startId=${realPageId}`,
          'X-Requested-With': 'XMLHttpRequest'
        } : {
          'Accept': 'application/json, text/javascript, */*; q=0.01',
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        };
        
        finalPriceResult = await makeApiRequest(paymentUrl, {
          method: 'POST',
          headers: paymentHeaders,
          body: JSON.stringify(orderPayload)
        });
        
        console.log('💳 [createPaymentRequest] POST /Payment response:', {
          isError: finalPriceResult.isError,
          amount: finalPriceResult.data?.amount,
          orderId: finalPriceResult.data?.orderId,
          completed: finalPriceResult.data?.completed,
          bookingReference: finalPriceResult.data?.bookingReference,
          transRef: finalPriceResult.data?.transRef,
          // ✅ CRITICAL: Log if booking is being finalized prematurely
          isBookingFinalized: !!(finalPriceResult.data?.bookingReference || finalPriceResult.data?.transRef || finalPriceResult.data?.completed === true)
        });
        
        // ✅ LOG: Inform about order creation timing
        console.log('📦 [createPaymentRequest] ===== ORDER CREATION =====');
        console.log('📦 [createPaymentRequest] POST /Payment has created an order in Venpos');
        console.log('📦 [createPaymentRequest] This is BEFORE payment is completed');
        console.log('📦 [createPaymentRequest] The order should be in "PENDING/UNPAID" status');
        console.log('📦 [createPaymentRequest] Booking will be confirmed AFTER payment succeeds');
        console.log('📦 [createPaymentRequest] ===========================');
        
        // ✅ WARNING: If POST /Payment is finalizing the booking, that's a problem
        if (!finalPriceResult.isError && (finalPriceResult.data?.bookingReference || finalPriceResult.data?.transRef)) {
          console.warn('⚠️⚠️⚠️ [createPaymentRequest] CRITICAL WARNING ⚠️⚠️⚠️');
          console.warn('⚠️ [createPaymentRequest] POST /Payment returned booking reference!');
          console.warn('⚠️ [createPaymentRequest] This suggests the booking may be CONFIRMED before payment.');
          console.warn('⚠️ [createPaymentRequest] Expected behavior: Order should be PENDING until payment succeeds.');
          console.warn('⚠️ [createPaymentRequest] Booking reference:', finalPriceResult.data?.bookingReference || finalPriceResult.data?.transRef);
          console.warn('⚠️ [createPaymentRequest] Completed status:', finalPriceResult.data?.completed);
          console.warn('⚠️⚠️⚠️⚠️⚠️⚠️⚠️⚠️⚠️⚠️⚠️⚠️⚠️⚠️⚠️⚠️⚠️⚠️');
        } else {
          console.log('✅ [createPaymentRequest] No booking reference returned (expected)');
          console.log('✅ [createPaymentRequest] Order created but NOT confirmed yet');
        }

        // If direct call fails, try through proxy
        if (finalPriceResult.isError) {

          const proxyUrl = `${window.location.origin}/api/venpos-finalize.php`;
          finalPriceResult = await fetch(proxyUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include', // Forward browser cookies to proxy
            body: JSON.stringify({
              action: 'createPayment',
              locationId: locationId,
              payload: orderPayload,
              mainWebPageId: realPageId,
              paymentType: 0
            })
          }).then(async (r) => {
            if (!r.ok) {
              const errorText = await r.text();
              return { isError: true, error: `Proxy HTTP ${r.status}: ${errorText}`, data: null };
            }
            return r.json();
          });
        }

        if (!finalPriceResult.isError) {


          // Extract payment data from POST /Payment response
          const paymentData = finalPriceResult.data || {};
          
          // Extract amount from response
          const finalAmount = paymentData.amount || amount;
          
          // Check if this is a direct debit payment (GoCardless redirect)
          if (hasDirectDebit && paymentData.redirectUrl) {
            console.log('💳 [Direct Debit] GoCardless redirect URL received:', paymentData.redirectUrl);
            
            return {
              isError: false,
              data: {
                orderId: paymentData.orderId || basketId,
                amount: finalAmount,
                currency: 'GBP',
                paymentProcessor: 1, // Direct Debit (GoCardless)
                paymentToken: paymentData.paymentToken || null,
                requiresRedirect: true,
                redirectUrl: paymentData.redirectUrl,
                completed: false,
                isDirectDebit: true,
                venposData: paymentData
              }
            };
          }

          // LOG ORDER DETAILS (especially discount codes)
          try {
            const logPayload = {
              basketId,
              orderId: paymentData.orderId || basketId,
              amount: finalAmount,
              currency: 'GBP',
              customerDetails: {
                firstName: customerDetails.firstName,
                lastName: customerDetails.lastName,
                email: customerDetails.email,
                phone: customerDetails.phone || customerDetails.mobile,
                address: customerDetails.address
              },
              appliedDiscounts: appliedDiscounts || [],
              selectedTickets: selectedTickets || [],
              selectedDate: selectedDate || currentDate,
              pageId: realPageId,
              locationId: locationId,
              paymentMethod: finalAmount > 0 ? 'PaymentSense' : 'Free',
              timestamp: new Date().toISOString()
            };

            // Send to logging API
            const logUrl = process.env.NODE_ENV === 'development' 
              ? 'http://localhost:8888/Website/front-end/public/api/log-order.php'
              : `${window.location.origin}/api/log-order.php`;
            
            fetch(logUrl, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(logPayload)
            }).catch(err => console.error('Order logging failed:', err));
          } catch (logError) {
            console.error('Failed to log order:', logError);
          }
          
          // Extract Payment Sense parameters from response
          const paymentToken = paymentData.paymentToken;
          const customParams = paymentData.customParameters || {};
          const jwt = customParams.JWT;
          const externalJsUrl = customParams.ExternalJsUrl;
          const merchantUrl = customParams.MerchantUrl;
          const orderId = paymentData.orderId || basketId;


          if (!paymentToken || !jwt || !externalJsUrl) {


            // Fall through to use the paymentsense-token.php fallback below
          } else {
            // Check if the script URL is the embedded form (client.js) or hosted checkout (checkout.js)
            const isEmbeddedForm = externalJsUrl && externalJsUrl.includes('client.js');
            const isHostedCheckout = externalJsUrl && externalJsUrl.includes('checkout.js');
            
            if (isHostedCheckout) {


              // Fall through to use the paymentsense-token.php fallback below
            } else if (isEmbeddedForm || externalJsUrl.includes('/assets/js/')) {


              const sessionData = {
                orderId: orderId,
                amount: finalAmount,
                paymentProcessor: 3, // Payment Sense
                paymentToken: paymentToken,
                requiresRedirect: false,
                completed: false,
                
                // Payment Sense parameters from Venpos
                paymentSenseCheckoutUrl: externalJsUrl,
                paymentSenseJWT: jwt,
                paymentSenseJWT_ALT: jwt, // Backup field name
                paymentSenseMerchantUrl: merchantUrl,
                paymentSenseMerchantUrl_ALT: merchantUrl, // Backup field name
                paymentSense_ApiUrl: externalJsUrl, // Alternative field name
                currency: 'GBP',
                
                // Store full response for debugging
                venposData: paymentData
              };

              return {
                isError: false,
                data: sessionData
              };
            }
          }
        } else {

        }
      } catch (e) {

      }

      // 🎯 Fallback: Use paymentsense-token.php to get client.js script URL
      // Note: The order may have already been created by POST /Payment above


      try {
        const minorUnits = Math.round(Number(amount) * 100);
        const tokenBase = (typeof window !== 'undefined' && window.location && window.location.hostname === 'localhost')
          ? 'http://localhost:8888/Website/front-end/public'
          : 'https://belvoircastle.com';

        const tokenResp = await fetch(`${tokenBase}/api/paymentsense-token.php`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            orderId: basketId,
            amountMinorUnits: minorUnits,
            currencyCode: 826,
            returnUrl: `${window.location.origin}/booking-journey`,
            cancelUrl: `${window.location.origin}/booking-journey`
          })
        });
        const tokenJson = await tokenResp.json();

        if (tokenResp.ok && tokenJson && tokenJson.ok && tokenJson.jwt && tokenJson.scriptUrl) {


          const fallbackSessionData = {
            orderId: basketId,
            amount: amount,
            paymentProcessor: 1, // Payment Sense
            requiresRedirect: false,
            completed: false,
            paymentSenseCheckoutUrl: tokenJson.scriptUrl,
            paymentSenseJWT: tokenJson.jwt,
            paymentSenseMerchantUrl: tokenJson.merchantUrl,
            currency: 'GBP'
          };

          return {
            isError: false,
            data: fallbackSessionData
          };
        } else {

        }
      } catch (psErr) {

        return { isError: true, error: 'Payment Sense token request failed', data: null };
      }
    } catch (error) {

      return {
        isError: true,
        error: error.message || 'Failed to create payment request',
        data: null
      };
    }
  },

  // Poll payment status following Vennersys documentation flow
  async getPaymentStatus(locationId, orderId) {
    try {

      // Try different payment status endpoints based on documentation pattern
      const statusEndpoints = [
        `${VENPOS_API_BASE}/Payment/Get?locationId=${locationId}&orderId=${orderId}`,
        `${VENPOS_API_BASE}/SharedPayment/Get?locationId=${locationId}&orderId=${orderId}`,
        `${VENPOS_API_BASE}/Checkout/GetStatus?locationId=${locationId}&orderId=${orderId}`,
        `${VENPOS_API_BASE}/Payment/Status?locationId=${locationId}&orderId=${orderId}`
      ];

      let result = null;
      for (const endpoint of statusEndpoints) {
        try {

          result = await makeApiRequest(endpoint, { method: 'GET' });
          
          if (!result.isError) {

            break;
          }
        } catch (e) {

          continue;
        }
      }

      if (!result || result.isError) {
        return {
          isError: true,
          data: null,
          error: result?.error || 'Failed to get payment status from any endpoint'
        };
      }

      const paymentData = result.data || result;
      
      // Parse SharedPaymentViewModel status response
      return {
        isError: false,
        data: {
          status: paymentData.completed ? 'completed' : 'pending',
          orderId: paymentData.orderId || paymentData.OrderId,
          paymentToken: paymentData.paymentToken || paymentData.PaymentToken,
          transactionId: paymentData.transactionId,
          amount: paymentData.amount || paymentData.Amount,
          currency: paymentData.currency || 'GBP',
          completed: paymentData.completed || paymentData.Completed || false,
          requiresRedirect: paymentData.requiresRedirect || paymentData.RequiresRedirect
        }
      };
    } catch (error) {

      return {
        isError: true,
        data: null,
        error: 'Failed to check payment status'
      };
    }
  },

  // Complete payment after Payment Sense processing
  async completePayment(locationId, basketId, paymentToken, paymentResult, pageId) {
    try {
      // Check if this is a direct debit payment
      const isDirectDebit = paymentResult?.isDirectDebit || paymentResult?.paymentMethod === 'DirectDebit';
      
      // Build redirect URL back into our app (avoid vendor confirmation page)
      // For localhost, use production URL since Venpos doesn't accept localhost domains
      const redirectUrl = IS_DEVELOPMENT 
        ? 'https://belvoircastle.com/booking-journey'
        : `${window.location.origin}/booking-journey`;
      
      // Venpos uses a GET request to /Payment to register the completed payment
      const paymentUrl = new URL(`${VENPOS_API_BASE}/Payment`);
      paymentUrl.searchParams.append('paymentToken', paymentToken);
      paymentUrl.searchParams.append('paymentProcessorTypeId', isDirectDebit ? '1' : '3'); // 1 = GoCardless, 3 = Payment Sense
      paymentUrl.searchParams.append('locationId', locationId);
      paymentUrl.searchParams.append('paymentType', isDirectDebit ? '1' : '0'); // 1 = Direct Debit, 0 = standard payment
      paymentUrl.searchParams.append('orderId', basketId);
      paymentUrl.searchParams.append('ddOnCompletionredirectUrl', redirectUrl);


      let result = await makeApiRequest(
        paymentUrl.toString(),
        {
          method: 'GET',
          headers: {
            'Accept': 'application/json, text/javascript, */*; q=0.01',
            'X-Requested-With': 'XMLHttpRequest'
          }
        }
      );

      // If direct call fails (common on localhost due to CORS or Venpos error-page redirect), use proxy fallback
      if (result.isError || (result?.data?.isError && typeof result.data.isError === 'boolean')) {
        try {
          const proxyBase = (typeof window !== 'undefined' && window.location && window.location.hostname === 'localhost')
            ? 'http://localhost:8888/Website/front-end/public'
            : 'https://belvoircastle.com';
          const proxyUrl = `${proxyBase}/api/venpos-finalize.php`;

          const proxyResp = await fetch(proxyUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({
              action: 'completePayment',
              locationId,
              paymentToken,
              orderId: basketId,
              paymentType: isDirectDebit ? '1' : '0', // 1 = Direct Debit, 0 = standard payment
              redirectUrl,
              mainWebPageId: pageId,
              startId: pageId
            })
          });

          if (!proxyResp.ok) {
            const errText = await proxyResp.text();

            result = { isError: true, data: null, error: `Proxy HTTP ${proxyResp.status}: ${errText}` };
          } else {
            const proxyJson = await proxyResp.json();
            result = proxyJson;

          }
        } catch (proxyErr) {

        }
      }

      const completionData = result.data || result;
      
      console.log('💳 [completePayment] completionData:', completionData);
      console.log('💳 [completePayment] result:', result);
      
      // Extract booking reference from GET /Payment response (this is how the working version gets it)
      const bookingReference = completionData?.bookingReference || result?.bookingReference;
      
      console.log('💳 [completePayment] Extracted bookingReference:', bookingReference);
      console.log('💳 [completePayment] completionData?.bookingReference:', completionData?.bookingReference);
      console.log('💳 [completePayment] result?.bookingReference:', result?.bookingReference);
      
      if (bookingReference) {
        console.log('✅ [completePayment] Found booking reference:', bookingReference);
      } else if (!result.isError) {
        console.warn('⚠️ [completePayment] No booking reference in response, but no error');
      } else {
        console.error('❌ [completePayment] Error in result:', result.error);
      }
      
      // Return success response (payment was processed by Payment Sense)
      const actualOrderId = paymentResult?.orderId || basketId;
      console.log('💳 [completePayment] Returning response with orderId:', actualOrderId);
      console.log('💳 [completePayment] Returning response with bookingReference:', bookingReference);
      
      return {
        isError: false,
        data: {
          paymentSuccessful: true,
          transactionId: completionData?.transactionId || paymentResult?.transactionId || paymentResult?.authCode,
          bookingReference: bookingReference, // From GET /Payment response
          orderId: completionData?.orderId || actualOrderId,
          webTransHeaderID: completionData?.webTransHeaderID || completionData?.WebTransHeaderID || actualOrderId,
          amount: completionData?.amount || paymentResult?.amount,
          currency: completionData?.currency || 'GBP',
          paymentMethod: 'PaymentSense',
          status: 'Success',
          getPaymentSucceeded: !result.isError
        }
      };
    } catch (error) {

      // Return success anyway since Payment Sense already completed

      return {
        isError: false,
        data: {
          paymentSuccessful: true,
          transactionId: paymentResult?.authCode || paymentResult?.transactionId,
          orderId: basketId,
          amount: paymentResult?.amount || 0,
          currency: 'GBP',
          paymentMethod: 'PaymentSense',
          status: 'Success',
          note: 'Payment completed by Payment Sense (Venpos notification error)'
        }
      };
    }
  },

  // Load basket using the correct Basket/Load endpoint with proper payload structure
  async loadBasket(locationId, payload, familySaverCodeFromContext = null) {
    try {
      const url = new URL(`${VENPOS_API_BASE}/Basket/Load`);
      url.searchParams.append('locationId', locationId);

      // Use provided IDs or generate new ones only if not provided
      // Prioritize existing IDs over generation
      const customerId = payload?.customerId || null; // Don't generate customer ID - use registered one
      const orderId = payload?.orderId || generateGuid();

      // Filter items with quantity > 0 - preserve ALL item data including upsell metadata
      const validItems = (payload?.items || []).filter(item => item.quantity > 0);

      validItems.forEach((item, idx) => {

      });

      // Only use explicitly provided discounts (and family saver code if provided by caller)
      const discounts = [...(payload?.discounts || [])];
      if (familySaverCodeFromContext) {
        discounts.push(familySaverCodeFromContext);

      }

      // Extract modifiers from items and format for root-level array
      // Format: [{webSaleItemId, productModifierId, productModifierTextId, value, index}]
      const modifiersArray = [];
      const modifierIndexByItemForLoad = new Map(); // Track index per webSaleItemId
      validItems.forEach(item => {
        if (item.modifierSelection) {
          const currentIndex = modifierIndexByItemForLoad.get(item.webSaleItemId) || 0;
          modifierIndexByItemForLoad.set(item.webSaleItemId, currentIndex + 1);
          
          modifiersArray.push({
            webSaleItemId: item.webSaleItemId,
            productModifierId: item.modifierSelection.modifierId,
            productModifierTextId: item.modifierSelection.modifierTextId,
            value: "1",
            index: currentIndex // Use per-item index instead of global index
          });
        }
      });

      const body = {
        items: validItems.map(item => {
          // Remove ONLY questionAnswers and modifierSelection - preserve everything else!
          const { questionAnswers, modifierSelection, ...cleanItem } = item;
          
          // CRITICAL: Don't reconstruct - just clean and return the item AS-IS
          // This preserves pageType: "EventTemplate", correct pageId, eventId for upsells
          return {
            pageType: cleanItem.pageType || 'WebPage',  // Preserve EventTemplate for upsells
            pageId: cleanItem.pageId,  // Preserve upsell's own pageId
            fixedDonationAmount: cleanItem.fixedDonationAmount ?? 0,
            giftAidEligible: cleanItem.giftAidEligible ?? false,
            webSaleItemId: cleanItem.webSaleItemId,
            quantity: cleanItem.quantity,
            webSaleItemTypeID: cleanItem.webSaleItemTypeID,
            name: cleanItem.name,
            isSysGift: cleanItem.isSysGift ?? false,
            isSysMemberCard: cleanItem.isSysMemberCard ?? false,
            selectedDate: cleanItem.selectedDate,
            eventId: cleanItem.eventId,  // Preserve upsell's own eventId
            reservationExpiryDateTime: cleanItem.reservationExpiryDateTime,
            eventCapacityUsageId: cleanItem.eventCapacityUsageId,  // Preserve upsell's own capacity
            // Include parentWebSaleItemId for upsells (from working example)
            ...(cleanItem.parentWebSaleItemId && { parentWebSaleItemId: cleanItem.parentWebSaleItemId })
          };
        }),
        discounts: discounts,
        giftAidSelected: payload?.giftAidSelected ?? false,
        pageId: payload?.pageId,
        modifiers: modifiersArray, // Include modifiers at root level as per working example
        discountsFromMembership: payload?.discountsFromMembership || [],
        orderId: orderId
      };

      // Only include customerId if we have one (registered customer)
      if (customerId) {
        body.customerId = customerId;

      } else {

      }

      // Don't include customer object in payload - only customerId is needed
      // Modifiers are included at root level as per working example

      body.items.forEach((item, idx) => {

      });


      const result = await makeApiRequest(url.toString(), {
        method: 'POST',
        body: JSON.stringify(body)
      });

      // Log basket validation status and any error messages
      if (result.data?.basketIsValid === false) {

      }

      if (result.isError) {

        throw new Error(`Basket/Load failed: ${result.error}`);
      }

      // Check if family saver discount was applied (from provided discounts)
      const providedFamilySaver = (payload?.discounts || []).find(code => typeof code === 'string' && code.startsWith('FS'))
        || (familySaverCodeFromContext || null);
      if (providedFamilySaver && result.data?.discounts) {
        const appliedDiscount = result.data.discounts.find(d => d.code === providedFamilySaver);
        if (appliedDiscount) {


        }
      }

      // Ensure the response includes customerId and orderId if they're not already present
      // These should be in the response, but let's make sure
      if (result.data && !result.data.customerId) {
        result.data.customerId = payload?.customerId;
      }
      if (result.data && !result.data.orderId) {
        result.data.orderId = payload?.orderId;
      }

      return result;

    } catch (error) {


      // Alert user about authentication requirement if it's a network/auth error
      if (error.message && error.message.includes('Authentication')) {


      }
      
      // Additional guidance for development CORS issues
      if (IS_DEVELOPMENT) {


      }
      
      // Provide fallback structure matching expected Venpos format
      const items = payload?.items || [];
      return {
        isError: false,
        information: null,
        data: {
          totalPrice: items.reduce((sum, item) => sum + (25.00 * item.quantity), 0),
          totalDiscountedPrice: items.reduce((sum, item) => sum + (25.00 * item.quantity), 0),
          finalPrice: items.reduce((sum, item) => sum + (25.00 * item.quantity), 0),
          groups: items.length > 0 ? [{
            name: "Christmas at the Castle 2025",
            selectedDate: items[0]?.selectedDate ? `${items[0].selectedDate}T16:00:00` : "2025-11-29T16:00:00",
            basketId: `fallback-${Date.now()}`,
            items: items.map((item, index) => ({
              webSaleItemId: item.webSaleItemId,
              name: `Christmas Ticket ${item.webSaleItemId?.substring(0, 8)}`,
              price: 25.00,
              totalPrice: 25.00 * item.quantity,
              discountedAmount: 25.00 * item.quantity,
              quantity: item.quantity,
              pageId: item.pageId,
              pageType: item.pageType || "WebPage",
              selectedDate: item.selectedDate ? `${item.selectedDate}T16:00:00` : "2025-11-29T16:00:00",
              webSaleItemTypeID: item.webSaleItemTypeID,
              index: index,
              instances: Array.from({length: item.quantity}, (_, i) => ({
                index: i,
                itemModifiers: [],
                webTransItemId: "00000000-0000-0000-0000-000000000000"
              }))
            }))
          }] : [],
          discounts: [],
          basketIsValid: true,
          orderId: `fallback-order-${Date.now()}`,
          availableAtType: "Online"
        }
      };
    }
  },

  async getBookingConfirmation(bookingReference, locationId) {
    try {

      const response = await fetch(
        `${VENPOS_API_BASE}/Booking/Confirmation?bookingReference=${bookingReference}&locationId=${locationId}`,
        {
          method: 'GET',
          headers: {
            'Accept': 'application/json, text/javascript, */*; q=0.01',
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
  }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch booking confirmation');
      }

      const data = await response.json();

      return data;
    } catch (error) {

      throw error;
    }
  },

  async getUpsells(locationId, mainWebPageId, upsellType = 'Event', dateSelectorType = 'OnScreenCalendar', items) {
    try {

      // Match the exact payload structure from the working example
      const payload = {
        locationId: parseInt(locationId),
        mainWebPageId,
        upsellType,
        dateSelectorType,
        items
      };

      const response = await fetch(
        `${VENPOS_API_BASE}/Upsell/ForBasket?locationId=${locationId}&mainWebPageId=${mainWebPageId}&upsellType=${upsellType}&dateSelectorType=${dateSelectorType}`,
        {
          method: 'POST',
          headers: {
            'Accept': 'application/json, text/javascript, */*; q=0.01',
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify(payload),
          mode: 'cors',
          credentials: 'omit' // Omit credentials to avoid CORS issues
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to fetch upsells: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();

      if (data.isError) {
        throw new Error(data.information || 'Error fetching upsells');
      }

      return data;
    } catch (error) {

      // In development, provide sample upsells for testing the UI
      if (IS_DEVELOPMENT) {

        return {
          isError: false,
          information: null,
          data: {
            pageDescription: 'Sample upsells for development testing',
            upsells: [
              {
                id: "677ed884-a50a-4f7d-896d-fd1f1f8ca787",
                parentWebSaleItemId: "sample-item-id",
                parentProductId: "sample-product-id",
                pageType: "EventTemplate",
                name: "Afternoon Tea in The Aviary Tearoom",
                description: "Enjoy a traditional afternoon tea experience in our beautiful tearoom",
                imageUrl: "https://online1.venpos.net/Site/Images/Resource/977978_Afternoon-tea-tile2.jpg",
                price: 0,
                minPurchaseQty: 0,
                maxPurchaseQty: 0,
                mandatory: false,
                required: false,
                primaryColour: "#FFFFFF",
                secondaryColour: "#FFFFFF",
                quantityAvailable: 150,
                showDescription: false,
                webSaleItemTypeID: 0,
                isSysMemberCard: false,
                facilityModeID: 0
              },
              {
                id: "4ecb9f5c-2d83-4f52-9aae-e2344dbbd177",
                parentWebSaleItemId: "sample-item-id-2",
                parentProductId: "sample-product-id-2",
                pageType: "EventTemplate",
                name: "Animal Park",
                description: "Explore our beautiful animal park and meet the friendly creatures",
                imageUrl: "https://online1.venpos.net/Site/Images/Resource/977978_animalUpsell.jpg",
                price: 0,
                minPurchaseQty: 0,
                maxPurchaseQty: 0,
                mandatory: false,
                required: false,
                primaryColour: "#FFFFFF",
                secondaryColour: "#FFFFFF",
                quantityAvailable: 600,
                showDescription: false,
                webSaleItemTypeID: 0,
                isSysMemberCard: false,
                facilityModeID: 0
              },
              {
                id: "8161de7c-0d6a-4e29-8f16-035dc66884bf",
                parentWebSaleItemId: "sample-item-id-3",
                parentProductId: "sample-product-id-3",
                pageType: "EventTemplate",
                name: "Garden Bus Tour",
                description: "Take a scenic bus tour around our magnificent gardens and estate",
                imageUrl: "https://online1.venpos.net/Site/Images/Resource/977978_Bus.jpg",
                price: 0,
                minPurchaseQty: 0,
                maxPurchaseQty: 0,
                mandatory: false,
                required: false,
                primaryColour: "#FFFFFF",
                secondaryColour: "#FFFFFF",
                quantityAvailable: 30,
                showDescription: false,
                webSaleItemTypeID: 0,
                isSysMemberCard: false,
                facilityModeID: 0
              },
              {
                id: "90367197-45af-4991-a195-e66e248121dd",
                parentWebSaleItemId: "sample-item-id-4",
                parentProductId: "sample-product-id-4",
                pageType: "EventTemplate",
                name: "Castle Guided Tour",
                description: "Join our expert guides for an in-depth tour of the castle's rich history",
                imageUrl: "https://online1.venpos.net/Site/Images/Resource/977978_Belvoir_Castle.JPG",
                price: 0,
                minPurchaseQty: 0,
                maxPurchaseQty: 0,
                mandatory: false,
                required: false,
                primaryColour: "#FFFFFF",
                secondaryColour: "#FFFFFF",
                quantityAvailable: 30,
                showDescription: false,
                webSaleItemTypeID: 0,
                isSysMemberCard: false,
                facilityModeID: 0
              }
            ]
          }
        };
      }
      
      // Return empty upsells on error to allow booking to continue
      return {
        isError: false,
        information: null,
        data: {
          pageDescription: '',
          upsells: []
        }
      };
    }
  },

  async getUpsellItemDetails(locationId, upsellId, pageType = 'EventTemplate', dateSelectorType = 'OnScreenCalendar', selectedDate = '') {
    try {

      const url = `${VENPOS_API_BASE}/WebSaleItem/Get?locationId=${locationId}&id=${upsellId}&pageType=${pageType}&dateSelectorType=${dateSelectorType}&selectedDate=${selectedDate}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Accept': 'application/json, text/javascript, */*; q=0.01',
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        mode: 'cors',
        credentials: 'omit'
      });

      if (!response.ok) {
        const errorText = await response.text();

        throw new Error(`Failed to fetch upsell item details: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();

      if (data.isError) {
        throw new Error(data.information || 'Error fetching upsell item details');
      }

      return data;
    } catch (error) {

      // In development, provide sample upsell item details for testing
      if (IS_DEVELOPMENT) {

        return {
          isError: false,
          information: null,
          data: {
            title: upsellId === "677ed884-a50a-4f7d-896d-fd1f1f8ca787" ? "Afternoon Tea in The Aviary Tearoom" : "Sample Experience",
            description: "Enjoy a traditional afternoon tea experience",
            textAdditionalInfo: "",
            textTop: "",
            textBottom: "",
            isVisible: true,
            showProductThumbnails: false,
            minimumTotalItems: 0,
            maximumTotalItems: 0,
            isCateringJourney: false,
            cateringMode: "NotSetup",
            waitTimeMessage: null,
            pauseOrders: false,
            pauseOrdersMessage: null,
            cateringCustomTextField1: "",
            cateringCustomTextField2: "",
            cateringFacilityId: null,
            useResourceManagement: false,
            webSaleItems: [
              {
                webSaleItemId: "sample-adult-" + Date.now(),
                name: "Adult Experience",
                description: "Adult Experience",
                price: 35.0000,
                quantity: 0,
                minPurchaseQty: 1,
                maxPurchaseQty: 999,
                imageUrl: "",
                maximumHeadCount: 0,
                isValidAnySaleDate: false,
                isValidAnyVisitDate: false,
                leadTimePeriod: 2,
                giftAidEligible: false,
                leadTimeMode: "Hours",
                webSaleItemDates: [],
                webSaleItemTypeID: 2,
                isSysLocked: false,
                isSysUnlocked: false,
                isSysGift: false,
                isSysMemberCard: false,
                fixedDonationAmount: 0.0000,
                hasImage: false,
                hasThumbnail: false
              },
              {
                webSaleItemId: "sample-child-" + Date.now(),
                name: "Child Experience",
                description: "Child Experience",
                price: 15.0000,
                quantity: 0,
                minPurchaseQty: 1,
                maxPurchaseQty: 999,
                imageUrl: "",
                maximumHeadCount: 0,
                isValidAnySaleDate: false,
                isValidAnyVisitDate: false,
                leadTimePeriod: 2,
                giftAidEligible: false,
                leadTimeMode: "Hours",
                webSaleItemDates: [],
                webSaleItemTypeID: 2,
                isSysLocked: false,
                isSysUnlocked: false,
                isSysGift: false,
                isSysMemberCard: false,
                fixedDonationAmount: 0.0000,
                hasImage: false,
                hasThumbnail: false
              }
            ],
            useCustomerAccount: true
          }
        };
      }
      
      throw error;
    }
  },

  async getUpsellEventTimes(locationId, mainWebPageId, upsellId, pageType = 'EventTemplate', date, items) {
    try {

      const url = `${VENPOS_API_BASE}/Event/GetTimes?locationId=${locationId}&mainWebPageId=${mainWebPageId}&id=${upsellId}&pageType=${pageType}&date=${encodeURIComponent(date)}`;

      const payload = {
        locationId: parseInt(locationId),
        mainWebPageId,
        id: upsellId,
        pageType,
        date,
        items
      };

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Accept': 'application/json, text/javascript, */*; q=0.01',
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        mode: 'cors',
        credentials: 'omit',
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorText = await response.text();

        throw new Error(`Failed to fetch upsell event times: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();

      if (data.isError) {
        throw new Error(data.information || 'Error fetching upsell event times');
      }

      return data;
    } catch (error) {

      // In development, provide sample event times for testing
      if (IS_DEVELOPMENT) {

        return {
          isError: false,
          information: null,
          data: {
            timeSelectorType: 'List',
            timeSelectorText: '',
            events: [
              {
                eventId: 'sample-event-1',
                eventCapacityUsageId: 'sample-capacity-1', // CRITICAL: Include for proper basket handling
                description: 'Sample Experience',
                startDateTime: date + 'T12:00:00',
                quantityAvailable: 30,
                bookingMinimumTicketCount: null,
                bookingMaximumTicketCount: null,
                useCapacityHeadCount: false,
                errorMessage: null,
                webSaleItems: items.map(item => item.webSaleItemId),
                acceptDeposit: false,
                showAvailabilityTypeID: null,
                duration: 60,
                priceFrom: 35.0000,
                useResourceManagement: false,
                hasUnlimitedCapacity: true
              },
              {
                eventId: 'sample-event-2',
                eventCapacityUsageId: 'sample-capacity-2', // CRITICAL: Include for proper basket handling
                description: 'Sample Experience',
                startDateTime: date + 'T14:00:00',
                quantityAvailable: 30,
                bookingMinimumTicketCount: null,
                bookingMaximumTicketCount: null,
                useCapacityHeadCount: false,
                errorMessage: null,
                webSaleItems: items.map(item => item.webSaleItemId),
                acceptDeposit: false,
                showAvailabilityTypeID: null,
                duration: 60,
                priceFrom: 35.0000,
                useResourceManagement: false,
                hasUnlimitedCapacity: true
              }
            ]
          }
        };
      }
      
      throw error;
    }
  },

  async getEventQuestions(locationId, webSaleItems, eventIds) {
    try {

      const url = `${VENPOS_API_BASE}/Event/GetEventQuestionsForBasketItems?locationId=${locationId}`;

      const payload = {
        webSaleItems,
        eventIds
      };


      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Accept': 'application/json, text/javascript, */*; q=0.01',
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        mode: 'cors',
        credentials: 'omit',
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorText = await response.text();

        throw new Error(`Failed to fetch event questions: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();


      // DEEP DIVE: Show actual eventQuestions structure
      if (data.data) {


        if (data.data.eventQuestions) {


          // If it's an object, show its contents
          if (!Array.isArray(data.data.eventQuestions)) {

            // Try to find questions in nested structure
            Object.keys(data.data.eventQuestions).forEach(key => {

            });
          }
        }
        
        // Check for product-based questions (like Santa's Grotto)
        if (data.data.eventProductQuestions) {


          // Convert product questions to standard format
          const productQuestions = [];
          Object.keys(data.data.eventProductQuestions).forEach(productId => {
            const productQs = data.data.eventProductQuestions[productId];

            if (Array.isArray(productQs)) {
              productQuestions.push(...productQs);
            }
          });

          // Merge product questions into eventQuestions array
          if (productQuestions.length > 0) {
            if (!data.data.eventQuestions) {
              data.data.eventQuestions = [];
            }
            data.data.eventQuestions = [...data.data.eventQuestions, ...productQuestions];

          }
        }
      }
      
      if (data.isError) {
        throw new Error(data.information || 'Error fetching event questions');
      }

      return data;
    } catch (error) {

      // In development, provide sample event questions for testing
      if (IS_DEVELOPMENT) {

        return {
          isError: false,
          information: null,
          data: {
            eventQuestions: [
              {
                configurationId: 'sample-config-' + Date.now(),
                questionId: 'sample-question-' + Date.now(),
                question: 'Any Allergies?',
                productId: null,
                productName: null,
                isRequired: false,
                order: 1,
                index: 0,
                title: null,
                webSaleItemId: null,
                answerType: 'Text',
                controlType: 'TextBox',
                validationRegex: null,
                selectedAnswer: '',
                answers: [],
                eventId: eventIds && eventIds.length > 0 ? eventIds[0] : 'sample-event-id'
              }
            ],
            eventProductQuestions: {}
          }
        };
      }
      
      throw error;
    }
  },

  async getEventParticipantDetails(locationId, items) {
    try {

      const url = `${VENPOS_API_BASE}/ParticipantDetails/GetEventParticipantDetails?locationId=${locationId}`;

      const payload = {
        items: items.map(item => ({
          pageType: item.pageType,
          pageId: item.pageId,
          fixedDonationAmount: item.fixedDonationAmount || 0,
          giftAidEligible: item.giftAidEligible || false,
          webSaleItemId: item.webSaleItemId,
          quantity: item.quantity,
          webSaleItemTypeID: item.webSaleItemTypeID,
          name: item.name,
          isSysGift: item.isSysGift || false,
          isSysMemberCard: item.isSysMemberCard || false,
          selectedDate: item.selectedDate,
          eventId: item.eventId,
          eventCapacityUsageId: item.eventCapacityUsageId,
          reservationExpiryDateTime: item.reservationExpiryDateTime,
          ...(item.parentWebSaleItemId && { parentWebSaleItemId: item.parentWebSaleItemId })
        }))
      };


      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Accept': 'application/json, text/javascript, */*; q=0.01',
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        mode: 'cors',
        credentials: 'omit',
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorText = await response.text();

        throw new Error(`Failed to fetch participant details: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();


      if (data.isError) {
        throw new Error(data.information || 'Error fetching participant details');
      }

      return data;
    } catch (error) {

      throw error;
    }
  },

  async getProductModifiers(locationId, items) {
    try {

      const url = `${VENPOS_API_BASE}/ProductModifier/Get?locationId=${locationId}`;

      const payload = {
        locationId: parseInt(locationId),
        items
      };

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Accept': 'application/json, text/javascript, */*; q=0.01',
          'Accept-Encoding': 'gzip, deflate, br, zstd',
          'Accept-Language': 'en-GB,en-US;q=0.9,en;q=0.8',
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        mode: 'cors',
        credentials: 'omit',
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorText = await response.text();

        throw new Error(`Failed to fetch product modifiers: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();

      if (data.isError) {
        throw new Error(data.information || 'Error fetching product modifiers');
      }

      return data;
    } catch (error) {

      throw error;
    }
  },

  /**
   * Add upsell items to basket with proper event capacity reservation
   * Similar to addEventCapacityUsages but specifically for upsell items
   * 
   * @param {string} locationId - Venpos location ID
   * @param {string} pageId - Main page/event ID
   * @param {Array} upsellItems - Array of upsell items to add
   * @param {string} orderId - Current basket/order ID
   * @param {string|null} customerId - Registered customer ID
   * @param {Array} discounts - Array of discount codes
   * @param {Array} existingItems - ALL existing basket items (main + previously added upsells)
   * @param {Array} formattedQuestions - Formatted event questions with answers
   */
  async addUpsellItemsToBasket(locationId, pageId, upsellItems, orderId, customerId = null, discounts = [], existingItems = [], formattedQuestions = []) {
    try {

      // Prepare items with proper event metadata
      const enrichedItems = upsellItems.map(item => {
        // CRITICAL: Extract event metadata from selectedEventTime if available
        // The eventId should already be set from the time slot selection in UpsellDetailModal
        const eventId = item.eventId || item.selectedEventTime?.eventId || null;
        const eventCapacityUsageId = item.eventCapacityUsageId || item.selectedEventTime?.eventCapacityUsageId || null;
        
        // Format the selectedDate properly (should include time from selectedEventTime)
        let selectedDate = item.selectedDate;
        if (!selectedDate && item.selectedEventTime?.startDateTime) {
          selectedDate = item.selectedEventTime.startDateTime;
        }

        // Validate that we have the critical event metadata
        if (!eventId) {

        }

        return {
          pageType: item.pageType || 'EventTemplate', // Upsells typically use EventTemplate
          pageId: item.pageId || item.parentUpsellId || pageId, // Use upsell's own pageId (already set in UpsellDetailModal)
          webSaleItemId: item.webSaleItemId,
          webSaleItemTypeID: item.webSaleItemTypeID || 2,
          quantity: item.quantity,
          selectedDate: selectedDate,
          fixedDonationAmount: 0,
          name: item.name || '',
          giftAidEligible: false,
          isSysGift: false,
          isSysMemberCard: false,
          reservationExpiryDateTime: item.reservationExpiryDateTime || new Date(Date.now() + 30 * 60 * 1000).toISOString(),
          // CRITICAL: Include event metadata - eventId should already be set from time slot selection
          eventId: eventId,
          eventCapacityUsageId: eventCapacityUsageId, // May be null before capacity reservation
          // Link to parent item (from working example: parentWebSaleItemId)
          parentWebSaleItemId: item.parentWebSaleItemId || null,
          // Store questions and modifiers for later extraction
          // These will be extracted by loadBasket into separate arrays
          questionAnswers: item.questionAnswers || {},
          modifierSelection: item.modifierSelection || null
        };
      });

      // Group upsell items by product + event + selectedDate so capacity is reserved in one line with correct quantity
      const capacityGroupsMap = new Map(); // key -> { representative, indices[], totalQty }
      const capacityGroups = [];
      enrichedItems.forEach((item, idx) => {
        const key = `${item.webSaleItemId}|${item.eventId}|${item.selectedDate}`;
        if (!capacityGroupsMap.has(key)) {
          const rep = { ...item, quantity: 0 };
          const group = { key, representative: rep, indices: [], totalQty: 0 };
          capacityGroupsMap.set(key, group);
          capacityGroups.push(group);
        }
        const group = capacityGroupsMap.get(key);
        group.indices.push(idx);
        group.totalQty += (item.quantity || 1);
      });

      const capacityRequestItems = capacityGroups.map(g => ({
        ...g.representative,
        quantity: g.totalQty
      }));

      // CRITICAL: For upsells, use the upsell's own pageId, not the main event's pageId
      // Each upsell item already has its correct pageId set in enrichedItems
      // We'll use the first upsell item's pageId for the Event/AddEventCapacityUsages call
      const upsellPageId = enrichedItems[0]?.pageId || pageId;

      // Use addEventCapacityUsages to reserve capacity for the new upsell items
      // This handles the complete flow of reserving event capacity
      const capacityResponse = await this.addEventCapacityUsages(
        locationId,
        upsellPageId, // Use upsell's pageId, not main event's pageId
        capacityRequestItems,
        orderId,
        discounts
      );

      if (capacityResponse.isError) {
        throw new Error(capacityResponse.error || 'Failed to add event capacity for upsells');
      }

      // CRITICAL FIX: Extract event capacity usage IDs from response
      // Response can be either capacityResponse.data (array) or capacityResponse.data.items (array)
      let updatedUpsellItems = [...enrichedItems];
      const responseItems = Array.isArray(capacityResponse.data) 
        ? capacityResponse.data 
        : (capacityResponse.data?.items || []);


      if (responseItems && responseItems.length > 0) {


        // Map response per capacity group, then fan out to all enriched items in that group
        capacityGroups.forEach((group, groupIndex) => {
          const responseItem = responseItems[groupIndex];
          if (!responseItem) {

            return;
          }
          group.indices.forEach(enrichedIdx => {
            const item = updatedUpsellItems[enrichedIdx];
            updatedUpsellItems[enrichedIdx] = {
              ...item,
              eventCapacityUsageId: responseItem.eventCapacityUsageId || item.eventCapacityUsageId,
              eventId: responseItem.eventId || item.eventId,
              reservationExpiryDateTime: responseItem.reservationExpiryDateTime || item.reservationExpiryDateTime
            };
          });
        });


        updatedUpsellItems.forEach((item, idx) => {

        });
      } else {


      }

      // CRITICAL FIX: Combine existing items with new upsell items for basket refresh
      // This ensures the basket contains ALL items (main tickets + all upsells)
      const allItems = [...existingItems, ...updatedUpsellItems];

      // Log all items for debugging

      allItems.forEach((item, idx) => {

      });

      // Extract modifiers from all items (existing + new) for root-level array
      const modifiersArray = [];
      const modifierIndexByItemForUpsell = new Map(); // Track index per webSaleItemId
      allItems.forEach(item => {
        if (item.modifierSelection) {
          const currentIndex = modifierIndexByItemForUpsell.get(item.webSaleItemId) || 0;
          modifierIndexByItemForUpsell.set(item.webSaleItemId, currentIndex + 1);
          
          modifiersArray.push({
            webSaleItemId: item.webSaleItemId,
            productModifierId: item.modifierSelection.modifierId,
            productModifierTextId: item.modifierSelection.modifierTextId,
            value: "1",
            index: currentIndex // Use per-item index instead of global index
          });
        }
      });

      // Build the complete basket payload matching working example structure
      // Include modifiers at root level as per working example
      const basketPayload = {
        items: allItems,
        discounts: discounts || [],
        giftAidSelected: false,
        pageId: pageId, // Main event pageId
        modifiers: modifiersArray, // Include modifiers at root level
        discountsFromMembership: [],
        orderId: orderId
      };
      
      // Only include customerId if we have one
      if (customerId) {
        basketPayload.customerId = customerId;
      }

      const loadResponse = await this.loadBasket(locationId, basketPayload, null);

      if (loadResponse.isError) {

        // Return success anyway since capacity was reserved
        return {
          isError: false,
          data: {
            items: updatedUpsellItems,
            capacityReserved: true,
            basketRefreshFailed: true
          }
        };
      }

      // If there are formatted questions, call Basket/Validate to persist them
      if (formattedQuestions.length > 0) {

        try {
          const validatePayload = {
            items: allItems,
            questions: formattedQuestions,
            discounts: discounts || [],
            giftAidSelected: false,
            pageId: pageId,
            discountsFromMembership: [],
            orderId: orderId
          };
          
          // Only include customerId if we have one
          if (customerId) {
            validatePayload.customerId = customerId;
          }
          
          await this.validateBasketWithQuestions(
            locationId,
            pageId,
            'WebPage', // Main event page type
            validatePayload
          );

        } catch (validateError) {

          // Don't fail the entire operation if question validation fails
        }
      }
      
      // If there are modifiers, call Basket/ValidateModifiers to persist them
      if (modifiersArray.length > 0) {

        try {
          const validateModifiersPayload = {
            items: allItems.map(item => {
              const { questionAnswers, modifierSelection, ...cleanItem } = item;
              return cleanItem;
            }),
            modifiers: modifiersArray
          };
          
          await this.validateBasketWithModifiers(
            locationId,
            validateModifiersPayload
          );

        } catch (validateError) {

          // Don't fail the entire operation if modifier validation fails
        }
      }
      
      return {
        isError: false,
        data: {
          items: updatedUpsellItems,
          basketDetails: loadResponse.data,
          capacityReserved: true,
          formattedQuestions: formattedQuestions // Return questions so they can be used in CreateFreeOrder
        }
      };

    } catch (error) {

      return {
        isError: true,
        error: error.message || 'Failed to add upsell items to basket'
      };
    }
  },

  /**
   * Get membership details for selected membership items
   * @param {string} locationId - Location ID
   * @param {Array} items - Array of membership items with pageType, pageId, webSaleItemId, quantity, etc.
   * @returns {Promise<Object>} Membership details response
   */
  async getMembershipDetails(locationId, items) {
    try {
      const url = `${VENPOS_API_BASE}/Membership/GetMembershipDetails?locationId=${locationId}`;

      const payload = {
        items: items.map(item => ({
          pageType: item.pageType || 'WebPage',
          pageId: item.pageId,
          fixedDonationAmount: item.fixedDonationAmount || 0,
          giftAidEligible: item.giftAidEligible || false,
          webSaleItemId: item.webSaleItemId,
          quantity: item.quantity,
          webSaleItemTypeID: item.webSaleItemTypeID || 4,
          name: item.name,
          isSysGift: item.isSysGift || false,
          isSysMemberCard: item.isSysMemberCard || false
        }))
      };

      const response = await fetch(url, {
        method: 'POST',
        headers: getCorsHeaders(),
        mode: 'cors',
        credentials: 'omit',
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to fetch membership details: ${response.status} ${response.statusText}. Body: ${errorText}`);
      }

      const data = await response.json();

      if (data.isError) {
        throw new Error(data.information || 'Error fetching membership details');
      }

      return data;
    } catch (error) {
      return { isError: true, data: null, error: error.message };
    }
  },

  /**
   * Get membership customer details
   * @param {string} locationId - Location ID
   * @param {string} customerId - Customer ID
   * @returns {Promise<Object>} Membership customer details response
   */
  async getMembershipCustomerDetails(locationId, customerId) {
    try {
      const url = `${VENPOS_API_BASE}/Membership/GetMembershipCustomerDetails?locationId=${locationId}`;

      const payload = {
        customerId: customerId
      };

      const response = await fetch(url, {
        method: 'POST',
        headers: getCorsHeaders(),
        mode: 'cors',
        credentials: 'omit',
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to fetch membership customer details: ${response.status} ${response.statusText}. Body: ${errorText}`);
      }

      const data = await response.json();

      if (data.isError) {
        throw new Error(data.information || 'Error fetching membership customer details');
      }

      return data;
    } catch (error) {
      return { isError: true, data: null, error: error.message };
    }
  }
}; 